import {
  datenregelImportAntwortSchema,
  datenregelImportProbeAntwortSchema,
  datenregelSatzSchema,
} from './schemas';
import type {
  DatenregelExportRoh,
  DatenregelImportAntwort,
  DatenregelImportProbeAntwort,
  DatenregelImportProbeRoh,
  DatenregelImportRoh,
  DatenregelSatz,
} from './schemas';
import type {
  AborechnungAnlageRaw,
  AborechnungLaufAufhebungGrund,
  AborechnungPatchRaw,
  AborechnungPositionInputRaw,
  AborechnungVorlageAnsicht,
  AborechnungVorlagenListenzeile,
  AccountInputRaw,
  AccountNewVersionInputRaw,
  Anmeldung,
  BankAccountCreateRaw,
  BelegvorlageAnsicht,
  BelegvorlageFassungAnsicht,
  BelegvorlageKatalog,
  BelegvorlageListe,
  CustomerInputRaw,
  CustomerLinkInput,
  CustomerPatchRaw,
  DocumentInputRaw,
  DocumentLineInputRaw,
  DocumentPatchRaw,
  FintsZugangEingabeRaw,
  IncomingPatchRequest,
  IssuerProfileInputRaw,
  MailPlatzhalterAntwort,
  MailvorlageAnsicht,
  MailvorlageFassungAnsicht,
  MailvorlageListe,
  MailvorlageVorschauAntwort,
  NumberGapInputRaw,
  VatCheckInput,
  VendorInputRaw,
  SachkontoPraeferenzen,
  SystemAnfrageDetail,
  SystemAnfragenListe,
  Tabellenansicht,
  Suchergebnis,
  Vorschlagsantwort,
  VendorPatchRaw,
} from '@fameacc/contracts';
import type { AuswertungPrognose, AuswertungUmsatz } from '@fameacc/contracts';
// Vorsystem — Seite Übernahme (LX-webseite): die Verträge der Übernahme aus Lexware
// Office kommen aus dem Vertragspaket, keine zweite Liste hier.
import {
  eroeffnungsformSchema,
  eroeffnungspostenFreigabeAntwortSchema,
  eroeffnungspostenListeSchema,
  eroeffnungspostenSammelfreigabeAntwortSchema,
  vorsystemAuftragListeSchema,
  vorsystemAuftragSchema,
  vorsystemBefundeSchema,
  vorsystemAngelegtSchema,
  vorsystemKategorieDetailsSchema,
  vorsystemKontaktentscheideSchema,
  vorsystemKontaktentscheidSchema,
  vorsystemKontenabbildungSchema,
  vorsystemKontenExportErgebnisSchema,
  vorsystemKontenStapelErgebnisSchema,
  vorsystemUebernahmeStatusSchema,
  type Eroeffnungsform,
  type EroeffnungsformEingabe,
  type EroeffnungspostenFreigabeAntwort,
  type EroeffnungspostenListe,
  type EroeffnungspostenSammelfreigabeAntwort,
  type VorsystemAuftrag,
  type VorsystemAuftragEingabe,
  type VorsystemAuftragSchritt,
  type VorsystemBefunde,
  type VorsystemKategorieDetails,
  type VorsystemKontaktentscheid,
  type VorsystemKontaktentscheide,
  type VorsystemKontaktentscheidEingabe,
  type VorsystemKontenabbildung,
  type VorsystemKontenExportErgebnis,
  type VorsystemKontenStapelErgebnis,
  type VorsystemUebernahmeStatus,
} from '@fameacc/contracts';
import type { DashboardKennzahlenBericht, DashboardZeitraumTyp } from '@fameacc/contracts';
// Die Abrufreihe bei PayPal (Spec Zahldienste 13b, D-20): der Vertrag traegt keine
// gebrandeten Betraege und wird deshalb unmittelbar benutzt, ohne lokalen Spiegel.
import { abrufreiheSchema, abrufreiheStandSchema, type Abrufreihe } from '@fameacc/contracts';
// Die Kundenauswertung kommt aus dem VERTRAG und nicht aus `api/schemas.ts`:
// Die Chartbausteine nehmen `Money` entgegen, der lokale Spiegel liefert
// unmarkierte Zeichenketten. Begruendung an `getAuswertungKunden()` unten.
import {
  auswertungAboSchema,
  auswertungKundenSchema,
  type AuswertungAbo,
  type AuswertungKunden,
  type AuswertungReihenlaenge,
  type AuswertungZeitraumTyp,
} from '@fameacc/contracts';
// Die sieben Vertraege der DATEV-Exportmaske — Chip `DX-maske`, aus dem
// Vertragspaket und nicht aus `api/schemas.ts` abgeschrieben, Begruendung am
// Abschnitt „DATEV-Export" unten.
import type {
  DatevExportDetail,
  DatevExportListe,
  DatevExportPeriodenListe,
  DatevExportRueckmeldungZeile,
  DatevExportZeileAnsicht,
  DatevStapelAnsicht,
} from '@fameacc/contracts';
import {
  datevExportDetailSchema,
  datevExportListeSchema,
  datevExportPeriodenListeSchema,
  datevExportRueckmeldungZeileSchema,
  datevExportZeileAnsichtSchema,
  datevStapelAnsichtSchema,
} from '@fameacc/contracts';
// Die Vertraege der Webhook-Verwaltung — Chip `WH-oberflaeche`, aus dem
// Vertragspaket und nicht aus `api/schemas.ts` abgeschrieben, Begruendung am
// Abschnitt „Webhooks" unten.
import type {
  AutomatikStart,
  AutomatikStartListe,
  AutomatikStartSchluessel,
  WebhookEndpunktAnlage,
  WebhookEndpunktAntwort,
  WebhookEndpunktListe,
  WebhookEndpunktMitSecret,
  WebhookEndpunktPatch,
  WebhookZustellung,
  WebhookZustellungStatus,
  WebhookZustellungenSeite,
} from '@fameacc/contracts';
import {
  automatikStartListeSchema,
  automatikStartSchema,
  webhookEndpunktAntwortSchema,
  webhookEndpunktListeSchema,
  webhookEndpunktMitSecretSchema,
  webhookZustellungSchema,
  webhookZustellungenSeiteSchema,
} from '@fameacc/contracts';
// Der E-Mail-Eingang — Auftrag `EM-web`. Aus dem Vertragspaket und nicht aus
// `api/schemas.ts` abgeschrieben, dieselbe Wahl wie bei den Webhooks darüber.
import type {
  MailAbrufErgebnis,
  MailAbrufProtokoll,
  MailKonto,
  MailKontoAbsender,
  MailKontoAbsenderListe,
  MailKontoListe,
  MailKontoPruefung,
  MailRegel,
  MailRegelListe,
  mailKontoAbsenderAnlageSchema,
  mailKontoAnlageSchema,
  mailKontoPatchSchema,
  mailKontoPruefungRumpfSchema,
  mailRegelAnlageSchema,
  mailRegelPatchSchema,
} from '@fameacc/contracts';
import {
  mailAbrufProtokollSchema,
  mailKontoAbsenderListeSchema,
  mailKontoAbsenderSchema,
  mailKontoListeSchema,
  mailKontoPruefungSchema,
  mailKontoSchema,
  mailRegelListeSchema,
  mailRegelSchema,
} from '@fameacc/contracts';
// Die Verträge der Workflows — Auftrag `WF-web`, aus dem Vertragspaket, Begründung am
// Abschnitt „Workflows" unten.
import type {
  WorkflowAnlage,
  WorkflowDetail,
  WorkflowEntwurfAntwort,
  WorkflowEntwurfRumpf,
  WorkflowEreignisListe,
  WorkflowFassung,
  WorkflowFassungListe,
  WorkflowGeheimnis,
  WorkflowGeheimnisAnlage,
  WorkflowGeheimnisListe,
  WorkflowGeheimnisAenderung,
  WorkflowKatalog,
  WorkflowLaufDetail,
  WorkflowLaufListe,
  WorkflowLaufStatus,
  WorkflowListe,
  WorkflowAenderung,
  WorkflowStartRumpf,
  WorkflowWebhookEingangAntwort,
  WorkflowWebhookEingangMitSecret,
} from '@fameacc/contracts';
import {
  workflowDetailSchema,
  workflowEntwurfAntwortSchema,
  workflowEreignisListeSchema,
  workflowFassungListeSchema,
  workflowFassungSchema,
  workflowGeheimnisListeSchema,
  workflowGeheimnisSchema,
  workflowKatalogSchema,
  workflowLaufDetailSchema,
  workflowLaufListeSchema,
  workflowListeSchema,
  workflowWebhookEingangAntwortSchema,
  workflowWebhookEingangMitSecretSchema,
} from '@fameacc/contracts';
// Der Vertrag der Steuerszenario-Auskunft — Chip `ST-web`, Begruendung am
// Abschnitt „Das Steuerszenario am Beleg" unten.
import type { Satzangebot, SteuerszenarioAuskunft, VendorErkennungArt } from '@fameacc/contracts';
import { satzangebotSchema, steuerszenarioAuskunftSchema } from '@fameacc/contracts';
// Die Vertraege der Auswertungs-Unterseiten — AUS DEM VERTRAGSPAKET und nicht
// aus `api/schemas.ts`, aus demselben Grund wie beim Dashboard-Bericht unten:
// Die Chartbausteine (`ui/charts/`) nehmen ECHT `Money`/`Factor`-markierte
// Felder entgegen, und das lokal gespiegelte `betragSchema` dieser Anwendung
// liefert unmarkierte Zeichenketten.
import type { AuswertungKosten, AuswertungSteuer } from '@fameacc/contracts';
import { auswertungKostenSchema, auswertungSteuerSchema } from '@fameacc/contracts';
// Die BWA — Task 3 `[BWA-web]`. Dieselbe Wahl wie bei Kosten/Steuer oben: das
// Vertragsschema kommt direkt aus `@fameacc/contracts`, kein Spiegel in
// `api/schemas.ts`. Der Dienst dahinter (Task 2 `[BWA-api]`) fährt auf einem
// eigenen Zweig; bis er landet, antwortet `/auswertungen/bwa` nicht, und
// diese Funktion bleibt trotzdem übersetzbar und geprüft — gegen eine
// Vertragsfixture im DOM-Test und eine `page.route()`-Attrappe im e2e-Lauf.
import type { AuswertungBwa } from '@fameacc/contracts';
import { auswertungBwaSchema } from '@fameacc/contracts';
// Das Kennzahlblatt der Umsatzsteuer-Voranmeldung — Task 3 `[UV-web]`. Aus dem
// Vertragspaket und nicht aus `api/schemas.ts` abgeschrieben, dieselbe Wahl wie
// bei der BWA oben: Die Antwort ist `.strict()`, und der Steuersatz kommt als
// `Factor`, den ein lokaler Spiegel als blosse Zeichenkette lieferte.
import type {
  UstvaAnfrage,
  UstvaBlatt,
  UstvaNachweis,
  UstvaNachweisAnfrage,
} from '@fameacc/contracts';
import { ustvaBlattSchema, ustvaNachweisSchema } from '@fameacc/contracts';
import type { Marke } from '@fameacc/contracts';
import { markeSchema } from '@fameacc/contracts';
// Das SEPA-Lastschriftmandat — Task 2 `[LS-mandat]`. Aus dem Vertragspaket und
// nicht aus `api/schemas.ts` abgeschrieben, dieselbe Wahl wie bei Kosten/
// Steuer und der BWA oben (`W-schemadoppel`): `sepaMandatSchema` trägt eine
// Aufzählung (`status`) und mehrere Kalenderfelder, die dieselbe Regel wie
// die Datenbank tragen sollen — ein zweiter, lokaler Spiegel wäre genau die
// Fehlerklasse, die jener Chip geschlossen hat.
import type { SepaMandat, SepaMandatAnlage, SepaMandatUebersicht } from '@fameacc/contracts';
import { sepaMandatSchema, sepaMandatUebersichtSchema } from '@fameacc/contracts';
// Das Hinweisband der Lizenz — Auftrag `LZ-oberflaeche`. Aus dem Vertragspaket
// und nicht aus `api/schemas.ts` abgeschrieben, dieselbe Wahl wie bei den
// Verträgen darüber (`W-schemadoppel`): Die Antwort ist `.strict()`, und ein
// lokaler Spiegel wäre die zweite Wahrheit über dieselben acht Felder.
import type { LizenzAuskunft, LizenzHinweis } from '@fameacc/contracts';
import { lizenzAuskunftSchema, lizenzHinweisSchema } from '@fameacc/contracts';
import { meldeLizenzNurLesen } from '../lizenz/lage';
// Die Datensicherung — Auftrag `DS-web`. Aus dem Vertragspaket und nicht aus
// `api/schemas.ts` abgeschrieben (`W-schemadoppel`): Jede Antwort ist
// `.strict()`, und die Aufzählungen (Laufarten, Zustände, Fehlerarten) gehören
// einer einzigen Stelle.
import type {
  SicherungAnforderung,
  SicherungAufbewahrung,
  SicherungLaufAngefordert,
  SicherungLaufArt,
  SicherungLaufListe,
  SicherungPasswort,
  SicherungStatus,
  SicherungZielAuskunft,
  sicherungAufbewahrungAnfrageSchema,
  sicherungBestaetigenSchema,
  sicherungZielAnlegenSchema,
  sicherungZielFassungAnfrageSchema,
  sicherungZielPruefenSchema,
} from '@fameacc/contracts';
import {
  sicherungAnforderungSchema,
  sicherungAufbewahrungSchema,
  sicherungLaufAngefordertSchema,
  sicherungLaufListeSchema,
  sicherungPasswortSchema,
  sicherungStatusSchema,
  sicherungZielAuskunftSchema,
} from '@fameacc/contracts';
import type {
  ZahlungKlaerungsgrund,
  ZahlungKlaerungsliste,
  ZahlungZuordnung,
  ZahlungZuordnungFreigabeliste,
  ZahlungZuordnungKette,
  ZahlungZuordnungQuelle,
  Zahlungsstand,
  Zahlungsvorschlag,
} from '@fameacc/contracts';
import {
  // Die sechs Vertraege der Zahlungszuordnung — aus dem Vertragspaket und
  // nicht aus `api/schemas.ts` abgeschrieben, Begruendung am Abschnitt unten.
  zahlungKlaerungslisteSchema,
  zahlungZuordnungFreigabelisteSchema,
  zahlungZuordnungKetteSchema,
  zahlungZuordnungSchema,
  zahlungsstandSchema,
  zahlungsvorschlagSchema,
} from '@fameacc/contracts';
import {
  // Die zwei Antwortformen der Aborechnungsvorlagen kommen aus dem
  // Vertragspaket und nicht aus `api/schemas.ts` — sie haben dort ein
  // Gegenstück, und eine zweite Abschrift wäre genau die Fehlerklasse, die
  // `W-schemadoppel` geschlossen hat (dieselbe Wahl wie beim
  // Anfrage-Debugger unten).
  aborechnungVorlageAnsichtSchema,
  aborechnungVorlageListeSchema,
  createdApiTokenSchema,
  createdUserSchema,
  passwordResetSchema,
  // Die Umsatzauswertung — aus dem VERTRAG und nicht aus `api/schemas.ts`:
  // Charts und Kennzahlkarten nehmen echt markierte `Money`/`Factor`-Werte
  // entgegen, und das lokal gespiegelte `betragSchema` liefert unmarkierte
  // Zeichenketten. Derselbe Weg wie beim Dashboard darüber.
  auswertungPrognoseSchema,
  auswertungUmsatzSchema,
  dashboardKennzahlenBerichtSchema,
  // Die Vertraege der Belegvorlagen — aus dem Vertragspaket, Begruendung
  // am Abschnitt «Belegvorlagen» unten.
  belegvorlageAnlageSchema,
  belegvorlageAnsichtSchema,
  belegvorlageFassungAnsichtSchema,
  belegvorlageFassungEingabeSchema,
  belegvorlageKatalogSchema,
  belegvorlageListeSchema,
  belegvorlageVorschauEingabeSchema,
  // Die fuenf Vertraege der E-Mail-Vorlagen — aus dem Vertragspaket und nicht
  // aus `api/schemas.ts` abgeschrieben, Begruendung am Abschnitt unten.
  mailPlatzhalterAntwortSchema,
  mailvorlageAnsichtSchema,
  mailvorlageFassungAnsichtSchema,
  mailvorlageListeSchema,
  mailvorlageVorschauAntwortSchema,
  meAnmeldungenAntwortSchema,
  suchergebnisSchema,
  // Die zwei Verträge des Anfrage-Debuggers kommen aus dem Vertragspaket und
  // nicht aus `api/schemas.ts`: Sie haben dort ein Gegenstück, und eine zweite
  // Abschrift wäre genau die Fehlerklasse, die `W-schemadoppel` geschlossen hat.
  systemAnfrageDetailSchema,
  systemAnfragenListeSchema,
  vorschlagsantwortSchema,
} from '@fameacc/contracts';
import type {
  LastschriftDatei,
  LastschriftDateiListFints,
  LastschriftDateiZeile,
  LastschriftEinzug,
  LastschriftFaelligList,
} from '@fameacc/contracts';
import {
  // Die Lastschrift-Listen und -Einzelantworten kommen aus dem Vertragspaket
  // und nicht aus `api/schemas.ts` — dieselbe Wahl wie bei den
  // Zahlungszuordnungs- und Aborechnungsverträgen oben, aus demselben Grund
  // (`W-schemadoppel`): eine zweite Abschrift der Listenhülle wäre die
  // Fehlerklasse, die jener Auftrag geschlossen hat.
  lastschriftDateiErzeugenAntwortSchema,
  lastschriftDateiListSchema,
  lastschriftDateiSchema,
  lastschriftEinzugSchema,
  lastschriftFaelligListSchema,
} from '@fameacc/contracts';
import { SACHKONTO_PRAEFERENZEN_TABELLE } from '@fameacc/contracts';
// Die Bezeichnungen der Oberflaeche -- Auftrag `PF-erkennung`, aus dem Vertrag.
import { bezeichnungenSchema, type Bezeichnungen } from '@fameacc/contracts';
// Die Funktionsschalter -- Auftrag `PF-guthabenschalter` (`D-57`), aus dem Vertrag.
import { funktionsstandSchema, type Funktionsstand, type GuthabenSchalten } from '@fameacc/contracts';
// Der Einrichtungsassistent -- Auftrag `PF-assistent`, Task 11 des Plans
// Systemeinstellungen. Alle Vertraege aus dem Vertragspaket, keiner abgeschrieben.
import type {
  BetriebEinstellung,
  BetriebSetzen,
  EinrichtungKreisSetzen,
  Einrichtungsstand,
  Einstellungen,
  ErkennungEinstellung,
  ErkennungSetzen,
  Freigabeprofil,
  FreigabeprofilFortschreiben,
  IssuerLogoUploadResult,
  LogoMedientyp,
  Steuerprofil,
  SteuerprofilFortschreiben,
  UmsatzVorsystem,
  UmsatzVorsystemSetzenEingabe,
  Zustaendigkeit,
  ZustaendigkeitSetzen,
} from '@fameacc/contracts';
import {
  betriebEinstellungSchema,
  einrichtungsstandSchema,
  einstellungenSchema,
  erkennungEinstellungSchema,
  freigabeprofilSchema,
  issuerLogoUploadResultSchema,
  steuerprofilSchema,
  umsatzVorsystemSchema,
  zustaendigkeitSchema,
} from '@fameacc/contracts';
// Vorsystem — Einrichtungsschritt (LX-einrichtungsschritt): Verträge der Übernahme
// aus dem Vertragspaket, keiner abgeschrieben.
import type {
  VorsystemAuftragAnlage,
  VorsystemEinstellung,
  VorsystemEinstellungEingabe,
  VorsystemSchluesselEingabe,
  VorsystemSchluesselStatus,
} from '@fameacc/contracts';
import { vorsystemEinstellungSchema, vorsystemSchluesselStatusSchema } from '@fameacc/contracts';
import { EINRICHTUNG_OFFEN_HINWEIS } from '../einstellungen/einrichtung-hinweis';
import type { z } from 'zod';
import {
  accountListSchema,
  apiTokenListSchema,
  apiTokenPermissionsResponseSchema,
  schnittstellenbeschreibungSchema,
  incomingDuplicateListSchema,
  formatReportSchema,
  gapReportSchema,
  incomingCaptureResultSchema,
  incomingDocumentListSchema,
  incomingDocumentSchema,
  incomingInvoiceDetailSchema,
  incomingInvoiceListSchema,
  incomingUploadResultSchema,
  kontenkatalogAntwortSchema,
  kontierungAntwortSchema,
  kontierungAutomatischListeSchema,
  kontierungSchema,
  kontierungsvorschlagAntwortSchema,
  kontierungWartendListeSchema,
  bewegungssucheAntwortSchema,
  rechnungBewegungAntwortSchema,
  ohneInhaltSchema,
  prefillSchema,
  startkennzahlenBerichtSchema,
  zuordnungsquoteBerichtSchema,
  offenePostenBerichtSchema,
  zahlstatusAntwortSchema,
  zahlstatusListeSchema,
  verbindlichkeitZahlstatusAntwortSchema,
  verbindlichkeitZahlstatusListeSchema,
  unbookedReportSchema,
  unkontierteListeSchema,
  accountWriteResultSchema,
  bankAccountListSchema,
  bankAccountSchema,
  bankStatementListSchema,
  bankTransactionListSchema,
  countryListSchema,
  coverageReportSchema,
  customerDetailSchema,
  customerListSchema,
  kundenakteSchema,
  documentListSchema,
  documentVersandEntrySchema,
  documentVersandEntwurfSchema,
  documentVersandHistorySchema,
  documentViewSchema,
  documentWithoutPdfListSchema,
  guthabenAbstimmungDetailSchema,
  guthabenAbstimmungListSchema,
  guthabenEinloesungSchema,
  guthabenEinloesungStornoSchema,
  guthabenErstattungSchema,
  guthabenErstattungWartendListSchema,
  guthabenSchalterSchema,
  guthabenKorrekturKetteSchema,
  guthabenKorrekturListSchema,
  guthabenKorrekturSchema,
  guthabenKorrekturSpiegelOffenListSchema,
  geldkontoAnfangsbestandFreigabeAntwortSchema,
  geldkontoAnfangsbestandListeSchema,
  sachkontoSaldenvortragAnlageAntwortSchema,
  sachkontoSaldenvortragFreigabeAntwortSchema,
  sachkontoSaldenvortragListeSchema,
  sachkontoPraeferenzenAntwortSchema,
  tabellenansichtAntwortSchema,
  systemStatusSchema,
  regelUebersichtSchema,
  datenregelFassungAntwortSchema,
  datenregelnListeSchema,
  datenregelVorschauAntwortSchema,
  datenregelVorlagenListeSchema,
  datenregelBuchungsvorschauAntwortSchema,
  duplicateListSchema,
  fetchListSchema,
  fetchWindowListSchema,
  fintsInstitutListeSchema,
  fintsLaufSchema,
  fintsZugangSchema,
  fintsEinrichtungSchema,
  fintsTanMedienAbrufSchema,
  fintsZugangstestSchema,
  issueResultSchema,
  issuerProfileListSchema,
  issuerProfileSchema,
  journalEntryDetailSchema,
  journalListSchema,
  kontoblattSchema,
  lastschriftEinreichungLaufSchema,
  linkSchema,
  meSchema,
  numberGapDetailSchema,
  numberGapSchema,
  numberRangeListSchema,
  numberRangeReportListSchema,
  periodListSchema,
  portalVorgangListeSchema,
  portalVorgangSchema,
  priceBasisChangeSchema,
  stageSchema,
  statementImportBatchResultSchema,
  teilzahlungListeSchema,
  totpEnrollmentSchema,
  userListSchema,
  vatCheckListSchema,
  vatCheckSchema,
  vendorDetailSchema,
  vendorErkennungListeSchema,
  vendorErkennungRegelSchema,
  vendorListSchema,
  zugangsprobeSchema,
  type Account,
  type AccountWriteResult,
  type BankAccount,
  type BankStatement,
  type BankTransaction,
  type Country,
  type CoverageReport,
  type Customer,
  type Kundenakte,
  type FetchReport,
  type FintsEinrichtung,
  type FintsInstitut,
  type FintsLauf,
  type FintsZugang,
  type FintsTanMedienAbruf,
  type FintsZugangstest,
  type UebersprungenesFenster,
  type Zugangsprobe,
  type DocumentSummary,
  type DocumentVersandEntry,
  type DocumentVersandEntwurf,
  type DocumentView,
  type DocumentWithoutPdf,
  type DuplicateEntry,
  type IssuerProfile,
  type IssueResult,
  type JournalEntry,
  type JournalEntryDetail,
  type Kontoblatt,
  type LastschriftEinreichungLauf,
  type KontenkatalogAntwort,
  type Kontierung,
  type KontierungAntwort,
  type KontierungAutomatischListe,
  type KontierungHerkunft,
  type KontierungsvorschlagAntwort,
  type KontierungWartendZeile,
  type Link,
  type Me,
  type NumberGap,
  type NumberGapDetail,
  type GuthabenAbstimmungDetail,
  type GuthabenAbstimmungListItem,
  type GuthabenEinloesung,
  type GuthabenEinloesungStorno,
  type GuthabenErstattung,
  type GuthabenSchalter,
  type GuthabenKorrektur,
  type GuthabenKorrekturSpiegelOffen,
  type GeldkontoAnfangsbestandFreigabeAntwort,
  type GeldkontoAnfangsbestandZeile,
  type SachkontoSaldenvortragAnlageAntwort,
  type SachkontoSaldenvortragAnlageEingabe,
  type SachkontoSaldenvortragErsetzenEingabe,
  type SachkontoSaldenvortragFreigabeAntwort,
  type SachkontoSaldenvortragListe,
  type NumberRange,
  type NumberRangeReport,
  type Period,
  type PortalVorgang,
  type PortalVorgangListe,
  type PriceBasisChangeResult,
  type Stage,
  type StatementImportBatchResult,
  type SystemStatus,
  type RegelUebersicht,
  type TeilzahlungBewegung,
  type TotpEnrollment,
  type UnkontierteListe,
  type ApiToken,
  type ApiTokenPermission,
  type CreatedApiToken,
  type Schnittstellenbeschreibung,
  type CreatedUser,
  type PasswordReset,
  type User,
  type VatCheck,
  type Vendor,
  type VendorErkennungRegel,
  auswertungUeberblickSchema,
  type AuswertungUeberblickAnsicht,
  auswertungZahlungswegeSchema,
  type AuswertungZahlungswegeAnsicht,
  auswertungForderungenSchema,
  type AuswertungForderungenAnsicht,
} from './schemas';
import type {
  IncomingDuplicate,
  FormatReport,
  GapReport,
  IncomingCaptureResult,
  IncomingDocument,
  IncomingDocumentDiscardReason,
  IncomingKorbzeile,
  IncomingInvoiceDetail,
  IncomingInvoiceList,
  IncomingPrefill,
  IncomingUploadResult,
  UnbookedReport,
  BewegungssucheAntwort,
  RechnungBewegungAntwort,
  StartkennzahlenBericht,
  ZuordnungsquoteBericht,
  OffenePostenBericht,
  RechnungZahlstand,
  RechnungZahlstatus,
  EingangsrechnungZahlstand,
  EingangsrechnungZahlstatus,
  DatenregelFassungAntwort,
  DatenregelnListe,
  DatenregelVorschauAntwort,
  DatenregelVorlagenListe,
  DatenregelBuchungsvorschauAntwort,
} from './schemas';

/**
 * Der einzige Weg des Frontends zur API.
 *
 * **Alle Anfragen laufen über einen relativen Pfad.** Es gibt keine
 * konfigurierbare Basisadresse und keine Umgebungsvariable dafür. Das ist
 * keine Auslassung: Die Sitzungscookies tragen `SameSite=Strict`, und eine
 * andere Herkunft bekäme sie nie zu sehen. Frontend und API liegen deshalb
 * hinter demselben Namen — im Betrieb über nginx, in der Entwicklung über den
 * Weiterleiter von Vite.
 *
 * **`credentials: 'same-origin'`** steht ausdrücklich da. Der Vorgabewert der
 * Fetch-API ist zwar derselbe, aber die Sitzung dieses Systems hängt daran;
 * eine Zusage, die trägt, gehört hingeschrieben und nicht angenommen.
 *
 * **Der Fehlerpfad ist ein Fall und keine Ausnahme.** Die API antwortet
 * einheitlich mit `{ error: { code, message, correlationId } }`. Der Klient
 * macht daraus einen `ApiError` mit genau diesen drei Feldern — die
 * Vorgangskennung gehört auf den Bildschirm, denn sie ist das einzige, womit
 * ein Anwender einen Vorfall meldbar macht.
 */

/** Die gemeinsame Wurzel aller Fachendpunkte. */
const BASE = '/api/v1';

/**
 * Ein Fehler, wie die API ihn meldet.
 *
 * **`details` trägt, was über `code`, `message` und `correlationId` hinaus
 * unter `error` stand.** Zwei Ablehnungen dieser Phase sind ohne diesen Inhalt
 * nicht bedienbar: `CUSTOMER_DUPLICATE_SUSPECTED` führt dort seine
 * `candidates`, `VENDOR_SIMILAR_EXISTS` seine `similar`. Eine Maske, die den
 * Anwender entscheiden lässt, ohne ihm zu zeigen worüber, verlagert die Arbeit
 * und gibt ihm nichts an die Hand.
 *
 * Der Klient reicht den ganzen `error`-Knoten durch und deutet ihn nicht: Was
 * darin steht, prüft die Maske mit dem Schema, das zu ihrem Fall gehört.
 */
export class ApiError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly correlationId: string | undefined,
    public readonly details: unknown = undefined,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Fehlt bloss die Anmeldung? Dann gehört der Anwender auf die Anmeldemaske. */
  public get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /**
   * Diese eine 401 verlangt mehr als eine Meldung an Ort und Stelle: Das
   * Passwort ist gewechselt — auf diesem Gerät oder, seit `C-pwsession`, auf
   * einem zweiten (dort serverseitig zerstört) — und die Sitzung ist tot.
   * Jede andere 401 (abgelaufenes Cookie, Abmeldung, ein anderer Kode) bleibt
   * beim gewöhnlichen Weg jeder Maske und löst hier nichts aus (`W-reauth`).
   */
  public get requiresReauth(): boolean {
    return this.code === 'PASSWORD_CHANGED_REAUTH_REQUIRED';
  }

  /**
   * Ist die Sitzung **gesperrt** statt abgelaufen? (`A-sitzungssperre`)
   *
   * Status UND Kode werden geprüft, nicht nur der Kode: 423 trägt in dieser
   * API zwei Bedeutungen — `ACCOUNT_LOCKED` (das Konto ist nach Fehlversuchen
   * zu, der Anwender kann nichts tun als warten) und `SESSION_LOCKED` (der
   * Bildschirm ist verriegelt, der Anwender öffnet ihn selbst). Wer nur auf
   * den Status sähe, zeigte einem gesperrten Konto einen Sperrbildschirm, den
   * es nie aufschliessen kann.
   */
  public get isLocked(): boolean {
    return this.status === 423 && this.code === 'SESSION_LOCKED';
  }

  /**
   * Fehlt schlicht die Anmeldung — und zwar so, dass die Anwendung darauf
   * reagieren MUSS?
   *
   * Enger als `isUnauthenticated`: `PASSWORD_CHANGED_REAUTH_REQUIRED` ist
   * ebenfalls eine 401, hat aber seinen eigenen, erklärenden Weg
   * (`requiresReauth`), und `TOTP_INVALID` ist eine 401 mitten in der
   * Anmeldestrecke, bei der niemand zur Anmeldemaske geschickt werden darf —
   * sie IST die Anmeldemaske.
   */
  public get isAuthRequired(): boolean {
    return this.status === 401 && this.code === 'AUTH_REQUIRED';
  }

  /**
   * Ist die Ersteinrichtung noch offen? (`PF-assistent`, `409 EINRICHTUNG_OFFEN`)
   *
   * Eine Zustandsfrage der Installation und keine der Anfrage: Der Weg heraus
   * ist der Assistent, nicht ein zweiter Versuch.
   */
  public get isEinrichtungOffen(): boolean {
    return this.status === 409 && this.code === 'EINRICHTUNG_OFFEN';
  }
}

/**
 * Von `App.tsx` gesetzt, um auf `ApiError.requiresReauth` zu reagieren —
 * gleich, aus welcher Maske der Fehler kommt. **Der Klient navigiert nicht
 * selbst:** Das bleibt Sache der Anwendung, die über den obersten Zustand
 * entscheidet; hier wird nur der Anlass gemeldet. Kein neuer
 * Anwendungszustand — eine austauschbare Rückruffunktion, wie ein
 * Event-Listener.
 */
let reauthListener: ((message: string) => void) | undefined;

/**
 * Registriert die einzige Stelle, die auf eine wegen Passwortwechsel tote
 * Sitzung reagiert. Gibt eine Abmeldefunktion zurück, die beim Aufräumen
 * (`useEffect`-Rückgabe in `App.tsx`) aufgerufen wird — ohne sie bliebe nach
 * einem erneuten Registrieren die alte Funktion daneben bestehen.
 */
export function onReauthRequired(listener: (message: string) => void): () => void {
  reauthListener = listener;
  return () => {
    if (reauthListener === listener) reauthListener = undefined;
  };
}

/**
 * Meldet der registrierten Anwendung einen Fehler, der die Anmeldemaske
 * verlangt. Eigens exportiert und von der eigentlichen Netzanfrage getrennt,
 * damit die Weiche ohne echten Netzzugriff geprüft werden kann — dieselbe
 * Bauform wie `freigabelage()` in `eingangsrechnungen/status.ts`.
 */
export function notifyReauthRequired(error: ApiError): void {
  if (error.requiresReauth) reauthListener?.(error.message);
}

/**
 * Die zwei weiteren Sitzungslagen, die eine beliebige Anfrage melden kann —
 * `A-sitzungssperre`.
 *
 * **Warum sie neben `reauthListener` stehen und nicht darin.** Die drei Fälle
 * verlangen drei verschiedene Handlungen: `PASSWORD_CHANGED_REAUTH_REQUIRED`
 * führt zur Anmeldemaske **mit einem erklärenden Satz**, `AUTH_REQUIRED` führt
 * zur Anmeldemaske **ohne** einen (das Cookie ist abgelaufen, es gibt nichts
 * zu erklären), `SESSION_LOCKED` führt zum Sperrbildschirm und gerade **nicht**
 * zur Anmeldemaske. Ein gemeinsamer Rückruf mit einem Unterscheidungsfeld
 * wäre dieselbe Fallunterscheidung, nur eine Ebene tiefer — und die
 * bestehende, geprüfte Weiche (`test/reauth.test.ts`) müsste dafür umgebaut
 * werden, ohne dass irgendetwas dadurch besser würde.
 */
let authRequiredListener: (() => void) | undefined;
let lockedListener: (() => void) | undefined;

/** Registriert die Stelle, die auf ein abgelaufenes Sitzungscookie reagiert. */
export function onAuthRequired(listener: () => void): () => void {
  authRequiredListener = listener;
  return () => {
    if (authRequiredListener === listener) authRequiredListener = undefined;
  };
}

/** Registriert die Stelle, die auf eine gesperrte Sitzung reagiert. */
export function onSessionLocked(listener: () => void): () => void {
  lockedListener = listener;
  return () => {
    if (lockedListener === listener) lockedListener = undefined;
  };
}

/**
 * Meldet der Anwendung die Sitzungslage, die aus diesem Fehler folgt — genau
 * eine oder keine.
 *
 * Eigens von der Netzanfrage getrennt und exportiert, damit die Weiche ohne
 * `fetch` prüfbar ist — dieselbe Bauform und dieselbe Begründung wie bei
 * `notifyReauthRequired` darüber.
 *
 * **Die Reihenfolge ist die Aussage.** `requiresReauth` zuerst, weil dieser
 * Kode ebenfalls eine 401 ist und seinen erklärenden Satz behalten muss;
 * danach die beiden anderen, die einander ausschliessen.
 */
export function meldeSitzungslage(error: ApiError): void {
  if (error.requiresReauth) {
    reauthListener?.(error.message);
    return;
  }
  if (error.isLocked) {
    lockedListener?.();
    return;
  }
  if (error.isAuthRequired) authRequiredListener?.();
}

/**
 * Die vierte Lage, die eine beliebige Anfrage melden kann: Die Ersteinrichtung
 * ist noch offen (`PF-assistent`, `409 EINRICHTUNG_OFFEN`).
 *
 * **Sie steht neben den Sitzungslagen und nicht in ihnen**, denn sie betrifft
 * keine Sitzung: Der Anwender ist angemeldet, nur diese Installation darf noch
 * nichts ausser sich selbst einrichten. `App.tsx` entscheidet, was daraus
 * folgt (Umleitung zum Assistenten oder ein Hinweis); der Klient navigiert
 * nicht selbst.
 */
let einrichtungOffenListener: (() => void) | undefined;

/** Registriert die Stelle, die auf eine offene Einrichtung reagiert. */
export function onEinrichtungOffen(listener: () => void): () => void {
  einrichtungOffenListener = listener;
  return () => {
    if (einrichtungOffenListener === listener) einrichtungOffenListener = undefined;
  };
}

/** Meldet der Anwendung eine offene Einrichtung, wenn dieser Fehler sie meint. */
export function meldeEinrichtungOffen(error: ApiError): void {
  if (error.isEinrichtungOffen) einrichtungOffenListener?.();
}

/** Der Rumpf einer Fehlerantwort, soweit er uns interessiert. */
interface ErrorBody {
  readonly error?: {
    readonly code?: unknown;
    readonly message?: unknown;
    readonly correlationId?: unknown;
  };
}

/**
 * Führt eine Anfrage aus und prüft die Antwort gegen ein Schema.
 *
 * Eine Antwort, die das Schema nicht erfüllt, ist ein Fehler und kein
 * halbwegs brauchbares Objekt: Ein fehlendes Feld fiele sonst erst in der
 * Anzeige auf, und zwar als leere Zelle, die aussieht wie ein leerer
 * Datenbestand.
 */
/*
 * **`z.ZodType<T, z.ZodTypeDef, unknown>` und nicht `z.ZodType<T>`.** Die
 * Kurzform legt den EINGABEtyp des Schemas auf denselben Typ wie seine
 * Ausgabe fest — für ein Schema ohne Umformung stimmt das, für eines MIT
 * Umformung nicht: `centAmount`/`dashboardTrendFactorSchema` aus
 * `@fameacc/contracts` nehmen eine gewöhnliche Zeichenkette entgegen und
 * geben ein markiertes `Money`/`Factor` zurück, und
 * `dashboardKennzahlenBerichtSchema` liess sich deshalb hier gar nicht
 * übergeben. `unknown` ist zugleich die WAHRE Eingabe: Geprüft wird ein
 * frisch geparster JSON-Rumpf, über dessen Form vorher nichts bekannt ist.
 * Die Änderung lockert nur — jedes bisher übergebene Schema passt
 * unverändert weiter.
 */
async function request<T>(
  path: string,
  schema: z.ZodType<T, z.ZodTypeDef, unknown>,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: 'same-origin',
    ...init,
    headers: {
      accept: 'application/json',
      ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...init.headers,
    },
  });

  const text = await response.text();
  let body: unknown = undefined;
  if (text !== '') {
    try {
      body = JSON.parse(text);
    } catch {
      body = undefined;
    }
  }

  if (!response.ok) throw toApiError(response.status, body);

  const parsed = schema.safeParse(body ?? {});
  if (!parsed.success) {
    throw new ApiError(
      response.status,
      'RESPONSE_UNREADABLE',
      `Die Antwort auf ${path} hat eine unerwartete Form.`,
      undefined,
    );
  }
  return parsed.data;
}

/** Baut aus einer Fehlerantwort den `ApiError`. Auch dann, wenn der Rumpf leer ist. */
function toApiError(status: number, body: unknown): ApiError {
  const error = (body as ErrorBody | undefined)?.error;
  const code = typeof error?.code === 'string' ? error.code : 'UNKNOWN';
  const serverMeldung =
    typeof error?.message === 'string'
      ? error.message
      : `Die Anfrage ist mit Status ${String(status)} gescheitert.`;
  // Bei offener Einrichtung sagt die Oberfläche den Satz der Einrichtung und
  // nicht den Text des Servers (`PF-assistent`, `E-1653`): Er benennt den Weg
  // heraus, und `ErrorBox` erkennt an ihm den Fehlerkasten, dem es einen
  // Verweis auf den Assistenten anhängt.
  const message = status === 409 && code === 'EINRICHTUNG_OFFEN' ? EINRICHTUNG_OFFEN_HINWEIS : serverMeldung;
  const correlationId = typeof error?.correlationId === 'string' ? error.correlationId : undefined;
  // Der ganze Knoten, ungedeutet. Der Filter der API setzt die fachlichen
  // Einzelheiten auf dieselbe Ebene wie `code` (siehe `database-error.filter.ts`);
  // welche davon eine Maske braucht, weiss nur die Maske.
  const apiError = new ApiError(status, code, message, correlationId, error);
  // Trifft auf JEDE Anfrage zu, die hier vorbeikommt — die anfängliche
  // `/auth/me` beim Start genauso wie ein Ruf mitten aus einer offenen Maske.
  // Genau deshalb sitzt die Meldung hier und nicht an einer der 30+ Stellen,
  // die `toApiError()` aus `auth/LoginPage.tsx` aufrufen: Diese Funktion ist
  // die einzige Stelle, die JEDE Antwort der API sieht, bevor sie zu einem
  // Fehler wird.
  //
  // `meldeSitzungslage` schliesst `notifyReauthRequired` mit ein; der Aufruf
  // steht hier deshalb nur einmal. Jene Funktion bleibt trotzdem exportiert —
  // sie ist der geprüfte Kern der ältesten der drei Weichen
  // (`test/reauth.test.ts`), und ein Export weniger wäre kein Gewinn.
  meldeSitzungslage(apiError);
  // Die Lizenzsperre (`503 LIZENZ_NUR_LESEN`, Spec 7) meldet sich an der
  // Meldestelle des Hinweisbandes an: Es zeigt denselben Text wie das Band statt
  // einer allgemeinen Fehlermeldung. Die Meldung des Servers selbst ist bereits
  // der Lizenztext; der Fehler bleibt ein `ApiError`, damit jede Maske ihren
  // Fehlerweg unverändert läuft.
  if (apiError.code === 'LIZENZ_NUR_LESEN') meldeLizenzNurLesen();
  meldeEinrichtungOffen(apiError);
  return apiError;
}

/**
 * Das Hinweisband der Lizenz — `GET /api/v1/lizenz/hinweis`, für jede Sitzung,
 * ohne Kennungen (Spec `docs/superpowers/specs/2026-09-29-lizenzierung-design.md`,
 * Abschnitt 8).
 *
 * **Wirft bei jedem Fehlschlag.** Die Schale (`lizenz/LizenzHinweis.tsx`) fängt
 * ihn ab und zeigt dann kein Band: Ein alter API-Stand ohne diese Route darf die
 * Oberfläche nicht stören.
 */
export async function ladeLizenzHinweis(): Promise<LizenzHinweis> {
  return await request('/lizenz/hinweis', lizenzHinweisSchema);
}

/**
 * Die Lizenzseite — die vier Verwaltungsrouten unter `/api/v1/lizenz`, alle
 * mit `lizenz:verwalten` (Spec Lizenzierung, Abschnitt 8; Auftrag
 * `LZ-erneuerung`). Jede liefert den Lizenzstand in derselben Form
 * (`lizenzAuskunftSchema` aus dem Vertragspaket, `.strict()`).
 */
export async function ladeLizenz(): Promise<LizenzAuskunft> {
  return await request('/lizenz', lizenzAuskunftSchema);
}

/**
 * Einen Lizenzschlüssel hinterlegen — `PUT /api/v1/lizenz`. `422
 * LIZENZ_SCHLUESSEL_UNGUELTIG`, wenn der Schlüssel nicht prüfbar ist; dann
 * ist nichts gespeichert. Der Rumpf wird serverseitig nie protokolliert.
 */
export async function hinterlegeLizenz(lizenzschluessel: string): Promise<LizenzAuskunft> {
  return await request('/lizenz', lizenzAuskunftSchema, {
    method: 'PUT',
    body: JSON.stringify({ lizenzschluessel }),
  });
}

/** „Jetzt prüfen" — `POST /api/v1/lizenz/erneuerung` (`409`, `502` als `ApiError`). */
export async function pruefeLizenzJetzt(): Promise<LizenzAuskunft> {
  return await request('/lizenz/erneuerung', lizenzAuskunftSchema, { method: 'POST', body: '{}' });
}

/** „Als neue Installation anmelden" — `POST /api/v1/lizenz/neue-installation`. */
export async function meldeNeueInstallationAn(): Promise<LizenzAuskunft> {
  return await request('/lizenz/neue-installation', lizenzAuskunftSchema, { method: 'POST', body: '{}' });
}

// ---------------------------------------------------------------------------
// Die Datensicherung — `/api/v1/sicherung`, Auftrag `DS-web`
// ---------------------------------------------------------------------------
//
// Sechzehn Routen (Spec 8.1): `status` und `laeufe` lesen mit `sicherung:read`,
// alles Übrige ist `sicherung:manage` und an eine Sitzung gebunden. **Die
// Oberfläche ruft nie ein Werkzeug**: Prüfen, Anlegen, Fortschreiben und das
// Schlüsselpaar legen eine Anforderung an (`D-33`), die der `worker`
// abarbeitet; die Seite fragt ihren Zustand mit `ladeSicherungAnforderung()`
// nach.
//
// **Die Rumpftypen sind die EINGABETYPEN der Vertragsschemas** (`z.input`): Der
// Port hat dort eine Vorgabe, und die Oberfläche soll ihn nennen dürfen, ohne
// ihn nennen zu müssen.

export type SicherungZielPruefenEingabe = z.input<typeof sicherungZielPruefenSchema>;
export type SicherungZielAnlegenEingabe = z.input<typeof sicherungZielAnlegenSchema>;
export type SicherungZielFassungEingabe = z.input<typeof sicherungZielFassungAnfrageSchema>;
export type SicherungBestaetigenEingabe = z.input<typeof sicherungBestaetigenSchema>;
export type SicherungAufbewahrungEingabe = z.input<typeof sicherungAufbewahrungAnfrageSchema>;

/** Der Stand: Ampel je Laufart, Ziel ohne Pfad und Benutzer, Aufbewahrung. */
export async function ladeSicherungStatus(): Promise<SicherungStatus> {
  return await request('/sicherung/status', sicherungStatusSchema);
}

/** Die Laufliste, neueste zuerst; `limit` höchstens 200. */
export async function ladeSicherungLaeufe(
  filter: { readonly art?: SicherungLaufArt; readonly limit?: number } = {},
): Promise<SicherungLaufListe> {
  return await request(`/sicherung/laeufe${query(filter)}`, sicherungLaufListeSchema);
}

/** Das Ziel samt Fassungsliste — als einzige Route mit Pfad und Benutzer. */
export async function ladeSicherungZiel(): Promise<SicherungZielAuskunft> {
  return await request('/sicherung/ziel', sicherungZielAuskunftSchema);
}

/** Zustand und Ergebnis einer Anforderung — hier fragt die Einrichtung nach. */
export async function ladeSicherungAnforderung(id: string): Promise<SicherungAnforderung> {
  return await request(`/sicherung/anforderungen/${encodeURIComponent(id)}`, sicherungAnforderungSchema);
}

/** Das Repository-Passwort einer Neuanlage, genau einmal (`no-store`, Spec 8.1). */
export async function holeSicherungPasswortAb(id: string): Promise<SicherungPasswort> {
  return await request(`/sicherung/anforderungen/${encodeURIComponent(id)}/abholen`, sicherungPasswortSchema, {
    method: 'POST',
    body: '{}',
    cache: 'no-store',
  });
}

/** Das Passwort der geltenden oder unbestätigten Fassung im Klartext (`no-store`, Prüfprotokoll). */
export async function zeigeSicherungPasswort(): Promise<SicherungPasswort> {
  return await request('/sicherung/ziel/passwort-anzeigen', sicherungPasswortSchema, {
    method: 'POST',
    body: '{}',
    cache: 'no-store',
  });
}

/** Ein Schlüsselpaar für das SFTP-Konto erzeugen lassen — liefert die Anforderung. */
export async function fordereSicherungSchluesselpaar(): Promise<SicherungAnforderung> {
  return await request('/sicherung/ziel/schluesselpaar', sicherungAnforderungSchema, {
    method: 'POST',
    body: '{}',
  });
}

/** Ein Ziel prüfen lassen: Verbindung und Host-Schlüssel; mit Fingerabdruck auch Anmeldung und Pfadlage. */
export async function fordereSicherungZielpruefung(
  eingabe: SicherungZielPruefenEingabe,
): Promise<SicherungAnforderung> {
  return await request('/sicherung/ziel/pruefen', sicherungAnforderungSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/** Das Repository anlegen oder einbinden lassen — `422 SICHERUNG_FINGERABDRUCK_FEHLT` ohne bestätigte Prüfung. */
export async function fordereSicherungZielanlage(
  eingabe: SicherungZielAnlegenEingabe,
): Promise<SicherungAnforderung> {
  return await request('/sicherung/ziel/anlegen', sicherungAnforderungSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/** Eine neue Fassung DESSELBEN Repositorys (Uhrzeit, Zugang, Host nach erneuter Prüfung). */
export async function fordereSicherungZielfassung(
  eingabe: SicherungZielFassungEingabe,
): Promise<SicherungAnforderung> {
  return await request('/sicherung/ziel', sicherungAnforderungSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/** Die Pflichtbestätigung (`D-2`, bei einer Wiederherstellung samt `D-38`). */
export async function bestaetigeSicherungZiel(eingabe: SicherungBestaetigenEingabe): Promise<SicherungZielAuskunft> {
  return await request('/sicherung/ziel/bestaetigen', sicherungZielAuskunftSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/** Die Auslagerung abschalten (`D-43`): das Repository am Ziel bleibt unberührt. */
export async function beendeSicherungZiel(): Promise<SicherungZielAuskunft> {
  return await request('/sicherung/ziel/beenden', sicherungZielAuskunftSchema, { method: 'POST', body: '{}' });
}

/** Eine neue Fassung der Aufbewahrung. */
export async function setzeSicherungAufbewahrung(
  eingabe: SicherungAufbewahrungEingabe,
): Promise<SicherungAufbewahrung> {
  return await request('/sicherung/aufbewahrung', sicherungAufbewahrungSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/** „Jetzt sichern" — legt eine Auslagerung als `angefordert` an. */
export async function fordereSicherungLauf(): Promise<SicherungLaufAngefordert> {
  return await request('/sicherung/lauf', sicherungLaufAngefordertSchema, { method: 'POST', body: '{}' });
}

/** „Jetzt prüfen" — legt eine Prüfung als `angefordert` an. */
export async function fordereSicherungPruefung(): Promise<SicherungLaufAngefordert> {
  return await request('/sicherung/pruefung', sicherungLaufAngefordertSchema, { method: 'POST', body: '{}' });
}

/**
 * Die Wiederherstellungskarte als Download (Spec 9, `D-28`).
 *
 * **Ein Geheimnisträger, deshalb `cache: 'no-store'`** und kein Verweis: Das
 * Blatt trägt das Repository-Passwort im Klartext und wird nie archiviert; die
 * API antwortet mit `Cache-Control: no-store` und `Content-Disposition:
 * attachment`. Wie `getDocumentPdf()` liest der Fehlerweg den JSON-Rumpf, damit
 * `409 SICHERUNG_KEIN_ZIEL` als `ApiError` ankommt und nicht als `[object
 * Blob]`. Der Dateiname kommt aus dem Kopf der Antwort; fehlt er, ein fester
 * Name.
 */
export async function ladeSicherungKarte(): Promise<{ readonly bytes: Blob; readonly dateiname: string }> {
  const response = await fetch(`${BASE}/sicherung/wiederherstellungskarte`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { accept: 'application/pdf' },
  });
  if (!response.ok) {
    let body: unknown = undefined;
    try {
      body = JSON.parse(await response.text());
    } catch {
      body = undefined;
    }
    throw toApiError(response.status, body);
  }
  const kopf = response.headers.get('content-disposition') ?? '';
  const dateiname = /filename="([^"]+)"/u.exec(kopf)?.[1] ?? 'wiederherstellungskarte.pdf';
  return { bytes: await response.blob(), dateiname };
}

/** Hängt die gesetzten Filter als Abfrageteil an. Leere Werte bleiben weg. */
function query(params: Readonly<Record<string, string | number | undefined>>): string {
  const search = new URLSearchParams();
  for (const [name, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(name, String(value));
  }
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

/** Erste Anmeldestufe: E-Mail und Passwort. */
export async function login(email: string, password: string): Promise<Stage> {
  return await request('/auth/login', stageSchema, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

/** Passwortwechsel — erzwungen aus der Kette oder freiwillig aus einer Sitzung. */
export async function changePassword(currentPassword: string, newPassword: string): Promise<Stage> {
  return await request('/auth/password', stageSchema, {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

/** Einrichtung des zweiten Faktors. Liefert Geheimnis und QR-Code genau einmal. */
export async function enrollTotp(): Promise<TotpEnrollment> {
  return await request('/auth/totp/enroll', totpEnrollmentSchema, { method: 'POST' });
}

/** Prüfung des Einmalkennworts. */
export async function verifyTotp(token: string): Promise<Stage> {
  return await request('/auth/totp/verify', stageSchema, {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

/**
 * Firmenname und Einrichtungsstand für die Anmeldeseite und die Menüspalte
 * (`PF-marke`, `E-1657`) — **ohne Sitzung** abrufbar, deshalb hier bei den
 * Anmeldeaufrufen und nicht bei den Einstellungen.
 *
 * Die Antwort trägt nur den Namen und den Stand; vor dem Abschluss der
 * Einrichtung ist der Name `null` (Näheres in `markeSchema`).
 */
export async function getMarke(): Promise<Marke> {
  return await request('/einstellungen/marke', markeSchema);
}

/** Wer bin ich, und was darf ich? */
export async function me(): Promise<Me> {
  return await request('/auth/me', meSchema);
}

/**
 * Den Sperrbildschirm aufschliessen (`A-sitzungssperre`).
 *
 * Die Antwort ist 204 ohne Rumpf; geprüft wird deshalb gegen
 * `ohneInhaltSchema`. Anders als `logout` unten läuft der Aufruf trotzdem
 * durch `request()` und nicht an ihm vorbei: Ein Fehlschlag muss hier als
 * `ApiError` ankommen, weil die Maske daraus «Code nicht gültig» oder
 * «Sitzung beendet» macht — und weil nur dieser Weg die Sitzungsweiche
 * (`meldeSitzungslage`) auslöst.
 */
export async function unlockSession(code: string): Promise<void> {
  await request('/auth/unlock', ohneInhaltSchema, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

/** Abmelden. Verwirft die Sitzung serverseitig. */
export async function logout(): Promise<void> {
  await fetch(`${BASE}/auth/logout`, { method: 'POST', credentials: 'same-origin' });
}

/** Die Filter der Buchungsliste. Genau die, die der Endpunkt kennt. */
export interface JournalFilter {
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly sourceSystem?: string | undefined;
  readonly periodId?: string | undefined;
  readonly limit: number;
  readonly offset: number;
}

/** Die Buchungen, jüngste zuerst. */
export async function listEntries(filter: JournalFilter): Promise<readonly JournalEntry[]> {
  const result = await request(`/journal/entries${query({ ...filter })}`, journalListSchema);
  return result.entries;
}

/** Eine Buchung samt ihrer Zeilen. */
export async function getEntry(id: string): Promise<JournalEntryDetail> {
  return await request(`/journal/entries/${encodeURIComponent(id)}`, journalEntryDetailSchema);
}

/** Alle Perioden, jüngste zuerst. */
export async function listPeriods(): Promise<readonly Period[]> {
  const result = await request('/periods', periodListSchema);
  return result.periods;
}

/** Der Kontenrahmen zum heutigen Tag. */
export async function listAccounts(): Promise<readonly Account[]> {
  const result = await request('/accounts', accountListSchema);
  return result.accounts;
}


/**
 * Jede Kontenversion, auch die abgelaufenen.
 *
 * Die Versionshistorie ist der Grund, aus dem `account` überhaupt versioniert
 * ist: Wer eine alte Buchung nachvollzieht, muss den Kontostand sehen, unter
 * dem sie entstand.
 */
export async function listAllAccountVersions(): Promise<readonly Account[]> {
  const result = await request('/accounts?all=true', accountListSchema);
  return result.accounts;
}

/** Legt eine Kontenversion an. */
export async function createAccount(input: AccountInputRaw): Promise<AccountWriteResult> {
  return await request('/accounts', accountWriteResultSchema, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Beendet die laufende Version und legt die neue an — in einer Transaktion. */
export async function createAccountVersion(
  id: string,
  input: AccountNewVersionInputRaw,
): Promise<AccountWriteResult> {
  return await request(`/accounts/${id}/new-version`, accountWriteResultSchema, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Die Filter des Kontoblatts. Genau die, die der Endpunkt kennt. */
export interface KontoblattFilter {
  readonly von?: string | undefined;
  readonly bis?: string | undefined;
  readonly limit: number;
  readonly offset: number;
}

/**
 * Das Kontoblatt eines Sachkontos — Anfangssaldo, Buchungszeilen mit laufendem
 * Saldo, Endsaldo. `number` ist die Kontonummer, nicht die Versions-id: Das
 * Blatt läuft über die Nummer und erfasst damit jede Version.
 */
export async function getKontoblatt(
  number: string,
  filter: KontoblattFilter,
): Promise<Kontoblatt> {
  return await request(
    `/accounts/${encodeURIComponent(number)}/blatt${query({ ...filter })}`,
    kontoblattSchema,
  );
}

// --- Phase 2: Stammdaten --------------------------------------------------

/**
 * Die Filter der Kundenliste. Genau die, die der Endpunkt kennt.
 *
 * `number` trifft `display_customer_no`, `debtor_no` und `external_id`
 * gleichermassen — ein Feld und nicht drei, weil wer eine Nummer in der Hand
 * hat in der Regel nicht weiss, welche der drei es ist.
 */
export interface CustomerFilter {
  readonly number?: string | undefined;
  readonly name?: string | undefined;
  readonly city?: string | undefined;
  readonly country?: string | undefined;
  readonly origin?: string | undefined;
  readonly status?: string | undefined;
  readonly limit: number;
  readonly offset: number;
}

/** Die Kundenliste, nach Kundennummer sortiert. */
export async function listCustomers(filter: CustomerFilter): Promise<readonly Customer[]> {
  const result = await request(`/customers${query({ ...filter })}`, customerListSchema);
  return result.customers;
}

/** Ein Kunde samt seiner offenen Verknüpfungen. */
export async function getCustomer(id: string): Promise<Customer> {
  return await request(`/customers/${encodeURIComponent(id)}`, customerDetailSchema);
}

/**
 * Legt einen Kunden an.
 *
 * **Der Fehler wird weitergereicht und nicht verschluckt.**
 * `CUSTOMER_DUPLICATE_SUSPECTED` trägt die Kandidatenliste im Fehlerkörper,
 * und die Maske braucht sie, um den zweiten Versuch mit `linkTo` beziehungsweise
 * `markDistinct` überhaupt stellen zu können.
 */
export async function createCustomer(
  input: CustomerInputRaw,
  options: {
    readonly linkTo?: readonly string[] | undefined;
    readonly markDistinct?: readonly string[] | undefined;
  } = {},
): Promise<Customer> {
  return await request('/customers', customerDetailSchema, {
    method: 'POST',
    body: JSON.stringify({
      ...input,
      ...(options.linkTo === undefined ? {} : { linkTo: options.linkTo }),
      ...(options.markDistinct === undefined ? {} : { markDistinct: options.markDistinct }),
    }),
  });
}

/** Ändert einen Kunden. Nummern und Herkunft kennt das Schema gar nicht. */
export async function updateCustomer(id: string, patch: CustomerPatchRaw): Promise<Customer> {
  return await request(`/customers/${encodeURIComponent(id)}`, customerDetailSchema, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/** Legt einen Kunden still. Gelöscht wird nicht — es gibt kein `DELETE`. */
export async function deactivateCustomer(id: string): Promise<Customer> {
  return await request(`/customers/${encodeURIComponent(id)}/deactivate`, customerDetailSchema, {
    method: 'POST',
  });
}

/** Nimmt einen stillgelegten Kunden wieder auf. */
export async function activateCustomer(id: string): Promise<Customer> {
  return await request(`/customers/${encodeURIComponent(id)}/activate`, customerDetailSchema, {
    method: 'POST',
  });
}

/**
 * Das aktive SEPA-Lastschriftmandat, die Historie, und der Referenzvorschlag
 * für ein neues (Task 2 `[LS-mandat]`, Spec Abschnitte 5, 6). Die IBAN steht
 * bereits maskiert — der Dienst gibt sie nie im Klartext heraus.
 */
export async function getSepaMandate(customerId: string): Promise<SepaMandatUebersicht> {
  return await request(
    `/customers/${encodeURIComponent(customerId)}/sepa-mandate`,
    sepaMandatUebersichtSchema,
  );
}

/** Legt ein SEPA-Lastschriftmandat an. */
export async function legeSepaMandatAn(
  customerId: string,
  eingabe: SepaMandatAnlage,
): Promise<SepaMandat> {
  return await request(
    `/customers/${encodeURIComponent(customerId)}/sepa-mandate`,
    sepaMandatSchema,
    { method: 'POST', body: JSON.stringify(eingabe) },
  );
}

/** Widerruft ein SEPA-Lastschriftmandat. */
export async function widerrufeSepaMandat(mandatId: string, grund: string): Promise<SepaMandat> {
  return await request(`/sepa-mandate/${encodeURIComponent(mandatId)}/widerrufen`, sepaMandatSchema, {
    method: 'POST',
    body: JSON.stringify({ grund }),
  });
}

/** Der Arbeitsvorrat der offenen Dublettenkandidaten, beide Sätze vollständig. */
export async function listDuplicates(): Promise<readonly DuplicateEntry[]> {
  const result = await request('/customers/duplicates', duplicateListSchema);
  return result.candidates;
}

/** Entscheidet einen Kandidaten. Eine Entscheidung fällt genau einmal. */
export async function decideDuplicate(
  linkId: string,
  status: 'duplicate' | 'distinct',
): Promise<Link> {
  return await request(`/customers/duplicates/${encodeURIComponent(linkId)}/decide`, linkSchema, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

/** Gibt eine Verknüpfung frei. Die Begründung ist Pflicht — sie steht im Nachweis. */
export async function releaseDuplicate(linkId: string, reason: string): Promise<Link> {
  return await request(`/customers/duplicates/${encodeURIComponent(linkId)}/release`, linkSchema, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/** Erklärt eine Verknüpfung von Hand. `candidate` gibt es hier nicht. */
export async function createLink(customerId: string, input: CustomerLinkInput): Promise<Link> {
  return await request(`/customers/${encodeURIComponent(customerId)}/links`, linkSchema, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Zeichnet eine von Hand durchgeführte BZSt-Abfrage am Kunden auf. */
export async function recordCustomerVatCheck(id: string, input: VatCheckInput): Promise<VatCheck> {
  return await request(`/customers/${encodeURIComponent(id)}/vat-check`, vatCheckSchema, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Die Prüfhistorie eines Kunden. Eine Aufzeichnung, die niemand ansehen kann, ist kein Nachweis. */
export async function listCustomerVatChecks(id: string): Promise<readonly VatCheck[]> {
  const result = await request(
    `/customers/${encodeURIComponent(id)}/vat-checks`,
    vatCheckListSchema,
  );
  return result.checks;
}

/**
 * Umsatz je Kalenderjahr und offene Posten eines Kunden — die Kundenakte.
 *
 * **Ohne die Belege.** Die Kundenseite holt sie über `listDocuments()` mit
 * `customerId`, denselben Weg wie die Belegliste; ein zweiter Belegweg wäre
 * eine zweite Liste, die bei jedem neuen Filter zurückbliebe.
 */
export async function getKundenakte(id: string): Promise<Kundenakte> {
  return await request(`/customers/${encodeURIComponent(id)}/akte`, kundenakteSchema);
}

/** Die Filter der Kreditorenliste. `origin` gibt es hier nicht — Kreditoren entstehen nur von Hand. */
export interface VendorFilter {
  readonly number?: string | undefined;
  readonly name?: string | undefined;
  readonly city?: string | undefined;
  readonly country?: string | undefined;
  readonly status?: string | undefined;
  readonly limit: number;
  readonly offset: number;
}

/** Die Kreditorenliste, nach Kreditorennummer sortiert. */
export async function listVendors(filter: VendorFilter): Promise<readonly Vendor[]> {
  const result = await request(`/vendors${query({ ...filter })}`, vendorListSchema);
  return result.vendors;
}

/** Ein Kreditor. */
export async function getVendor(id: string): Promise<Vendor> {
  return await request(`/vendors/${encodeURIComponent(id)}`, vendorDetailSchema);
}

/**
 * Legt einen Kreditor an.
 *
 * `acknowledgedSimilar` quittiert die Warnung über Gleichnamige. Ohne sie
 * lehnt der Dienst mit `VENDOR_SIMILAR_EXISTS` ab und nennt sie — eine
 * Warnung, keine Sperre.
 */
export async function createVendor(
  input: VendorInputRaw,
  options: { readonly acknowledgedSimilar?: boolean | undefined } = {},
): Promise<Vendor> {
  return await request('/vendors', vendorDetailSchema, {
    method: 'POST',
    body: JSON.stringify({
      ...input,
      ...(options.acknowledgedSimilar === undefined
        ? {}
        : { acknowledgedSimilar: options.acknowledgedSimilar }),
    }),
  });
}

/** Ändert einen Kreditor. `creditorNo` kommt nicht vor. */
export async function updateVendor(id: string, patch: VendorPatchRaw): Promise<Vendor> {
  return await request(`/vendors/${encodeURIComponent(id)}`, vendorDetailSchema, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/** Legt einen Kreditor still. */
export async function deactivateVendor(id: string): Promise<Vendor> {
  return await request(`/vendors/${encodeURIComponent(id)}/deactivate`, vendorDetailSchema, {
    method: 'POST',
  });
}

/** Die Erkennungsregeln eines Kreditors, aktive zuerst. */
export async function listVendorErkennung(id: string): Promise<readonly VendorErkennungRegel[]> {
  const result = await request(
    `/vendors/${encodeURIComponent(id)}/erkennung`,
    vendorErkennungListeSchema,
  );
  return result.regeln;
}

/** Legt eine Erkennungsregel an. Der Wert wird vom Server normalisiert. */
export async function createVendorErkennung(
  id: string,
  input: { readonly art: VendorErkennungArt; readonly wert: string },
): Promise<VendorErkennungRegel> {
  return await request(`/vendors/${encodeURIComponent(id)}/erkennung`, vendorErkennungRegelSchema, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Legt eine Erkennungsregel still. */
export async function deactivateVendorErkennung(
  id: string,
  regelId: string,
): Promise<VendorErkennungRegel> {
  return await request(
    `/vendors/${encodeURIComponent(id)}/erkennung/${encodeURIComponent(regelId)}/deaktivieren`,
    vendorErkennungRegelSchema,
    { method: 'POST' },
  );
}

/** Nimmt einen stillgelegten Kreditor wieder auf. */
export async function activateVendor(id: string): Promise<Vendor> {
  return await request(`/vendors/${encodeURIComponent(id)}/activate`, vendorDetailSchema, {
    method: 'POST',
  });
}

/** Zeichnet eine von Hand durchgeführte BZSt-Abfrage am Kreditor auf. */
export async function recordVendorVatCheck(id: string, input: VatCheckInput): Promise<VatCheck> {
  return await request(`/vendors/${encodeURIComponent(id)}/vat-check`, vatCheckSchema, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Die Prüfhistorie eines Kreditors. */
export async function listVendorVatChecks(id: string): Promise<readonly VatCheck[]> {
  const result = await request(`/vendors/${encodeURIComponent(id)}/vat-checks`, vatCheckListSchema);
  return result.checks;
}

/**
 * Die Länderliste für die Auswahlfelder.
 *
 * Sie kommt vom Server und wird nicht im Frontend geführt: Zwei Listen, die
 * dasselbe behaupten, laufen auseinander.
 */
export async function listCountries(): Promise<readonly Country[]> {
  const result = await request('/countries', countryListSchema);
  return result.countries;
}

/**
 * Die zwei Bezeichnungen der Oberflaeche -- Nebenbuch und Kundenherkunft
 * (`GET /einstellungen/bezeichnungen`, `PF-erkennung`, `E-1645`). Gelesen und
 * geteilt ueber `useBezeichnungen()` in `api/bezeichnungen.ts`.
 */
export async function ladeBezeichnungen(): Promise<Bezeichnungen> {
  return await request('/einstellungen/bezeichnungen', bezeichnungenSchema);
}

/** Der Stand der Funktionsschalter, lesbar für jede Sitzung (`GET /einstellungen/funktionen`, `D-57`). */
export async function ladeFunktionsstand(): Promise<Funktionsstand> {
  return await request('/einstellungen/funktionen', funktionsstandSchema);
}

/** Der Schalter des Kundenguthabens samt Bestand (`GET /einstellungen/guthaben`, `einstellung:manage`). */
export async function getGuthabenSchalter(): Promise<GuthabenSchalter> {
  return await request('/einstellungen/guthaben', guthabenSchalterSchema);
}

/** Schaltet das Kundenguthaben ein oder aus (`PUT /einstellungen/guthaben`). */
export async function setzeGuthabenSchalter(eingabe: GuthabenSchalten): Promise<GuthabenSchalter> {
  return await request('/einstellungen/guthaben', guthabenSchalterSchema, {
    method: 'PUT',
    body: JSON.stringify(eingabe),
  });
}

// --- Der Einrichtungsassistent (`PF-assistent`) ---------------------------

/** Die heute geltenden Systemeinstellungen (`GET /einstellungen`, `einstellung:manage`). */
export async function getEinstellungen(): Promise<Einstellungen> {
  return await request('/einstellungen', einstellungenSchema);
}

/** Der Stand der Ersteinrichtung samt offenen Punkten (`GET /einstellungen/einrichtung`). */
export async function getEinrichtungsstand(): Promise<Einrichtungsstand> {
  return await request('/einstellungen/einrichtung', einrichtungsstandSchema);
}

/** Startet die Einrichtung und verwirft dabei den Grundbestand (`POST …/einrichtung/start`). */
export async function starteEinrichtung(): Promise<Einrichtungsstand> {
  return await request('/einstellungen/einrichtung/start', einrichtungsstandSchema, {
    method: 'POST',
  });
}

/** Legt den Beginn der Buchführung fest (`PUT …/einrichtung/journalbeginn`). */
export async function setzeJournalbeginn(journalbeginn: string): Promise<Einrichtungsstand> {
  return await request('/einstellungen/einrichtung/journalbeginn', einrichtungsstandSchema, {
    method: 'PUT',
    body: JSON.stringify({ journalbeginn }),
  });
}

/** Die Zuständigkeitsgrenzen der Ausgangs- und Eingangsbelege (`GET …/zustaendigkeit`). */
export async function ladeZustaendigkeit(): Promise<Zustaendigkeit> {
  return await request('/einstellungen/zustaendigkeit', zustaendigkeitSchema);
}

/** Setzt eine oder beide Zuständigkeitsgrenzen (`PUT …/zustaendigkeit`). */
export async function setzeZustaendigkeit(eingabe: ZustaendigkeitSetzen): Promise<Zustaendigkeit> {
  return await request('/einstellungen/zustaendigkeit', zustaendigkeitSchema, {
    method: 'PUT',
    body: JSON.stringify(eingabe),
  });
}

/** Kennzeichen und Startnummer eines Belegkreises (`PUT …/einrichtung/kreise/:rangeKey`). */
export async function setzeEinrichtungKreis(
  rangeKey: string,
  eingabe: EinrichtungKreisSetzen,
): Promise<Einrichtungsstand> {
  return await request(
    `/einstellungen/einrichtung/kreise/${encodeURIComponent(rangeKey)}`,
    einrichtungsstandSchema,
    { method: 'PUT', body: JSON.stringify(eingabe) },
  );
}

/** Schliesst die Einrichtung ab (`POST …/einrichtung/abschluss`). */
export async function schliesseEinrichtungAb(): Promise<Einrichtungsstand> {
  return await request('/einstellungen/einrichtung/abschluss', einrichtungsstandSchema, {
    method: 'POST',
  });
}

/** Erfasst den Umsatz eines Vorsystems je Jahr (`PUT /einstellungen/umsatz-vorsystem`). */
export async function setzeUmsatzVorsystem(
  eingabe: UmsatzVorsystemSetzenEingabe,
): Promise<UmsatzVorsystem> {
  return await request('/einstellungen/umsatz-vorsystem', umsatzVorsystemSchema, {
    method: 'PUT',
    body: JSON.stringify(eingabe),
  });
}

// ---------------------------------------------------------------------------
// Vorsystem — Einrichtungsschritt (LX-einrichtungsschritt)
//
// Die Routen der Übernahme aus Lexware Office (`/api/v1/vorsystem/…`, Spec
// Lexware-Migration 20.8). Alle verlangen `vorsystem:import`. Der Schlüssel geht
// nur hinaus (`PUT`) und kommt nie zurück: Die Antwort nennt, ob er hinterlegt
// ist, nicht was er ist.
// ---------------------------------------------------------------------------

/** `GET /vorsystem/uebernahme` — Stand der Übernahme: Schlüssel, Abrufe, Schritte, Checkliste. */
export async function getVorsystemUebernahme(): Promise<VorsystemUebernahmeStatus> {
  return await request('/vorsystem/uebernahme', vorsystemUebernahmeStatusSchema);
}

/** `PUT /vorsystem/schluessel` — hinterlegt den Schnittstellenschlüssel (verschlüsselt, nie zurückgegeben). */
export async function setzeVorsystemSchluessel(
  eingabe: VorsystemSchluesselEingabe,
): Promise<VorsystemSchluesselStatus> {
  return await request('/vorsystem/schluessel', vorsystemSchluesselStatusSchema, {
    method: 'PUT',
    body: JSON.stringify(eingabe),
  });
}

/** `PUT /vorsystem/einstellung` — setzt den Tag „Lexware Office läuft bis“ und die Zahlungsquelle. */
export async function setzeVorsystemEinstellung(eingabe: VorsystemEinstellungEingabe): Promise<VorsystemEinstellung> {
  return await request('/vorsystem/einstellung', vorsystemEinstellungSchema, {
    method: 'PUT',
    body: JSON.stringify(eingabe),
  });
}

/** `DELETE /vorsystem/schluessel` — löscht den hinterlegten Schlüssel auf Wunsch des Menschen. */
export async function loescheVorsystemSchluessel(): Promise<VorsystemSchluesselStatus> {
  return await request('/vorsystem/schluessel', vorsystemSchluesselStatusSchema, { method: 'DELETE' });
}

/** `POST /vorsystem/auftraege` — fordert eine Verbindungsprüfung oder einen Schritt an (läuft im Hintergrund). */
export async function legeVorsystemAuftragAn(anlage: VorsystemAuftragAnlage): Promise<VorsystemAuftrag> {
  return await request('/vorsystem/auftraege', vorsystemAuftragSchema, {
    method: 'POST',
    body: JSON.stringify(anlage),
  });
}

/** `GET /vorsystem/auftraege/:id` — Stand eines Auftrags (die Oberfläche fragt alle fünf Sekunden). */
export async function getVorsystemAuftrag(id: string): Promise<VorsystemAuftrag> {
  return await request(`/vorsystem/auftraege/${encodeURIComponent(id)}`, vorsystemAuftragSchema);
}

/** `POST /vorsystem/auftraege/:id/abbrechen` — bricht einen Auftrag ab. */
export async function brecheVorsystemAuftragAb(id: string): Promise<VorsystemAuftrag> {
  return await request(`/vorsystem/auftraege/${encodeURIComponent(id)}/abbrechen`, vorsystemAuftragSchema, {
    method: 'POST',
  });
}

/** `GET /vorsystem/befunde` — die Befunde des jüngsten Laufs je Schritt. */
export async function getVorsystemBefunde(): Promise<VorsystemBefunde> {
  return await request('/vorsystem/befunde', vorsystemBefundeSchema);
}

/** Schreibt die Steuereinstellungen fort (`POST /einstellungen/steuerprofil`). */
export async function schreibeSteuerprofilFort(
  eingabe: SteuerprofilFortschreiben,
): Promise<Steuerprofil> {
  return await request('/einstellungen/steuerprofil', steuerprofilSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/** Schreibt die Freigaberegel fort (`POST /einstellungen/freigabeprofil`). */
export async function schreibeFreigabeprofilFort(
  eingabe: FreigabeprofilFortschreiben,
): Promise<Freigabeprofil> {
  return await request('/einstellungen/freigabeprofil', freigabeprofilSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/** Die Kennzeichen der Zahlungserkennung (`GET /einstellungen/erkennung`). */
export async function getErkennung(): Promise<ErkennungEinstellung> {
  return await request('/einstellungen/erkennung', erkennungEinstellungSchema);
}

/** Setzt die Kennzeichen der Zahlungserkennung (`PUT /einstellungen/erkennung`). */
export async function setzeErkennung(eingabe: ErkennungSetzen): Promise<ErkennungEinstellung> {
  return await request('/einstellungen/erkennung', erkennungEinstellungSchema, {
    method: 'PUT',
    body: JSON.stringify(eingabe),
  });
}

/** Die Betriebseinstellungen (`GET /einstellungen/betrieb`). */
export async function getBetrieb(): Promise<BetriebEinstellung> {
  return await request('/einstellungen/betrieb', betriebEinstellungSchema);
}

/** Setzt Bezeichnungen und Kundennummernform (`PUT /einstellungen/betrieb`). */
export async function setzeBetrieb(eingabe: BetriebSetzen): Promise<BetriebEinstellung> {
  return await request('/einstellungen/betrieb', betriebEinstellungSchema, {
    method: 'PUT',
    body: JSON.stringify(eingabe),
  });
}

/**
 * Legt ein Logo im Logo-Archiv ab (`POST /issuer-profiles/logo`). Der Rumpf IST
 * die Datei, der Medientyp steht im Kopf; sichtbar wird das Logo erst mit einer
 * neuen Fassung des Rechnungsstellers (`logoQuelle: 'ablage'`).
 */
export async function ladeLogoHoch(
  datei: Blob,
  mediaType: LogoMedientyp,
): Promise<IssuerLogoUploadResult> {
  return await request('/issuer-profiles/logo', issuerLogoUploadResultSchema, {
    method: 'POST',
    body: datei,
    headers: { 'content-type': mediaType },
  });
}

/** Die Nummernkreise samt ihrer Grenzen. */
export async function listNumberRanges(): Promise<readonly NumberRange[]> {
  const result = await request('/number-ranges', numberRangeListSchema);
  return result.ranges;
}

// --- Phase 3: Belegwesen --------------------------------------------------

/**
 * Die Filter der Belegliste. Genau die, die der Endpunkt kennt.
 *
 * `number` trifft **Belegnummer und Kundennummer gleichermassen** — der
 * praktische Fall ist ein Kunde am Telefon, der eine der beiden nennt
 * (Spec 8.8). Ein Feld und nicht zwei, weil wer eine Nummer in der Hand hat in
 * der Regel nicht weiss, welche der beiden es ist.
 */
export interface DocumentFilter {
  readonly number?: string | undefined;
  readonly customerId?: string | undefined;
  readonly type?: string | undefined;
  readonly status?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly rangeKey?: string | undefined;
  /**
   * Der VEREINTE Belegzustand (`AI-zustandvereint`, 2026-09-05) — er ersetzt
   * den vorherigen Filter `legacyStatus` (`AI-zahlstatusanzeige`), der nur
   * Archivbelege finden konnte. Der Server rechnet ihn in SQL aus `status` und
   * `legacy_status`; der Filter wirkt deshalb auf den GANZEN Bestand und nicht
   * nur auf die geladene Seite.
   */
  readonly belegzustand?: string | undefined;
  /**
   * Sortierung — `issueDate` oder `number` (Auftrag `W-belegsortierung`,
   * 2026-09-10). Fehlt sie, ordnet der Server wie bisher: Kreis, Zählerstand,
   * Anlagezeitpunkt, Entwürfe zuerst. Ein unbekannter Wert wird vom Endpunkt
   * mit 400 abgewiesen und nicht übergangen — deshalb schickt die Oberfläche
   * nur, was `SORTIERBARE_SPALTEN` in `DocumentsPage.tsx` führt.
   */
  readonly sort?: string | undefined;
  /** `asc` oder `desc`. Ohne `sort` wirkungslos. */
  readonly order?: string | undefined;
  readonly limit: number;
  readonly offset: number;
}

/** Die Belegliste samt der echten Gesamtzahl zu denselben Filtern. */
export interface DocumentListResult {
  readonly documents: readonly DocumentSummary[];
  readonly total: number;
}

/**
 * Die Belegliste.
 *
 * Sortiert kommt sie vom Server — über `(number_range_key, number_sequence)` und
 * nicht über `number`. Die Zeichenkette bricht am Stellenwechsel des Zählers,
 * und eine Sortierung im Browser ordnete überdies nur die geladene Seite.
 *
 * `total` ist die echte Gesamtzahl zu denselben Filtern, nicht die
 * Seitengrösse (Auftrag `W-belegtabs`).
 */
export async function listDocuments(filter: DocumentFilter): Promise<DocumentListResult> {
  return await request(`/documents${query({ ...filter })}`, documentListSchema);
}

/** Ein Beleg samt Positionen und Steuersatzgruppen. */
export async function getDocument(id: string): Promise<DocumentView> {
  return await request(`/documents/${encodeURIComponent(id)}`, documentViewSchema);
}

/** Legt einen Belegentwurf an. */
export async function createDocument(input: DocumentInputRaw): Promise<DocumentView> {
  return await request('/documents', documentViewSchema, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Ändert einen Entwurf. Ohne `type`, `customerId` und `priceBasis`. */
export async function updateDocument(id: string, patch: DocumentPatchRaw): Promise<DocumentView> {
  return await request(`/documents/${encodeURIComponent(id)}`, documentViewSchema, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/**
 * Ersetzt **alle** Positionen eines Entwurfs.
 *
 * `PUT` und nicht `POST`: Es ist eine Ersetzung der gesamten Positionsmenge und
 * keine Ergänzung. Die Antwort trägt den neuen GESAMTZUSTAND einschliesslich
 * Gruppen und Summen — das ist der Grund, aus dem im Browser nichts gerechnet
 * wird (Spec 9).
 */
export async function replaceDocumentLines(
  id: string,
  lines: readonly DocumentLineInputRaw[],
): Promise<DocumentView> {
  return await request(`/documents/${encodeURIComponent(id)}/lines`, documentViewSchema, {
    method: 'PUT',
    body: JSON.stringify({ lines }),
  });
}

/**
 * Wechselt die Preisbasis eines Entwurfs.
 *
 * `mode` ist Pflicht, weil die Umrechnung nicht eindeutig ist: `keep_net` lässt
 * den Nettopreis stehen und ändert den Endpreis, `keep_gross` umgekehrt. Eine
 * Vorgabe wäre eine stille Entscheidung über die Marge.
 */
export async function setDocumentPriceBasis(
  id: string,
  basis: 'net' | 'gross',
  mode: 'keep_net' | 'keep_gross',
): Promise<PriceBasisChangeResult> {
  return await request(`/documents/${encodeURIComponent(id)}/price-basis`, priceBasisChangeSchema, {
    method: 'POST',
    body: JSON.stringify({ basis, mode }),
  });
}

/**
 * Gibt einen Beleg frei — die zweite Person des Vier-Augen-Prinzips.
 *
 * Der Rumpf ist leer: `approved_by` kommt aus der Sitzung. Käme es von aussen,
 * wäre das Vier-Augen-Prinzip eine Behauptung des Aufrufers.
 */
export async function approveDocument(id: string): Promise<DocumentView> {
  return await request(`/documents/${encodeURIComponent(id)}/approve`, documentViewSchema, {
    method: 'POST',
  });
}

/** Stellt einen Beleg aus — Nummer, Buchung, Zustandswechsel. */
export async function issueDocument(
  id: string,
  input: { readonly issueDate?: string | undefined } = {},
): Promise<IssueResult> {
  return await request(`/documents/${encodeURIComponent(id)}/issue`, issueResultSchema, {
    method: 'POST',
    body: JSON.stringify(input.issueDate === undefined ? {} : { issueDate: input.issueDate }),
  });
}

/**
 * Legt den Stornoentwurf zu einem ausgestellten Beleg an.
 *
 * Die Begründung ist Pflicht (Spec 8.4). Ein Knopf, der einen Pflichtwert leer
 * lässt, erzeugt einen 400, den niemand erwartet hat — deshalb hat die
 * Oberfläche einen Dialog und keinen nackten Knopf.
 *
 * Ausgestellt wird der Storno über den gewöhnlichen `issue`-Weg, weil zwischen
 * Anlage und Ausstellung die Freigabe liegt.
 */
export async function cancelDocument(id: string, reason: string): Promise<DocumentView> {
  return await request(`/documents/${encodeURIComponent(id)}/cancel`, documentViewSchema, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/**
 * Legt den Entwurf einer Rechnungskorrektur an.
 *
 * Ihre Positionen werden **frei erfasst** und nicht kopiert (anders als beim
 * Storno): Eine Rechnungskorrektur ist eine Teilkorrektur.
 */
export async function createCreditNote(
  id: string,
  input: {
    readonly reason: string;
    readonly lines: readonly DocumentLineInputRaw[];
    readonly serviceDate?: string | undefined;
    readonly dueDate?: string | undefined;
  },
): Promise<DocumentView> {
  return await request(`/documents/${encodeURIComponent(id)}/credit-note`, documentViewSchema, {
    method: 'POST',
    body: JSON.stringify({
      reason: input.reason,
      lines: input.lines,
      ...(input.serviceDate === undefined ? {} : { serviceDate: input.serviceDate }),
      ...(input.dueDate === undefined ? {} : { dueDate: input.dueDate }),
    }),
  });
}

/**
 * Verschickt den Beleg per E-Mail — der Anhang ist das archivierte PDF/A-Stück,
 * nie neu gerendert. Anders als `markDocumentSent` setzt dies `sent_at` NICHT
 * und lässt sich beliebig oft wiederholen; die eigene Spur ist die
 * Versandhistorie (`getDocumentVersandHistory`).
 */
export async function sendDocumentVersand(
  id: string,
  input: { readonly empfaenger: string; readonly betreff: string; readonly text: string },
): Promise<DocumentVersandEntry> {
  return await request(`/documents/${encodeURIComponent(id)}/versand`, documentVersandEntrySchema, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Der Entwurf der Versandmail — Betreff, Text und HTML-Rumpf aus der scharfen
 * E-Mail-Vorlage, gegen DIESEN Beleg gerendert (`C-mailversandnaht`, `MV-7`).
 *
 * **Der Versanddialog belegt daraus vor, statt einen Text zu erfinden.** Bis
 * zu diesem Auftrag stand der Vorgabetext als Zeichenkettenliteral in
 * `VersandDialog.tsx`; der Server konnte deshalb nicht wissen, was er
 * versendet hat, und `document_versand` trug den Rumpf nicht. Ein zweites
 * Exemplar hier wäre genau die Drift zurück.
 */
export async function getDocumentVersandEntwurf(
  id: string,
): Promise<DocumentVersandEntwurf> {
  return await request(
    `/documents/${encodeURIComponent(id)}/versand/entwurf`,
    documentVersandEntwurfSchema,
  );
}

/** Die Versandhistorie eines Belegs — jüngster Versuch zuerst, auch der gescheiterte. */
export async function getDocumentVersandHistory(
  id: string,
): Promise<readonly DocumentVersandEntry[]> {
  const result = await request(
    `/documents/${encodeURIComponent(id)}/versand`,
    documentVersandHistorySchema,
  );
  return result.entries;
}

/** Vermerkt den Versand. Trägt nichts — der Dienst setzt den Zeitpunkt. */
export async function markDocumentSent(id: string): Promise<DocumentView> {
  return await request(`/documents/${encodeURIComponent(id)}/mark-sent`, documentViewSchema, {
    method: 'POST',
  });
}

/**
 * Das archivierte Stück als Blob.
 *
 * **Kein `request<T>()`:** Die vorhandene Hilfe prüft die Antwort gegen ein
 * Zod-Schema und setzt JSON voraus. Hier kommen Bytes. Der Rest ist gleich —
 * `credentials: 'same-origin'`, damit das Sitzungscookie mitgeht, und die
 * Fehlerbehandlung über `ApiError`, damit ein 404 als `DOCUMENT_PDF_MISSING`
 * ankommt und nicht als „Netzwerkfehler".
 *
 * **Bei einem Fehlerstatus wird der Rumpf als JSON gelesen.** Die API antwortet
 * auf einen Fehler mit JSON, auch wenn der Erfolgsfall Bytes liefert. Wer das
 * übersieht, zeigt dem Anwender `[object Blob]`.
 */
export async function getDocumentPdf(id: string): Promise<Blob> {
  const response = await fetch(`${BASE}/documents/${encodeURIComponent(id)}/pdf`, {
    credentials: 'same-origin',
    headers: { accept: 'application/pdf' },
  });
  if (!response.ok) {
    let body: unknown = undefined;
    try {
      body = JSON.parse(await response.text());
    } catch {
      body = undefined;
    }
    throw toApiError(response.status, body);
  }
  return await response.blob();
}

/** Holt das Rendern nach. Liefert den Beleg mit gefülltem `pdfObjectKey`. */
export async function renderDocument(id: string): Promise<DocumentView> {
  return await request(`/documents/${encodeURIComponent(id)}/render`, documentViewSchema, {
    method: 'POST',
  });
}

/**
 * Die Belege ohne Archivstück.
 *
 * **Seit Bauabschnitt 3c ist diese Sicht im Regelfall LEER**: `issue()` rendert
 * nach dem Commit. Sie ist nicht leer, wenn ein Renderlauf gescheitert ist — und
 * dann ist sie der Arbeitsvorrat, aus dem sich das Nachholen anstossen lässt
 * (Spec 8.6.4, 12.3 Punkt 5).
 */
export async function listDocumentsWithoutPdf(): Promise<readonly DocumentWithoutPdf[]> {
  const result = await request('/documents/without-pdf', documentWithoutPdfListSchema);
  return result.documents;
}

/** Der Lückenbericht über alle Nummernkreise. */
export async function listNumberRangeReport(): Promise<readonly NumberRangeReport[]> {
  const result = await request('/documents/number-ranges', numberRangeReportListSchema);
  return result.ranges;
}

/** Die fehlenden Zählerstände eines Kreises, benannt. */
export async function getNumberGaps(rangeKey: string): Promise<NumberGapDetail> {
  return await request(`/documents/gaps/${encodeURIComponent(rangeKey)}`, numberGapDetailSchema);
}

/**
 * Trägt eine Lücke ein.
 *
 * **Der Eintrag ist unveränderbar.** Eine Begründung, die man umschreiben kann,
 * ist keine; eine falsche ist damit sichtbar statt korrigierbar (Spec 8.8). Die
 * Maske sagt das **vor** dem Absenden.
 */
export async function explainNumberGap(input: NumberGapInputRaw): Promise<NumberGap> {
  return await request('/documents/gaps', numberGapSchema, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Die Fassungen des Rechnungsstellers, neueste zuerst. */
export async function listIssuerProfiles(): Promise<readonly IssuerProfile[]> {
  const result = await request('/issuer-profiles', issuerProfileListSchema);
  return result.profiles;
}

/** Legt die erste Fassung des Rechnungsstellers an. */
export async function createIssuerProfile(input: IssuerProfileInputRaw): Promise<IssuerProfile> {
  return await request('/issuer-profiles', issuerProfileSchema, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Beendet die laufende Fassung und legt die neue an — in **einer** Transaktion.
 *
 * Getrennt von `createIssuerProfile`, weil es zwei verschiedene Vorgänge sind.
 * Dieselbe Trennung wie bei der Kontenversion.
 */
export async function createIssuerProfileVersion(
  id: string,
  input: IssuerProfileInputRaw,
): Promise<IssuerProfile> {
  return await request(
    `/issuer-profiles/${encodeURIComponent(id)}/new-version`,
    issuerProfileSchema,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

// --- Bankanbindung Stufe 1 ------------------------------------------------

/** Die Bankkonten samt letztem Auszug (Spec 10, Ansicht 1). */
export async function listBankAccounts(): Promise<readonly BankAccount[]> {
  const result = await request('/bank-accounts', bankAccountListSchema);
  return result.accounts;
}

/** Ein Bankkonto. */
export async function getBankAccount(id: string): Promise<BankAccount> {
  return await request(`/bank-accounts/${encodeURIComponent(id)}`, bankAccountSchema);
}

/**
 * Legt ein Bankkonto an.
 *
 * Der Fehler wird weitergereicht und nicht verschluckt: Eine fehlerhafte IBAN
 * und eine bereits belegte IBAN sind beide Ablehnungen, die der Anwender im
 * Klartext lesen muss (E-203). Die IBAN wird Bestandteil des Ablagepfades
 * (Spec 8) — sie ist keine Kosmetik.
 */
export async function createBankAccount(input: BankAccountCreateRaw): Promise<BankAccount> {
  return await request('/bank-accounts', bankAccountSchema, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Die Filter der Umsatzliste. Genau die, die der Endpunkt kennt. */
export interface BankTransactionFilter {
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly matchStatus?: string | undefined;
  /**
   * Der Suchbegriff über Verwendungszweck, Gegenpartei und deren IBAN sowie, wenn
   * er wie ein Betrag oder ein Datum aussieht, über Betrag und Datum.
   *
   * **Ein leerer Begriff kommt gar nicht erst auf die Leitung:** `query()`
   * überspringt `''` genauso wie `undefined`. Die Aussage «ein leeres Suchfeld
   * ist kein Filter» steht damit an drei Stellen und widerspricht sich an
   * keiner — hier, im Vertrag (`bankSearchTermSchema` macht aus Leerraum
   * `undefined`) und in der Abfrage (`undefined` lässt die Bedingung weg).
   */
  readonly search?: string | undefined;
  readonly limit: number;
  readonly offset: number;
  /**
   * Zeigt Guthabensperre und Aufhebung, statt sie auszublenden.
   *
   * `false` oder fehlend ist die Voreinstellung (`Z-guthabensperre`) — der
   * Umschalter "Technische Zeilen anzeigen" setzt ihn auf `true`. `query()`
   * kennt keinen Wahrheitswert; `'true'` geht auf die Leitung, `undefined`
   * lässt den Parameter ganz weg — dieselbe Wirkung wie ein weggelassener
   * `technische`-Parameter beim Endpunkt.
   */
  readonly technische?: boolean | undefined;
}

/**
 * Die Umsätze eines Kontos.
 *
 * Sortiert kommt die Liste vom Server. Eine Sortierung im Browser ordnete nur
 * die geladene Seite und gäbe je nach Blätterstand eine andere Antwort auf
 * dieselbe Frage — dasselbe Argument wie bei der Belegliste.
 */
export async function listBankTransactions(
  accountId: string,
  filter: BankTransactionFilter,
): Promise<{
  readonly transactions: readonly BankTransaction[];
  readonly total: number;
  readonly accountTotal: number;
  readonly technicalHidden: number;
}> {
  // **`total` kommt mit und wird nicht weggeworfen.** Ohne die Trefferzahl sind
  // «nichts gefunden» und «nichts vorhanden» dieselbe leere Tabelle, und ein
  // Anwender weiss nicht, ob er sich vertippt hat. Sie zählt ALLE Treffer der
  // Filter und nicht die gelieferte Seite.
  //
  // **`accountTotal` kommt seit dem 2026-08-21 daneben mit**, und ohne es gibt
  // `total` sich als Gesamtzahl aus: Ueber der Bewegungsliste des PayPal-Kontos
  // stand «5.229 Bewegungen», das Konto traegt 5.434 — die Differenz blendet
  // der vorbelegte Filter «Zuordnung: Offen» aus.
  //
  // **`technicalHidden` kommt seit dem 2026-08-23 dazu** (`Z-guthabensperre`):
  // Guthabensperre und ihre Aufhebung sind per Voreinstellung ausgeblendet,
  // und ohne diese Zahl saehe die Liste einfach kuerzer aus, ohne dass jemand
  // erfaehrt warum.
  //
  // **`technische` geht als `'true'`/weggelassen auf die Leitung**, nicht als
  // `boolean`: `query()` kennt nur `string | number | undefined`.
  const result = await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/transactions${query({
      ...filter,
      technische: filter.technische === true ? 'true' : undefined,
    })}`,
    bankTransactionListSchema,
  );
  return {
    transactions: result.transactions,
    total: result.total,
    accountTotal: result.accountTotal,
    technicalHidden: result.technicalHidden,
  };
}

/**
 * Liest eine Datei ein — **ZIP oder einzelne XML** (Spec 4.0).
 *
 * **Der Rumpf IST die Datei, der Name steht in der Abfragezeile.** Kein Multipart:
 * Ein Umschlag brächte einen Feldnamen, auf den sich Oberfläche und Endpunkt
 * ausserhalb des Vertrags einigen müssten, und `statementImportRequestSchema` führt
 * `filename` bereits als geprüftes Feld (Aufgabe 33).
 *
 * **`application/octet-stream` und nicht `file.type`.** Die Art des Behälters erkennt
 * der Server an den magischen Bytes (Spec 7.1, Stufe A). `file.type` ist eine
 * Vermutung des Browsers aus der Dateiendung — für ein ZIP je nach Laune
 * `application/zip`, `application/x-zip-compressed` oder leer. Eine Vermutung
 * mitzuschicken, die niemand auswertet, ist bestenfalls wirkungslos und
 * schlimmstenfalls die Zahl, an der sich später jemand orientiert.
 *
 * **Ein `File` ist ein gültiger `BodyInit`.** `fetch` schickt seinen Inhalt als Strom;
 * es wird nichts im Browser gepuffert und nichts umkodiert. Der Name gilt **hier** und
 * nicht überall: `apps/web/tsconfig.json` führt `"lib": ["ES2023", "DOM", …]`, also ist
 * `BodyInit` in `apps/web/src/` ein globaler Typ. In `apps/web/e2e/` ist er es nicht
 * (Handbuch 3.74) — dort steht `string | Uint8Array`.
 */
export async function importStatements(
  accountId: string,
  file: File,
): Promise<StatementImportBatchResult> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/statements` +
      `?filename=${encodeURIComponent(file.name)}`,
    statementImportBatchResultSchema,
    {
      method: 'POST',
      body: file,
      // Gewinnt gegen das `application/json`, das `request` sonst setzt: `init.headers`
      // steht dort zuletzt.
      headers: { 'content-type': 'application/octet-stream' },
    },
  );
}

/** Die eingelesenen Auszüge eines Kontos, jüngste zuerst. */
export async function listBankStatements(accountId: string): Promise<readonly BankStatement[]> {
  const result = await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/statements`,
    bankStatementListSchema,
  );
  return result.statements;
}

/**
 * Die archivierte Originaldatei als Blob.
 *
 * **Kein `request<T>()`:** Die Hilfe prüft die Antwort gegen ein Zod-Schema und
 * setzt JSON voraus; hier kommen Bytes. Derselbe Aufbau wie `getDocumentPdf` —
 * `credentials: 'same-origin'`, damit das Sitzungscookie mitgeht, und **bei einem
 * Fehlerstatus wird der Rumpf als JSON gelesen**: Die API antwortet auf einen
 * Fehler mit JSON, auch wenn der Erfolgsfall Bytes liefert. Wer das übersieht,
 * zeigt dem Anwender `[object Blob]`.
 *
 * Die Datei ist der empfangene Beleg im Sinne der GoBD und geht unverändert
 * heraus (Spec 8, 9.1) — hier wird nichts umkodiert und nichts umbenannt.
 */
export async function getStatementFile(accountId: string, importId: string): Promise<Blob> {
  const response = await fetch(
    `${BASE}/bank-accounts/${encodeURIComponent(accountId)}/statements/${encodeURIComponent(importId)}/file`,
    { credentials: 'same-origin', headers: { accept: 'application/xml' } },
  );
  if (!response.ok) {
    let body: unknown = undefined;
    try {
      body = JSON.parse(await response.text());
    } catch {
      body = undefined;
    }
    throw toApiError(response.status, body);
  }
  return await response.blob();
}

/** Der Lückenbericht eines Kontos (Spec 9.1). */
export async function getCoverage(accountId: string): Promise<CoverageReport> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/coverage`,
    coverageReportSchema,
  );
}

// --- Zahldienste Stufe A ---------------------------------------------------

/**
 * Löst einen Abruf aus (Spec Zahldienste 9).
 *
 * **Der Bericht kommt auch dann, wenn der Abruf scheitert** — solange der
 * Server ihn überhaupt schreiben konnte. Der Abrufdatensatz **ist** das
 * Ergebnis (`E-218`); ein Fenster mit `fetchStatus: 'gescheitert'` ist eine
 * Auskunft und kein verlorener Aufruf.
 *
 * **Der Zeitraum ist optional, und sein Fehlen ist eine Aussage** (Befund
 * `Z-mollieohnezeitraum`, 2026-08-21). Bei PayPal trägt der Auftrag ein
 * Fenster; bei Mollie wird nach keinem Zeitraum gefragt, und der Auftrag ist
 * **leer** — geholt wird die ganze Historie. Welcher Fall gilt, entscheidet der
 * **Dienst des Kontos** und nicht diese Funktion: `fragtNachZeitraum()` aus dem
 * Vertrag, angewandt in der Maske.
 *
 * **Die Antwort ist eine Liste, für beide Dienste.** Bei PayPal enthält sie
 * genau ein Fenster; bei Mollie so viele, wie in diesem Lauf geholt worden
 * sind, weil Mollies Saldobericht gemessen höchstens 180 Tage weit reicht.
 *
 * **Die Länge wird hier nicht geprüft.** Sie hängt am Dienst und steht in
 * `FENSTER_MAX_TAGE_JE_DIENST`; der Endpunkt weist ein zu langes Fenster mit
 * `PAY_WINDOW_TOO_LONG` ab. Eine zweite Prüfung im Browser wäre eine zweite
 * Auslegung derselben Zahl. Was die Maske tut, ist die Ablehnung im Klartext zu
 * zeigen.
 */
export async function triggerFetch(
  accountId: string,
  fenster?: { readonly from: string; readonly to: string },
): Promise<{
  readonly fetches: readonly FetchReport[];
  readonly skipped: readonly UebersprungenesFenster[];
}> {
  const result = await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fetches`,
    fetchListSchema,
    { method: 'POST', body: JSON.stringify(fenster ?? {}) },
  );
  // **Beide Listen gehen hinaus.** Wer nur `fetches` zurueckgaebe, baute den
  // Befund `W-gefilterte-zahl` an dieser Stelle wieder ein: Ein Lauf, der neun
  // von zehn Abschnitten ueberspringt und den zehnten verwirft, saehe aus wie
  // ein vollstaendig gescheiterter Lauf.
  return { fetches: result.fetches, skipped: result.skipped };
}

/**
 * `POST /bank-accounts/:id/fetches/reihe` — fordert bei PayPal eine Abrufreihe
 * vom Tag `from` bis heute an. Die Fenster holt der Hintergrunddienst; die
 * Antwort ist die angelegte Reihe mit ihrer geplanten Fensterzahl.
 */
export async function starteAbrufreihe(accountId: string, from: string): Promise<Abrufreihe> {
  return await request(`/bank-accounts/${encodeURIComponent(accountId)}/fetches/reihe`, abrufreiheSchema, {
    method: 'POST',
    body: JSON.stringify({ from }),
  });
}

/** `GET /bank-accounts/:id/fetches/reihe/:reiheId` — der Stand einer Reihe (die Seite fragt alle fünf Sekunden). */
export async function getAbrufreihe(accountId: string, reiheId: string): Promise<Abrufreihe> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fetches/reihe/${encodeURIComponent(reiheId)}`,
    abrufreiheSchema,
  );
}

/** `GET /bank-accounts/:id/fetches/reihe` — die jüngste Reihe des Kontos, oder keine. */
export async function getJuengsteAbrufreihe(accountId: string): Promise<Abrufreihe | null> {
  const stand = await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fetches/reihe`,
    abrufreiheStandSchema,
  );
  return stand.reihe;
}

/** `POST /bank-accounts/:id/fetches/reihe/:reiheId/abbrechen` — bricht eine Reihe ab; Geholtes bleibt. */
export async function brecheAbrufreiheAb(accountId: string, reiheId: string): Promise<Abrufreihe> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fetches/reihe/${encodeURIComponent(reiheId)}/abbrechen`,
    abrufreiheSchema,
    { method: 'POST' },
  );
}

/**
 * Die Abruffenster eines Kontos, jüngstes zuerst.
 *
 * **Der Zugangsstand braucht keinen zweiten Pfad.** Spec 13.2 nimmt „letzter
 * erfolgreicher Abruf" und „letztes Ergebnis" aus genau dieser Liste; nur die
 * Rechteprobe hat einen eigenen Endpunkt.
 */
export async function listFetches(accountId: string): Promise<readonly FetchReport[]> {
  const result = await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fetches`,
    fetchWindowListSchema,
  );
  return result.fetches;
}

/**
 * Fährt die Rechteprobe — **auf Verlangen und nicht beim Aufbau der Seite**.
 *
 * Acht Aufrufe je Probe, bei jedem Seitenaufruf und jedem Zurückgehen
 * gefahren, sind ein Selbstangriff auf die Drosselung des Anbieters — und die
 * Drosselung träfe dann den Abruf, der die Vollständigkeit trägt. Deshalb ein
 * Knopf und kein `useEffect`.
 *
 * **`POST` und nicht `GET`, obwohl nichts geschrieben wird.** Ein `GET` lädt
 * zum Zwischenspeichern, zum Vorabholen und zum Wiederholen durch die
 * Zurück-Taste ein; jede dieser Wiederholungen fährt gegen dieselbe Drosselung.
 */
export async function runZugangsprobe(accountId: string): Promise<Zugangsprobe> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/zugangsprobe`,
    zugangsprobeSchema,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

// --- FinTS Stufe 2a ---------------------------------------------------------

/**
 * Der Zustand des FinTS-Zugangs (`GET /bank-accounts/:id/fints`, Spec 10).
 *
 * **Ein nicht eingerichteter Zugang ist kein Fehler** — die Antwort ist
 * `{ eingerichtet: false }` und kommt mit `200`. Eine 404 dafür bräuchte die
 * Maske, um zwei Fehlerarten auseinanderzuhalten, wo keine ist.
 */
export async function getFintsZugang(accountId: string): Promise<FintsZugang> {
  return await request(`/bank-accounts/${encodeURIComponent(accountId)}/fints`, fintsZugangSchema);
}

/**
 * Legt den FinTS-Zugang an oder ändert ihn (`PUT /bank-accounts/:id/fints`).
 *
 * **`PUT` und nicht `POST`** — derselbe Grund wie beim Vertrag: Es gibt
 * höchstens einen Zugang je Konto, sein Ort steht fest, und zweimal derselbe
 * Aufruf hinterlässt denselben Zustand. Die Eingabe ist `FintsZugangEingabeRaw`
 * aus `@fameacc/contracts` und wird von der aufrufenden Maske gegen
 * `fintsZugangEingabeSchema` geprüft, bevor sie hier ankommt — dieselbe Bauform
 * wie `createBankAccount` mit `bankAccountCreateSchema`.
 */
export async function putFintsZugang(
  accountId: string,
  eingabe: FintsZugangEingabeRaw,
): Promise<FintsZugang> {
  return await request(`/bank-accounts/${encodeURIComponent(accountId)}/fints`, fintsZugangSchema, {
    method: 'PUT',
    body: JSON.stringify(eingabe),
  });
}

/**
 * Beginnt einen Abruf (`POST /bank-accounts/:id/fints/abruf`, Spec 10).
 *
 * **Die PIN geht im Rumpf und nicht in der Adresse** — ein Wegstück landet in
 * jedem Zugriffsprotokoll und in jeder Verlaufsliste (`E-313`). Die Antwort
 * ist der **Laufzustand**, sofort, und trägt noch keinen Bericht: Der Lauf hat
 * gerade erst begonnen. Die aufrufende Maske fragt seinen Fortgang über
 * {@link getFintsAbrufZustand} ab.
 */
export async function startFintsAbruf(accountId: string, pin: string): Promise<FintsLauf> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fints/abruf`,
    fintsLaufSchema,
    { method: 'POST', body: JSON.stringify({ pin }) },
  );
}

/**
 * Der Zustand eines Laufs (`GET …/fints/abruf/:laufId`, Spec 4.3).
 *
 * **Die Oberfläche fragt hier und nicht die Bank.** Ein zweiter Weg zum
 * Institut wäre ein zweiter Dialog, und der kostete eine zweite Freigabe.
 */
export async function getFintsAbrufZustand(accountId: string, laufId: string): Promise<FintsLauf> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fints/abruf/${encodeURIComponent(laufId)}`,
    fintsLaufSchema,
  );
}

/**
 * Reicht eine TAN nach — oder bestätigt von Hand die Freigabe in der App
 * (`POST …/fints/abruf/:laufId/tan`, Spec 4.2).
 *
 * **`tan` fehlt beim entkoppelten Verfahren, und das ist kein Versehen.** Ohne
 * sie heisst der Aufruf „ich habe in der App freigegeben, frag jetzt nach" —
 * derselbe Endpunkt wie das Nachreichen eines eingetippten Codes, weil er in
 * der Bibliothek derselbe Aufruf mit einem Argument mehr ist.
 */
export async function postFintsTan(
  accountId: string,
  laufId: string,
  tan?: string,
): Promise<FintsLauf> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fints/abruf/${encodeURIComponent(laufId)}/tan`,
    fintsLaufSchema,
    { method: 'POST', body: JSON.stringify(tan === undefined ? {} : { tan }) },
  );
}

// --- Lastschrift: die zweite Tür (HKDME mit TAN je Datei, Auftrag `LS-fints`) ---

/**
 * Beginnt die Einreichung einer Lastschriftdatei über FinTS
 * (`POST /lastschrift/dateien/:id/einreichen`, Spec 3.2, 6).
 *
 * **Dieselbe Bauform wie `startFintsAbruf`**: Die PIN geht im Rumpf, die
 * Antwort ist der Laufzustand, sofort — der Dialog mit der Bank läuft im
 * Hintergrund weiter. Die aufrufende Maske fragt über
 * {@link getLastschriftEinreichungZustand} nach.
 */
export async function startLastschriftEinreichung(
  dateiId: string,
  bankAccountId: string,
  pin: string,
): Promise<LastschriftEinreichungLauf> {
  return await request(
    `/lastschrift/dateien/${encodeURIComponent(dateiId)}/einreichen`,
    lastschriftEinreichungLaufSchema,
    { method: 'POST', body: JSON.stringify({ bankAccountId, pin }) },
  );
}

/**
 * Der Zustand eines Einreichungslaufs
 * (`GET /lastschrift/dateien/:id/einreichung/:laufId`).
 */
export async function getLastschriftEinreichungZustand(
  dateiId: string,
  laufId: string,
): Promise<LastschriftEinreichungLauf> {
  return await request(
    `/lastschrift/dateien/${encodeURIComponent(dateiId)}/einreichung/${encodeURIComponent(laufId)}`,
    lastschriftEinreichungLaufSchema,
  );
}

/**
 * Reicht eine TAN nach — oder bestätigt von Hand die Freigabe in der App
 * (`POST …/einreichung/:laufId/tan`). Dieselbe Bauform wie `postFintsTan`.
 */
export async function postLastschriftEinreichungTan(
  dateiId: string,
  laufId: string,
  tan?: string,
): Promise<LastschriftEinreichungLauf> {
  return await request(
    `/lastschrift/dateien/${encodeURIComponent(dateiId)}/einreichung/${encodeURIComponent(laufId)}/tan`,
    lastschriftEinreichungLaufSchema,
    { method: 'POST', body: JSON.stringify(tan === undefined ? {} : { tan }) },
  );
}

/**
 * Sucht Institute über BLZ, BIC, Namen oder Ort (`GET /bank/fints/institute?suche=…`,
 * Chip `F-institute`). Komfort für die Zugangsmaske — die Handeingabe der
 * Adresse bleibt unabhängig davon voll möglich.
 */
export async function searchFintsInstitute(suche: string): Promise<readonly FintsInstitut[]> {
  const result = await request(`/bank/fints/institute${query({ suche })}`, fintsInstitutListeSchema);
  return result.institute;
}

/**
 * Beginnt einen Zugangstest (`POST /bank-accounts/:id/fints/test`, Chip
 * `F-zugangstest`) — ein Prüfdialog ohne Auftrag, der beweist, dass
 * Benutzerkennung, PIN und pushTAN zusammenpassen.
 *
 * **Dieselbe Bauform wie `startFintsAbruf`**: Die PIN geht im Rumpf, die
 * Antwort ist der Testzustand und trägt noch kein Ergebnis.
 */
export async function startFintsZugangstest(
  accountId: string,
  pin: string,
): Promise<FintsZugangstest> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fints/test`,
    fintsZugangstestSchema,
    { method: 'POST', body: JSON.stringify({ pin }) },
  );
}

/** Der Zustand eines Zugangstests (`GET …/fints/test/:testId`). */
export async function getFintsZugangstestZustand(
  accountId: string,
  testId: string,
): Promise<FintsZugangstest> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fints/test/${encodeURIComponent(testId)}`,
    fintsZugangstestSchema,
  );
}

/**
 * Reicht eine TAN nach — oder bestätigt von Hand die Freigabe in der App
 * (`POST …/fints/test/:testId/tan`). Dieselbe Bauform wie `postFintsTan`.
 */
export async function postFintsZugangstestTan(
  accountId: string,
  testId: string,
  tan?: string,
): Promise<FintsZugangstest> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fints/test/${encodeURIComponent(testId)}/tan`,
    fintsZugangstestSchema,
    { method: 'POST', body: JSON.stringify(tan === undefined ? {} : { tan }) },
  );
}

/**
 * Beginnt die geführte Einrichtung (`POST /bank-accounts/:id/fints/einrichtung`,
 * Chip `F-tanautomatik`) — Bank, Kennung und PIN genügen; TAN-Verfahren und
 * -Medium ermittelt der Lauf aus den Antworten der Bank.
 *
 * **Die PIN geht im Rumpf**, wie beim Zugangstest, und kommt in keiner Antwort
 * zurück.
 */
export async function startFintsEinrichtung(
  accountId: string,
  eingabe: {
    readonly url: string;
    readonly blz: string;
    readonly benutzerkennung: string;
    readonly kundenkennung?: string | undefined;
    readonly pin: string;
    readonly pinMerken?: boolean | undefined;
  },
): Promise<FintsEinrichtung> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fints/einrichtung`,
    fintsEinrichtungSchema,
    { method: 'POST', body: JSON.stringify(eingabe) },
  );
}

/** Der Zustand einer Einrichtung (`GET …/fints/einrichtung/:laufId`). */
export async function getFintsEinrichtungZustand(
  accountId: string,
  laufId: string,
): Promise<FintsEinrichtung> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fints/einrichtung/${encodeURIComponent(laufId)}`,
    fintsEinrichtungSchema,
  );
}

/** Beantwortet eine Rückfrage der Einrichtung — genau ein Verfahren oder ein Gerät. */
export async function postFintsEinrichtungWahl(
  accountId: string,
  laufId: string,
  wahl: { readonly verfahrenCode: number } | { readonly medium: string },
): Promise<FintsEinrichtung> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fints/einrichtung/${encodeURIComponent(laufId)}/wahl`,
    fintsEinrichtungSchema,
    { method: 'POST', body: JSON.stringify(wahl) },
  );
}

/** Reicht die Freigabe der Bank nach (`POST …/fints/einrichtung/:laufId/tan`). */
export async function postFintsEinrichtungTan(
  accountId: string,
  laufId: string,
  tan?: string,
): Promise<FintsEinrichtung> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fints/einrichtung/${encodeURIComponent(laufId)}/tan`,
    fintsEinrichtungSchema,
    { method: 'POST', body: JSON.stringify(tan === undefined ? {} : { tan }) },
  );
}

/**
 * Holt die TAN-Medien vom Institut (`POST …/fints/tan-medien`, `V-fintstab`).
 *
 * **Fertig statt Kennung.** Anders als der Zugangstest gibt dieser Aufruf sein
 * Ergebnis unmittelbar zurück: Der Vorgang dahinter (`HKTAB`) ist
 * zustimmungsfrei, es gibt nichts, worauf zu warten wäre. Verlangt ein Institut
 * trotzdem eine Freigabe, steht das als `freigabeNoetig` in der Antwort.
 */
export async function postFintsTanMedien(
  accountId: string,
  pin: string,
): Promise<FintsTanMedienAbruf> {
  return await request(
    `/bank-accounts/${encodeURIComponent(accountId)}/fints/tan-medien`,
    fintsTanMedienAbrufSchema,
    { method: 'POST', body: JSON.stringify({ pin }) },
  );
}

// --- Eingangsrechnungen Stufe A -------------------------------------------

/**
 * Lädt ein Archivstück hoch — **eine Datei je Aufruf**.
 *
 * **Der Rumpf IST die Datei, die Angaben stehen in der Abfragezeile.** Kein
 * Multipart: dieselbe Bauform und dieselbe Begründung wie bei
 * `importStatements`.
 *
 * **Der Knopf nimmt trotzdem mehrere Dateien und fragt EINMAL nach dem
 * Empfangsweg.** Ein Stapel aus dem Postfach hat denselben Weg; die Seite ruft
 * diese Funktion je Datei auf und bekommt je Datei eine eigene Antwort —
 * „gespeichert" oder „Mehrstück". In einem Sammelbericht ginge genau die
 * Unterscheidung unter, wegen der es `E-244` gibt.
 *
 * **Der Medientyp wird ausdrücklich mitgeschickt und nicht dem Transport
 * überlassen.** `[G]` GoBD Rz. 131 verlangt die Aufbewahrung im **empfangenen**
 * Format. `file.type` ist die Vermutung des Browsers aus der Endung — die beste
 * Angabe, die an dieser Stelle zu haben ist; fehlt sie, geht
 * `application/octet-stream` hinaus statt einer geratenen.
 */
export async function uploadIncomingDocument(
  file: File,
  angaben: {
    readonly origin: 'email' | 'portal' | 'paper';
    readonly kind?: string;
    readonly creditorNo?: string;
    readonly vendorId?: string;
    readonly invoiceDate?: string;
    /**
     * Der Medientyp, wenn der Aufrufer ihn **besser weiss als der Browser**.
     *
     * Der einzige Fall bisher ist die begleitende E-Mail: Für `.eml` und
     * `.msg` ist die Typtabelle des Betriebssystems je nach Rechner
     * unterschiedlich gefüllt, und `file.type` kommt dann leer an
     * (`mailanhang.ts`). Ohne diese Angabe ginge
     * `application/octet-stream` hinaus, und die Mail läge als `.bin` im
     * Archiv — also gerade nicht im empfangenen Format (GoBD Rz. 131).
     *
     * **Kein Freibrief.** Für Rechnungsdateien bleibt es bei der Angabe des
     * Browsers; eine geratene ist keine bessere.
     */
    readonly mediaType?: string;
  },
): Promise<IncomingUploadResult> {
  const parameter = new URLSearchParams({
    origin: angaben.origin,
    mediaType:
      angaben.mediaType ?? (file.type === '' ? 'application/octet-stream' : file.type),
    filename: file.name,
  });
  if (angaben.kind !== undefined) parameter.set('kind', angaben.kind);
  if (angaben.creditorNo !== undefined) parameter.set('creditorNo', angaben.creditorNo);
  if (angaben.vendorId !== undefined) parameter.set('vendorId', angaben.vendorId);
  if (angaben.invoiceDate !== undefined) parameter.set('invoiceDate', angaben.invoiceDate);

  return await request(`/incoming-documents?${parameter.toString()}`, incomingUploadResultSchema, {
    method: 'POST',
    body: file,
    // Gewinnt gegen das `application/json`, das `request` sonst setzt.
    headers: { 'content-type': 'application/octet-stream' },
  });
}

/**
 * Die Archivstücke — mit `nurOhneBeleg` der Stapel, der noch zu erfassen ist.
 *
 * **`seit` grenzt zusätzlich auf `receivedAt >= seit` ein** (Auftrag
 * `B-dasheingangneu`): `total` zählt dann die Schnittmenge, `gesamtOhneFilter`
 * bleibt der volle Archivbestand. Genau das ist die Zahl der
 * Eingangskorb-Neuzugänge in der Tageskontext-Zeile der Übersicht — ohne ein
 * eigenes Zählfeld auf der API-Seite.
 */
export async function listIncomingDocuments(
  nurOhneBeleg: boolean,
  seit?: string,
  verworfen?: 'ohne' | 'nur' | 'alle',
): Promise<{ documents: readonly IncomingKorbzeile[]; total: number; gesamtOhneFilter: number }> {
  return await request(
    `/incoming-documents${query({
      ...(nurOhneBeleg ? { unassigned: 'true' } : {}),
      seit,
      verworfen,
    })}`,
    incomingDocumentListSchema,
  );
}

/**
 * Ein noch nicht erfasstes Archivstück **verwerfen** — Owner-Wunsch vom
 * 2026-09-02, Auftrag `F-korbspalten`.
 *
 * **Es gibt keinen Löschweg, und diese Funktion ist keiner** (`E-262`). Wer
 * hier sucht, weil er löschen will, findet die Antwort: Das Stück verschwindet
 * aus dem Korb; die Datei, ihr SHA-256 und ihre Zeile bleiben unangetastet, und
 * unter dem Reiter „Verworfen" ist es wiederzufinden. Zurücknehmen lässt es
 * sich nicht — wer sich geirrt hat, lädt die Datei erneut hoch und bekommt über
 * den Hash dasselbe Archivstück zurück (`E-244`).
 */
export async function discardIncomingDocument(
  id: string,
  reason: IncomingDocumentDiscardReason,
  note?: string,
): Promise<void> {
  await request(`/incoming-documents/${encodeURIComponent(id)}/discard`, ohneInhaltSchema, {
    method: 'POST',
    body: JSON.stringify(note === undefined || note === '' ? { reason } : { reason, note }),
  });
}

/** Ein Archivstück samt Sondenergebnis. */
export async function getIncomingDocument(id: string): Promise<IncomingDocument> {
  return await request(
    `/incoming-documents/${encodeURIComponent(id)}`,
    incomingDocumentSchema,
  );
}

/**
 * Die Bytes eines Archivstücks — **unverändert**.
 *
 * Kein `request<T>()`: Die Hilfe setzt JSON voraus, hier kommen Bytes. Derselbe
 * Aufbau wie `getStatementFile` — und **bei einem Fehlerstatus wird der Rumpf
 * als JSON gelesen**, weil die API auf einen Fehler mit JSON antwortet, auch
 * wenn der Erfolgsfall Bytes liefert.
 */
export async function getIncomingDocumentFile(id: string): Promise<Blob> {
  const response = await fetch(`${BASE}/incoming-documents/${encodeURIComponent(id)}/file`, {
    credentials: 'same-origin',
  });
  if (!response.ok) {
    let body: unknown = undefined;
    try {
      body = JSON.parse(await response.text());
    } catch {
      body = undefined;
    }
    throw toApiError(response.status, body);
  }
  return await response.blob();
}

/**
 * Die Belegliste — Zustände kommagetrennt, alle Filter wirken zusammen.
 *
 * `q` — Aufgabe `[W-manuellbind]`: Freitext über Rechnungsnummer,
 * Kreditorname und Kreditornummer, für den Beleg-Wähler der Kontierungsmaske.
 *
 * `ansicht` — `W-eingangsarchivvorsystem` (`E-2083`): `arbeit` (Vorgabe) oder
 * `archiv-vorsystem`, die aus dem Vorsystem übernommenen Rechnungen, die nicht
 * in der Arbeitsliste stehen. `limit` hebt die Seitengrösse der Antwort an
 * (Vorgabe 200, Höchstgrenze 1000).
 *
 * `offset` — `W-archivfilterserver` (`E-2110`): wo die Seite beginnt. **Nur mit
 * `ansicht: 'archiv-vorsystem'`**; die Arbeitsliste weist ein `offset` über 0 mit
 * `400` ab. `q` sucht im ganzen Archiv, `total` zählt die Treffer.
 */
export async function listIncomingInvoices(filter: {
  readonly status?: string;
  readonly vendorId?: string;
  readonly from?: string;
  readonly to?: string;
  readonly q?: string;
  readonly ansicht?: 'arbeit' | 'archiv-vorsystem';
  readonly limit?: number;
  readonly offset?: number;
}): Promise<IncomingInvoiceList> {
  return await request(`/incoming-invoices${query({ ...filter })}`, incomingInvoiceListSchema);
}

/**
 * Ein erfasster Beleg **vollständig** — zum Wiederöffnen der Erfassungsmaske.
 *
 * Anders als `listIncomingInvoices` (die Zeile der Liste) trägt die Antwort
 * die Steuerzeilen, die Herkunftsspalte und den Verweis auf die
 * Archivstücke, die an diesem Beleg hängen.
 */
export async function getIncomingInvoice(id: string): Promise<IncomingInvoiceDetail> {
  return await request(
    `/incoming-invoices/${encodeURIComponent(id)}`,
    incomingInvoiceDetailSchema,
  );
}

/**
 * Der automatische Fund „Rechnung → Bewegung" — Aufgabe `[W-manuellbind]`,
 * die Umkehrung von Richtung 2 aus `abgleich.ts`. `treffer: null`, wenn
 * IBAN und normalisierte Rechnungsnummer keine eindeutige offene Bewegung
 * ergeben.
 */
export async function getBewegungsvorschlagFuerRechnung(
  invoiceId: string,
): Promise<RechnungBewegungAntwort> {
  return await request(
    `/incoming-invoices/${encodeURIComponent(invoiceId)}/bewegungsvorschlag`,
    rechnungBewegungAntwortSchema,
  );
}

/**
 * Die manuelle Suche über offene Bewegungen — Aufgabe `[W-manuellbind]`, der
 * Weg neben dem automatischen Fund, wenn er nichts liefert oder falsch liegt.
 */
export async function sucheOffeneBewegungen(filter: {
  readonly q?: string;
  readonly limit?: number;
}): Promise<BewegungssucheAntwort> {
  return await request(
    `/incoming-invoices/bewegungssuche${query({ ...filter })}`,
    bewegungssucheAntwortSchema,
  );
}

/**
 * Die Vorbelegung eines Kreditors zum Stichtag.
 *
 * **`asOf` ist das Rechnungsdatum und nicht «heute».** Ein Beleg vom Februar,
 * im März erfasst, muss das im Februar gültige Konto vorgeschlagen bekommen.
 */
/**
 * Die Vorbelegung — **auch ohne Kreditor** (Nachtrag vom 2026-08-23).
 *
 * `vendorId` ist freiwillig: Die Auslese des Belegs hängt am Archivstück und
 * nicht am Kreditor, und die Maske holt sie deshalb beim Öffnen. Ohne
 * Kreditor kommen die ausgelesenen Felder, die Erfahrungswerte bleiben leer,
 * und die Antwort trägt zusätzlich einen **Kreditorvorschlag** aus USt-IdNr.
 * oder IBAN des Belegs. `query()` lässt einen leeren wie einen fehlenden Wert
 * weg — ein Auswahlfeld, das nichts gewählt hat, schickt genau das.
 */
export async function getIncomingPrefill(
  vendorId?: string,
  asOf?: string,
  documentId?: string,
): Promise<IncomingPrefill> {
  return await request(
    `/incoming-invoices/prefill${query({ vendorId, asOf, documentId })}`,
    prefillSchema,
  );
}

/** Legt einen Beleg an und erfasst ihn — derselbe Dienst wie im Durchgang. */
export async function captureIncomingInvoice(
  eingabe: Record<string, unknown>,
): Promise<IncomingCaptureResult> {
  return await request('/incoming-invoices', incomingCaptureResultSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/**
 * Hängt eine **nachgereichte** Begleit-E-Mail an einen bereits erfassten Beleg.
 *
 * Derselbe Rumpf wie jede andere Nachbesserung (`PATCH :id`), und trotzdem der
 * einzige Schlüssel darin, der keine Spalte an `incoming_invoice` ist: Er
 * schreibt die Zuordnung im **Archiv**. Sie ist einmalig — es gibt keinen
 * Aufruf, der sie wieder löst.
 */
export async function attachIncomingMail(
  invoiceId: string,
  mailDocumentId: string,
): Promise<void> {
  await request(`/incoming-invoices/${encodeURIComponent(invoiceId)}`, ohneInhaltSchema, {
    method: 'PATCH',
    body: JSON.stringify({ mailDocumentId }),
  });
}

/**
 * Erfassungsfelder eines bereits erfassten Belegs ändern —
 * **derselbe Weg wie `attachIncomingMail`, allgemein gefasst** (`W-patchmaske`).
 *
 * `patch` trägt nur die Felder, die sich geändert haben; wer alle elf schickt,
 * behauptet elf Änderungen, wo vielleicht eine war (`E-258`s „ein leerer Rumpf
 * ist keine Änderung" gilt sinngemäss auch für einen zu vollen). Die
 * Aufrufstelle baut diesen Rumpf über `erstelleAenderungssatz()`
 * (`aenderung.ts`) — nicht hier, denn diese Funktion kennt die Maske nicht.
 */
export async function updateIncomingInvoice(
  invoiceId: string,
  patch: IncomingPatchRequest,
): Promise<void> {
  await request(`/incoming-invoices/${encodeURIComponent(invoiceId)}`, ohneInhaltSchema, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/** Die sachliche Freigabe. */
export async function approveIncomingInvoice(id: string): Promise<void> {
  await request(
    `/incoming-invoices/${encodeURIComponent(id)}/approve`,
    ohneInhaltSchema,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

/**
 * Verwerfen — **mit Grund, und das Archivstück bleibt**.
 *
 * Es gibt keinen Löschweg (`E-262`). Wer diese Funktion sucht, weil er löschen
 * will, findet hier die Antwort: Ein empfangener Beleg bleibt
 * aufbewahrungspflichtig, auch wenn er eine Dublette ist.
 */
export async function discardIncomingInvoice(
  id: string,
  reason: 'duplicate' | 'not_an_invoice' | 'received_in_error',
  note?: string,
): Promise<void> {
  await request(
    `/incoming-invoices/${encodeURIComponent(id)}/discard`,
    ohneInhaltSchema,
    {
      method: 'POST',
      body: JSON.stringify(note === undefined ? { reason } : { reason, note }),
    },
  );
}

/** Die offenen Dublettenkandidaten über den ganzen Bestand. */
export async function listIncomingDuplicates(): Promise<readonly IncomingDuplicate[]> {
  const ergebnis = await request('/incoming-invoices/duplicates', incomingDuplicateListSchema);
  return ergebnis.candidates;
}

/** Einen Kandidaten freigeben — **er kommt nicht wieder**, deshalb mit Grund. */
export async function releaseIncomingDuplicate(linkId: string, reason: string): Promise<void> {
  await request(
    `/incoming-invoices/duplicates/${encodeURIComponent(linkId)}/release`,
    ohneInhaltSchema,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}

/** Der Lückenbericht — die einzige Ansicht, die eine Auslassung sieht. */
export async function getIncomingGaps(asOf?: string): Promise<GapReport> {
  return await request(`/incoming-invoices/gaps${query({ asOf })}`, gapReportSchema);
}

/** Der Bericht über Nichtgebuchtes (`E-261`). */
export async function getIncomingUnbooked(): Promise<UnbookedReport> {
  return await request('/incoming-invoices/unbooked', unbookedReportSchema);
}

/**
 * `GET /api/v1/berichte/startkennzahlen` — die Kennzahlen-Karten der
 * Übersicht (Auftrag `V-startkennzahlen`): Umsatz Netto/Steuer/Brutto und
 * Offene Forderungen, je Jahr, laufendes Quartal und laufender Monat.
 */
export async function getStartkennzahlen(): Promise<StartkennzahlenBericht> {
  return await request('/berichte/startkennzahlen', startkennzahlenBerichtSchema);
}

/**
 * `GET /api/v1/berichte/zuordnungsquote` — die kontenübergreifende
 * Zuordnungsquote der Übersicht (`E-569`, Auftrag `F-dashquote`).
 */
export async function getZuordnungsquote(): Promise<ZuordnungsquoteBericht> {
  return await request('/berichte/zuordnungsquote', zuordnungsquoteBerichtSchema);
}

/**
 * `GET /api/v1/berichte/dashboard-kennzahlen` — die EINE Aggregation hinter
 * dem Dashboard-Kopf (Auftrag `B-dashagg`, verdrahtet in `F-dashlayout`):
 * Umsatz, Ausgaben, Einnahmen-Ausgaben-Saldo für den gewählten Zeitraum
 * samt Trend gegen die Vorperiode, die feste Monatsreihe der letzten acht
 * Kalendermonate und die Aufschlüsselung nach Aufwandskonto.
 *
 * **Das Schema kommt aus `@fameacc/contracts` und nicht aus `./schemas`.**
 * Die beiden Chart-Bausteine (`berichte/UmsatzAusgabenChart.tsx`,
 * `berichte/AusgabenNachKategorieChart.tsx`) nehmen `DashboardMonat` bzw.
 * `DashboardAusgabenNachKategorie` mit ECHT `Money`/`Factor`-markierten
 * Feldern entgegen; das lokal gespiegelte `betragSchema` dieser Anwendung
 * liefert unmarkierte Zeichenketten und passte dort nicht. Derselbe Weg, den
 * `suchergebnisSchema`/`vorschlagsantwortSchema` oben schon gehen — der
 * Vertrag ist für diese Antwort die einzige Auslegung, nicht eine zweite
 * daneben.
 */
export async function getDashboardKennzahlen(
  zeitraum: DashboardZeitraumTyp,
): Promise<DashboardKennzahlenBericht> {
  return await request(
    `/berichte/dashboard-kennzahlen${query({ zeitraum })}`,
    dashboardKennzahlenBerichtSchema,
  );
}

/**
 * `GET /api/v1/berichte/offene-posten` — die Offene-Posten-Liste je Kunde
 * (Plan Task 14 `[C-offeneposten]`).
 */
export async function getOffenePosten(): Promise<OffenePostenBericht> {
  return await request('/berichte/offene-posten', offenePostenBerichtSchema);
}

/**
 * `GET /api/v1/berichte/zahlstatus` — der Zahlstand der angezeigten Belege.
 *
 * **Es werden die Kennungen der GELADENEN Seite mitgegeben** und nicht der
 * ganze Bestand geholt: Die Belegliste blättert serverseitig, und eine Antwort
 * über tausende Rechnungen, von denen fünfzig angezeigt werden, wäre Arbeit für
 * niemanden. Eine leere Liste fragt gar nicht erst.
 */
export async function getZahlstatusListe(
  belege: readonly string[],
): Promise<readonly RechnungZahlstand[]> {
  if (belege.length === 0) return [];
  const antwort = await request(
    `/berichte/zahlstatus${query({ belege: belege.join(',') })}`,
    zahlstatusListeSchema,
  );
  return antwort.rechnungen;
}

/**
 * `GET /api/v1/berichte/zahlstatus/:documentId` — der Zahlstand einer Rechnung
 * samt Teilzahlungen. `null` für alles, was keine Ausgangsrechnung ist.
 */
export async function getZahlstatus(documentId: string): Promise<RechnungZahlstatus | null> {
  const antwort = await request(`/berichte/zahlstatus/${documentId}`, zahlstatusAntwortSchema);
  return antwort.zahlstatus;
}

/**
 * `GET /api/v1/berichte/verbindlichkeit-zahlstatus` — der Zahlstand der
 * angezeigten Eingangsrechnungen. Auftrag `V-kreditorposten`.
 *
 * Dieselbe Bauart und dieselbe Begründung wie `getZahlstatusListe()` auf der
 * Forderungsseite: Es werden die Kennungen der GELADENEN Liste mitgegeben, und
 * eine leere Liste fragt gar nicht erst.
 */
export async function getVerbindlichkeitZahlstatusListe(
  belege: readonly string[],
): Promise<readonly EingangsrechnungZahlstand[]> {
  if (belege.length === 0) return [];
  const antwort = await request(
    `/berichte/verbindlichkeit-zahlstatus${query({ belege: belege.join(',') })}`,
    verbindlichkeitZahlstatusListeSchema,
  );
  return antwort.rechnungen;
}

/**
 * `GET /api/v1/berichte/verbindlichkeit-zahlstatus/:incomingInvoiceId` — der
 * Zahlstand einer Eingangsrechnung samt Teilzahlungen. `null` für alles, was
 * keine erfasste Eingangsrechnung ist.
 */
export async function getVerbindlichkeitZahlstatus(
  incomingInvoiceId: string,
): Promise<EingangsrechnungZahlstatus | null> {
  const antwort = await request(
    `/berichte/verbindlichkeit-zahlstatus/${incomingInvoiceId}`,
    verbindlichkeitZahlstatusAntwortSchema,
  );
  return antwort.zahlstatus;
}

/**
 * Der Messbericht.
 *
 * **Ohne Zeitraum: der laufende Kalendermonat, gesetzt vom Server.** Nicht „die
 * letzten dreissig Tage": Die Leiter wird je Monat beurteilt, und ein
 * gleitendes Fenster lieferte bei jedem Aufruf eine andere Grundgesamtheit.
 */
export async function getIncomingFormatReport(
  from?: string,
  to?: string,
): Promise<FormatReport> {
  return await request(
    `/incoming-invoices/format-report${query({ from, to })}`,
    formatReportSchema,
  );
}

/**
 * Die gespeicherte Tabellenansicht dieses Benutzers für diese Liste.
 *
 * **`/me/…` und kein Benutzer im Pfad.** Wer gemeint ist, entscheidet die
 * Sitzung; es gibt keinen Parameter, den ein Klient setzen könnte, und damit
 * auch keinen, den er fälschen könnte (`E-334`).
 *
 * `undefined` heißt: nichts gespeichert - oder etwas gespeichert, das diese
 * Fassung nicht mehr liest. Beides führt zur Vorgabeansicht, und der
 * Unterschied führt nirgends zu einer anderen Handlung.
 */
export async function getTabellenansicht(
  tabelle: string,
): Promise<Tabellenansicht | undefined> {
  const antwort = await request(
    `/me/tabellenansicht/${encodeURIComponent(tabelle)}`,
    tabellenansichtAntwortSchema,
  );
  return antwort.ansicht ?? undefined;
}

/**
 * Speichert die Tabellenansicht dieses Benutzers für diese Liste.
 *
 * **`PUT` mit der vollständigen Ansicht.** Zurücksetzen ist derselbe Aufruf mit
 * `leereAnsicht()` und kein `DELETE`: `fameacc_app` hat auf `user_table_view`
 * kein DELETE-Recht, weil `E-162` die DELETE-Rechte gegen alle Tabellen des
 * Schemas prüft und seit Migration `0129` (2026-09-02) genau drei zulässt
 * (`document_line`, `document_tax_group`, `aborechnung_vorlage_position`) —
 * `user_table_view` ist keine davon.
 */
export async function putTabellenansicht(
  tabelle: string,
  ansicht: Tabellenansicht,
): Promise<Tabellenansicht> {
  const antwort = await request(
    `/me/tabellenansicht/${encodeURIComponent(tabelle)}`,
    tabellenansichtAntwortSchema,
    { method: 'PUT', body: JSON.stringify(ansicht) },
  );
  // Der Endpunkt antwortet mit dem, was angekommen ist. `?? ansicht` ist der
  // Zweig, den es nicht gibt - `PUT` gibt nie `null` zurück -, und er steht
  // hier, weil das gemeinsame Antwortschema `nullable` ist und ein `!` die
  // Prüfung nur wegblättern würde.
  return antwort.ansicht ?? ansicht;
}

/**
 * Die Power-Suche — `GET /api/v1/suche`.
 *
 * **`signal` ist Pflicht und keine Kür.** Die Suche läuft beim Tippen; jede
 * neue Anfrage bricht die vorherige ab, sonst arbeitet der Server an Antworten,
 * die niemand mehr sehen will. Der Abbruch ist dabei ein **Wunsch** und keine
 * Zusage — eine Antwort kann bereits unterwegs sein. Die Zusage ist die
 * **Folgenummer**: Der Aufrufer verwirft jede Antwort, deren `folge` kleiner
 * ist als die zuletzt angezeigte (`suche/suchzustand.ts`).
 *
 * **Der Vertrag kommt aus `@fameacc/contracts` und nicht aus einer zweiten
 * Fassung hier.** Ein Schema, das die Oberfläche von Hand nachschreibt, ist
 * genau die Drift, gegen die `W-schemadoppel` angetreten ist.
 */
export async function sucheAusfuehren(
  q: string,
  optionen: { readonly folge: number; readonly limit?: number; readonly art?: string },
  signal: AbortSignal,
): Promise<Suchergebnis> {
  const pfad = `/suche${query({
    q,
    folge: optionen.folge,
    limit: optionen.limit,
    art: optionen.art,
  })}`;
  return await request(pfad, suchergebnisSchema, { signal });
}

/**
 * Die Werteliste eines Schlüssels — `GET /api/v1/suche/vorschlaege`.
 *
 * **Dieselben zwei Vorkehrungen wie bei der Suche, und aus demselben Grund.**
 * `signal` bricht die vorige Anfrage ab, damit der Server nicht an Antworten
 * arbeitet, die niemand mehr sehen will; `folge` ist die Zusage, dass ein
 * spätes Ergebnis ein neueres nie überschreibt (`suche/vorschlag.ts`,
 * `vorschlagUeberholt`). Beim Tippen lösen sich diese Anfragen im Abstand von
 * Millisekunden ab — das ist die Lage, für die beides gebaut ist.
 *
 * **Die Fähigkeitsgrenze zieht der Server**, nicht diese Zeile: Ein Vorschlag
 * ist eine Auskunft über den Bestand, und ein Kreditor, den der Aufrufer nicht
 * sehen darf, kommt hier gar nicht erst an (`E-358`).
 */
export async function suchVorschlaege(
  schluessel: string,
  praefix: string,
  folge: number,
  signal: AbortSignal,
): Promise<Vorschlagsantwort> {
  const pfad = `/suche/vorschlaege${query({ schluessel, praefix, folge })}`;
  return await request(pfad, vorschlagsantwortSchema, { signal });
}

/**
 * Die gespeicherten Sachkonto-Präferenzen dieses Benutzers (Favoriten,
 * zuletzt verwendet) — derselbe Pfad wie `getTabellenansicht()`, unter der
 * festen Tabellenkennung `sachkonto-auswahl` (`SACHKONTO_PRAEFERENZEN_TABELLE`,
 * `@fameacc/contracts`). Chip `V-favoritenpersist`.
 *
 * `undefined` heisst dasselbe wie bei `getTabellenansicht()`: nichts
 * gespeichert, oder eine Fassung, die diese Anwendung nicht mehr liest. Beides
 * führt zu leeren Präferenzen, und der Unterschied führt nirgends zu einer
 * anderen Handlung.
 */
export async function getSachkontoPraeferenzen(): Promise<SachkontoPraeferenzen | undefined> {
  const antwort = await request(
    `/me/tabellenansicht/${encodeURIComponent(SACHKONTO_PRAEFERENZEN_TABELLE)}`,
    sachkontoPraeferenzenAntwortSchema,
  );
  return antwort.ansicht ?? undefined;
}

/**
 * Speichert die Sachkonto-Präferenzen dieses Benutzers.
 *
 * **`PUT` mit den vollständigen Präferenzen**, dieselbe Bauform wie
 * `putTabellenansicht()`: kein `DELETE`-Recht auf `user_table_view` (`E-162`,
 * seit Migration `0129` drei DELETE-fähige Tabellen, `user_table_view` keine
 * davon), «alle Favoriten entfernen» ist ein `PUT` mit leeren Listen.
 */
export async function putSachkontoPraeferenzen(
  praeferenzen: SachkontoPraeferenzen,
): Promise<SachkontoPraeferenzen> {
  const antwort = await request(
    `/me/tabellenansicht/${encodeURIComponent(SACHKONTO_PRAEFERENZEN_TABELLE)}`,
    sachkontoPraeferenzenAntwortSchema,
    { method: 'PUT', body: JSON.stringify(praeferenzen) },
  );
  return antwort.ansicht ?? praeferenzen;
}

/**
 * Die eigenen letzten Anmeldevorgänge — `GET /api/v1/me/anmeldungen`, Auftrag
 * `W-profilseite`.
 *
 * **`/me/…` und kein Benutzer im Pfad**, dieselbe Bauform wie
 * `getTabellenansicht()`: Wer gemeint ist, kommt aus der Sitzung, es gibt
 * keinen Parameter, den dieser Aufruf setzen könnte.
 */
export async function getMeAnmeldungen(limit = 20): Promise<readonly Anmeldung[]> {
  const antwort = await request(
    `/me/anmeldungen${query({ limit })}`,
    meAnmeldungenAntwortSchema,
  );
  return antwort.anmeldungen;
}

/**
 * Der Kontenkatalog — die Kategorieauswahl der Kontierungsmaske
 * (`V-kontmaske`). Eine Abfrage, kein Parameter, siehe Kopfkommentar von
 * `apps/api/src/kontierung/kontenkatalog.query.ts`.
 */
export async function getKontenkatalog(): Promise<KontenkatalogAntwort> {
  return await request('/kontenkatalog', kontenkatalogAntwortSchema);
}

/**
 * Der Kontierungsvorschlag zu einer Bewegung (Task 7 `C-kontvorschlag`,
 * Endpunkt-Nachtrag aus Task 9 `V-kontmaske`) — `vorschlag: null`, wenn keine
 * der drei Quellen etwas liefert.
 */
export async function getKontierungsvorschlag(
  bankTransactionId: string,
): Promise<KontierungsvorschlagAntwort> {
  return await request(
    `/kontierungen/vorschlag/${encodeURIComponent(bankTransactionId)}`,
    kontierungsvorschlagAntwortSchema,
  );
}

/**
 * Bewegung samt ihrer aktuell gültigen Kontierung — Nachtrag zu
 * `V-kontmaske`. Die Maske ruft dies zuerst auf: `kontierung !== null`
 * bedeutet, dass die Bewegung bereits kontiert ist (freigegeben oder nicht)
 * und die Maske in den Prüfen/Freigeben-Zustand wechselt, statt eine neue
 * Kontierung anzulegen. Trägt zugleich die Anzeigedaten der Bewegung — die
 * einzige Quelle dafür, wenn die Maske über `/bankkonten/:id/kontieren/:bewegungId`
 * direkt angesprungen wird, ohne aus der Unkontiert-Liste zu kommen.
 */
export async function getKontierungFuerBewegung(bankTransactionId: string): Promise<KontierungAntwort> {
  return await request(
    `/kontierungen/bewegung/${encodeURIComponent(bankTransactionId)}`,
    kontierungAntwortSchema,
  );
}

/** `GET /api/v1/bank-accounts/:id/unkontiert` — die Liste für die Klärung. */
export async function listUnkontiert(
  bankAccountId: string,
  optionen: { readonly from?: string; readonly to?: string; readonly limit?: number } = {},
): Promise<UnkontierteListe> {
  const pfad = `/bank-accounts/${encodeURIComponent(bankAccountId)}/unkontiert${query({
    from: optionen.from,
    to: optionen.to,
    limit: optionen.limit,
  })}`;
  return await request(pfad, unkontierteListeSchema);
}

/**
 * `GET /api/v1/berichte/teilzahlungen` — Aufgabe 10 `[V-klaerliste4]`, der
 * eigene Klärlisten-Abschnitt für erkannte, aber nicht verteilte
 * Teilzahlungen (Spec 4.3, `A-K7`). `bankAccountId` fehlt: alle Konten.
 */
export async function listTeilzahlungen(
  bankAccountId?: string,
): Promise<readonly TeilzahlungBewegung[]> {
  const result = await request(
    `/berichte/teilzahlungen${query({ konto: bankAccountId })}`,
    teilzahlungListeSchema,
  );
  return result.bewegungen;
}

/**
 * `GET /api/v1/kontierungen/wartend` — Auftrag `W-freigabeliste`
 * (2026-08-27): der Abschnitt „Wartet auf Freigabe" der Klärliste.
 * `bankAccountId` fehlt: alle Konten — derselbe optionale Filter wie
 * `listTeilzahlungen()`, damit die Kontoauswahl der Klärliste alle
 * Abschnitte gleich behandelt.
 */
export async function listKontierungenWartend(
  bankAccountId?: string,
): Promise<readonly KontierungWartendZeile[]> {
  const result = await request(
    `/kontierungen/wartend${query({ konto: bankAccountId })}`,
    kontierungWartendListeSchema,
  );
  return result.bewegungen;
}

/**
 * `GET /api/v1/kontierungen/automatisch` — Auftrag `R-aufladungsauto`
 * (2026-08-28): der Abschnitt „Automatisch gebucht" der Klärliste.
 *
 * **Liefert die volle Antwort und nicht nur `bewegungen`**, anders als
 * `listKontierungenWartend()` daneben: `gesamt` ist die Zahl über den ganzen
 * gefilterten Bestand, `bewegungen` das gekappte Fenster darauf. Wer nur die
 * Liste zurückgäbe, verschwiege bei mehr als 200 Zeilen genau die Zahl, die
 * eine Nachschau interessant macht.
 *
 * **`regel` schränkt auf EINE Regel des Registers ein** (`R-regelregister`,
 * 2026-08-29). Fehlt sie, zeigt die Liste, was die laufenden Spuren aller
 * eingetragenen Regeln gebucht haben — heute dasselbe, weil es eine Regel gibt.
 */
export async function listKontierungenAutomatisch(
  bankAccountId?: string,
  regel?: string,
): Promise<KontierungAutomatischListe> {
  return await request(
    `/kontierungen/automatisch${query({ konto: bankAccountId, regel })}`,
    kontierungAutomatischListeSchema,
  );
}

/**
 * Eine Position beim Anlegen oder Korrigieren einer Kontierung — dieselbe
 * Form wie `kontierungPositionInputSchema` (`@fameacc/contracts`), hier ohne
 * Zod-Schema: Der Rumpf wird nicht client-seitig geprüft, dieselbe Bauart wie
 * bei `captureIncomingInvoice()`.
 */
export interface KontierungPositionEingabe {
  readonly accountNumber: string;
  readonly taxKey?: string | null;
  readonly amount: string;
  readonly begruendung?: string | null;
}

/** Die Angaben, die Anlage UND Korrektur einer Kontierung gemeinsam tragen. */
export interface KontierungKopfEingabe {
  readonly customerId?: string | null;
  readonly vendorId?: string | null;
  readonly matchedDocumentId?: string | null;
  readonly matchedIncomingInvoiceId?: string | null;
  readonly vorschlagsquelle?: string | null;
  readonly begruendung?: string | null;
  readonly positionen: readonly KontierungPositionEingabe[];
}

/** `POST /api/v1/kontierungen` — die Erstanlage. */
export async function legeKontierungAn(
  bankTransactionId: string,
  herkunft: KontierungHerkunft,
  eingabe: KontierungKopfEingabe,
): Promise<Kontierung> {
  return await request('/kontierungen', kontierungSchema, {
    method: 'POST',
    body: JSON.stringify({ bankTransactionId, herkunft, ...eingabe }),
  });
}

/**
 * `POST /api/v1/kontierungen/:id/korrektur` — die Nachfolgerin. `begruendung`
 * ist hier Pflicht (`D-K2`, Kopfkommentar `kontierung.ts` im Vertragspaket).
 */
export async function korrigiereKontierung(
  id: string,
  eingabe: KontierungKopfEingabe & { readonly begruendung: string },
): Promise<Kontierung> {
  return await request(`/kontierungen/${encodeURIComponent(id)}/korrektur`, kontierungSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/** `POST /api/v1/kontierungen/:id/freigabe` — die sachliche Freigabe (`A-K1`). */
export async function gebKontierungFrei(id: string): Promise<Kontierung> {
  return await request(`/kontierungen/${encodeURIComponent(id)}/freigabe`, kontierungSchema, {
    method: 'POST',
  });
}

/**
 * `POST /api/v1/kontierungen/:id/aufheben` — Stufe A, Auftrag
 * `W-kontkorrektur`: Lösen ohne Ersatz. Die Nachfolgerin trägt
 * `herkunft = 'aufgehoben'` und keine Position; sie wartet wie eine Korrektur
 * auf die Freigabe (`gebKontierungFrei`).
 */
export async function loeseKontierungAuf(id: string, begruendung: string): Promise<Kontierung> {
  return await request(`/kontierungen/${encodeURIComponent(id)}/aufheben`, kontierungSchema, {
    method: 'POST',
    body: JSON.stringify({ begruendung }),
  });
}

/**
 * `POST /api/v1/kontierungen/:id/storno` — Stufe B, Auftrag
 * `W-kontkorrektur`: eine bereits gebuchte Kontierung wird über eine
 * Stornobuchung im Journal zurückgenommen. Anders als bei der Freigabe ist
 * das Ergebnis bereits final — es gibt keinen weiteren Freigabeschritt.
 */
export async function storniereKontierung(id: string, begruendung: string): Promise<Kontierung> {
  return await request(`/kontierungen/${encodeURIComponent(id)}/storno`, kontierungSchema, {
    method: 'POST',
    body: JSON.stringify({ begruendung }),
  });
}

/**
 * Der Betriebszustand — für die Seite «Systemstatus».
 *
 * **Ein Abruf und nicht sechs.** Die Seite zeigt Dienste, Hintergrundläufe,
 * Anmeldefehlversuche und die Fehlerquote der Schnittstelle; sie alle einzeln
 * zu holen hiesse, sechs Antwortzeiten und sechs Fehlermöglichkeiten auf eine
 * Seite zu legen, deren einziger Zweck es ist, EINEN Gesamteindruck zu geben.
 * Die Teilmessungen fangen ihre Fehlschläge im Dienst und melden dafür
 * `unbekannt`.
 */
export async function getSystemStatus(): Promise<SystemStatus> {
  return await request('/system/status', systemStatusSchema);
}

/**
 * Der Anfrage-Debugger, NUR LESEND — Auftrag `W-debugger-ui` (2026-09-01).
 *
 * **Ein Abruf und nicht zwei**, obwohl die Antwort zwei Dinge trägt: die
 * gefilterte Seite und die Tokenliste, aus der der Filter wählt. Ein eigener
 * Endpunkt für die Auswahl hätte eine zweite Fähigkeit gebraucht
 * (`apitoken:manage`) — die Debugger-Seite kommt bewusst mit `system:read`
 * aus, derselben Fähigkeit wie Systemstatus und Fehlerablage.
 */
export async function getSystemAnfragen(filter: {
  readonly status?: 'erfolg' | 'fehlschlag' | undefined;
  readonly tokenId?: string | undefined;
  readonly pfad?: string | undefined;
  readonly limit?: number | undefined;
  readonly offset?: number | undefined;
}): Promise<SystemAnfragenListe> {
  return await request(
    `/system/anfragen${query({
      status: filter.status,
      tokenId: filter.tokenId,
      pfad: filter.pfad,
      limit: filter.limit,
      offset: filter.offset,
    })}`,
    systemAnfragenListeSchema,
  );
}

/**
 * Eine einzelne Anfrage samt ihrem **gespeicherten** Rumpf und, bei `5xx`,
 * dem Systemfehler derselben Vorgangskennung.
 *
 * **Erst beim Aufklappen geholt.** Ein Rumpf und ein Stacktrace je Zeile
 * machten aus einer Protokollseite eine Datenlast, die niemand liest; und für
 * eine erfolgreiche Anfrage gibt es beides ohnehin nicht.
 */
export async function getSystemAnfrage(id: string): Promise<SystemAnfrageDetail> {
  return await request(
    `/system/anfragen/${encodeURIComponent(id)}`,
    systemAnfrageDetailSchema,
  );
}

/**
 * Das Regelregister, NUR LESEND — Auftrag `W-regeluebersicht` (2026-08-31).
 *
 * Jede Regel genau einmal, samt den drei ehrlich abfragbaren Zählungen
 * (`RegelnUebersichtQuery`, `apps/api/src/regeln/uebersicht.query.ts`). Kein
 * `POST` in dieser Datei dafür: Regeln sind Code (`REI-2 = B`) — sie
 * entstehen als geprüfter Auftrag und nicht über diese Seite.
 */
export async function getRegelUebersicht(): Promise<RegelUebersicht> {
  return await request('/regeln', regelUebersichtSchema);
}

/**
 * Die ZWEITE Regelablage (`regel`/`regel_version`, Migration `0112`), NUR
 * LESEND — Auftrag `R-drliste` (2026-09-01), `D-20`: zweiter Abschnitt
 * derselben Seite, kein eigener Seitenbaum.
 *
 * **Die schreibenden Gegenstücke stehen seit `R-drmaske` (2026-09-01)
 * darunter** — anlegen, fortschreiben, stilllegen, scharfschalten. Bis
 * dahin stand hier, es gebe sie in dieser Datei nicht; das war der Stand
 * von `R-drliste`, dessen Seite nur zeigte.
 */
export async function getDatenregelnListe(): Promise<DatenregelnListe> {
  return await request('/regeln/datenregeln', datenregelnListeSchema);
}

/**
 * Die Live-Vorschau eines Kriteriensatzes — NUR LESEND, obwohl `POST`
 * (`R-drmaske`, 2026-09-01).
 *
 * Der Rumpf ist das verschachtelte Kriterienobjekt; in eine Abfragezeile
 * gepresst wäre es entweder ein eigenes Kodierformat oder ein JSON-Klumpen
 * in einem Parameter — und Kriterienwerte tragen Verwendungszwecke und
 * Namen, also genau das, was nicht in eine URL gehört, weil URLs im
 * Zugriffsprotokoll stehen.
 */
export async function datenregelVorschau(
  kriterien: unknown,
  grenze?: number,
  /**
   * Das Konto der Trefferprobe (`DR-builderui`, Spec 17.4, `D-29`) — ein
   * VERGLEICHSWERT und keine Prüfung: Mit ihm trägt die Antwort die
   * Aufteilung nach Kontierungsstand und je Treffer seinen Stand.
   */
  konto?: string,
): Promise<DatenregelVorschauAntwort> {
  return await request('/datenregeln/vorschau', datenregelVorschauAntwortSchema, {
    method: 'POST',
    body: JSON.stringify({
      kriterien,
      ...(grenze === undefined ? {} : { grenze }),
      ...(konto === undefined ? {} : { konto }),
    }),
  });
}

/**
 * Der Vorlagenkatalog mit der Auskunft dieser Installation — die Galerie
 * „Aus Vorlage" (`DR-builderui`, Spec 17.3/17.4). Nur lesend.
 */
export async function getDatenregelVorlagen(): Promise<DatenregelVorlagenListe> {
  return await request('/datenregeln/vorlagen', datenregelVorlagenListeSchema);
}

/**
 * Der Buchungssatz, den eine Regel an einem Treffer ergäbe — NUR LESEND,
 * obwohl `POST` (Spec 17.4, Nummer 5, `D-31`). Dieselbe reine Funktion wie
 * der Buchungspfad; ein ungültiges Sachkonto ist ein Befund, kein Fehler.
 */
export async function datenregelBuchungsvorschau(rumpf: {
  readonly bankTransactionId: string;
  readonly ergebnis: { readonly konto: string; readonly steuerschluessel?: string };
}): Promise<DatenregelBuchungsvorschauAntwort> {
  return await request('/datenregeln/buchungsvorschau', datenregelBuchungsvorschauAntwortSchema, {
    method: 'POST',
    body: JSON.stringify(rumpf),
  });
}

/**
 * Exportiert Datenregeln als Regelsatz — NUR LESEND, obwohl `POST`
 * (`DR-regelsatzui`, Spec 17.5, `D-33`). Der Rumpf nennt Auswahl und Form;
 * die Antwort ist die Datei selbst. Sie wird hier nicht gespeichert und nicht
 * ins Zugriffsprotokoll geschrieben (die Strecke steht auf dessen Ausschlussliste).
 */
export async function exportiereDatenregeln(rumpf: DatenregelExportRoh): Promise<DatenregelSatz> {
  return await request('/datenregeln/export', datenregelSatzSchema, {
    method: 'POST',
    body: JSON.stringify(rumpf),
  });
}

/**
 * Der Trockenlauf eines Regelsatz-Imports — NUR LESEND, obwohl `POST`
 * (Spec 17.5, `D-34`, Schritt 1): je Regel der Datei ein Befund und die Zahl der
 * Treffer im Bestand. Er rechnet mit den Entscheidungen, die der Rumpf mitbringt
 * (`DR-probeentscheidungen`), entscheidet aber nichts und legt nichts an.
 */
export async function probiereDatenregelImport(
  rumpf: DatenregelImportProbeRoh,
): Promise<DatenregelImportProbeAntwort> {
  return await request('/datenregeln/import/probe', datenregelImportProbeAntwortSchema, {
    method: 'POST',
    body: JSON.stringify(rumpf),
  });
}

/**
 * Die Übernahme eines Regelsatzes (Spec 17.5, `D-34`, Schritt 2): dieselbe Datei
 * plus die Entscheidung je Regel. Alles oder nichts — eine Abweisung ist ein
 * `409 DATENREGEL_IMPORT_ABGEWIESEN` mit `befunde` in `ApiError.details`.
 */
export async function importiereDatenregelSatz(
  rumpf: DatenregelImportRoh,
): Promise<DatenregelImportAntwort> {
  return await request('/datenregeln/import', datenregelImportAntwortSchema, {
    method: 'POST',
    body: JSON.stringify(rumpf),
  });
}

/** Legt eine Datenregel an — die erste Fassung ist immer ein Entwurf. */
export async function createDatenregel(rumpf: unknown): Promise<DatenregelFassungAntwort> {
  return await request('/datenregeln', datenregelFassungAntwortSchema, {
    method: 'POST',
    body: JSON.stringify(rumpf),
  });
}

/**
 * Schreibt eine Datenregel fort — eine NEUE Fassung, die alte wird zum
 * Stichtag geschlossen. Der Name steht auf dem Kopf und gehört deshalb nicht
 * in den Rumpf.
 */
export async function fortschreibeDatenregel(
  regelId: string,
  rumpf: unknown,
): Promise<DatenregelFassungAntwort> {
  return await request(
    `/datenregeln/${encodeURIComponent(regelId)}/fassungen`,
    datenregelFassungAntwortSchema,
    { method: 'POST', body: JSON.stringify(rumpf) },
  );
}

/** Legt eine Datenregel still — eine Fassung mit `stillgelegt`, kein Löschweg. */
export async function stillegeDatenregel(
  regelId: string,
  gueltigAb: string,
): Promise<DatenregelFassungAntwort> {
  return await request(
    `/datenregeln/${encodeURIComponent(regelId)}/stilllegen`,
    datenregelFassungAntwortSchema,
    { method: 'POST', body: JSON.stringify({ gueltigAb }) },
  );
}

/** Schaltet eine Datenregel auf Vollautomatik — allein für Administratoren. */
export async function scharfschalteDatenregel(
  regelId: string,
  gueltigAb: string,
): Promise<DatenregelFassungAntwort> {
  return await request(
    `/datenregeln/${encodeURIComponent(regelId)}/scharfschalten`,
    datenregelFassungAntwortSchema,
    { method: 'POST', body: JSON.stringify({ gueltigAb }) },
  );
}

/**
 * Die Guthabenabstimmung, NUR LESEND — Auftrag `W-abstimmansicht`.
 *
 * **Kein `POST` in dieser Datei für die Abstimmung selbst, und das ist
 * Absicht.** Ein Satz entsteht ausschliesslich über
 * `pnpm guthaben:abstimmung -- --datei <pfad>` auf der Kommandozeile; diese
 * beiden Abrufe zeigen seine Protokolle.
 */
export async function listGuthabenAbstimmungen(
  limit?: number,
): Promise<readonly GuthabenAbstimmungListItem[]> {
  const result = await request(
    `/guthaben/abstimmungen${query({ limit })}`,
    guthabenAbstimmungListSchema,
  );
  return result.saetze;
}

/** Ein Satz vollständig, inklusive `gleichung1` und `befunde`. */
export async function getGuthabenAbstimmung(id: string): Promise<GuthabenAbstimmungDetail> {
  return await request(
    `/guthaben/abstimmungen/${encodeURIComponent(id)}`,
    guthabenAbstimmungDetailSchema,
  );
}

/**
 * `POST /api/v1/guthaben/einloesungen` — eine bereits ausgestellte Rechnung
 * (ganz oder teilweise) aus dem Guthaben des Kunden bezahlen, Auftrag
 * `W-belegbezahlt`. `EinloesungDialog` in `documents/EinloesungDialog.tsx`
 * ruft dies aus dem geöffneten Beleg heraus auf, mit `betrag` auf den noch
 * offenen Teil vorbelegt und kürzbar (Mischzahlungsfall).
 *
 * **`idempotencyKey` wird immer mitgeschickt.** Der Dialog erzeugt ihn einmal
 * beim Öffnen (`crypto.randomUUID()`) und behält ihn über einen Wiederholungs-
 * versuch nach einem Netzwerkfehler hinweg bei — eine Wiederholung mit
 * demselben Schlüssel liefert die erste Antwort zurück statt ein zweites Mal
 * zu buchen (`guthabenEinloesungRequestSchema`, `@fameacc/contracts`).
 */
export async function einloeseGuthaben(eingabe: {
  readonly documentId: string;
  readonly betrag: string;
  readonly idempotencyKey: string;
}): Promise<GuthabenEinloesung> {
  return await request('/guthaben/einloesungen', guthabenEinloesungSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/**
 * `POST /api/v1/guthaben/einloesungen/:journalEntryId/storno` — der Rückweg
 * für eine irrtümliche Guthaben-Einlösung, Auftrag `C-einloesungsstorno`
 * (2026-08-30). `journalEntryId` ist die Kennung der Einlösungsbuchung selbst
 * — dieselbe, die `Zahlungsblock` in `documents/DocumentPage.tsx` für jede
 * Zeile mit `zahlweg === 'guthaben'` anzeigt (`RechnungZahlung.journalEntryId`).
 */
export async function storniereGuthabenEinloesung(
  journalEntryId: string,
  begruendung: string,
): Promise<GuthabenEinloesungStorno> {
  return await request(
    `/guthaben/einloesungen/${encodeURIComponent(journalEntryId)}/storno`,
    guthabenEinloesungStornoSchema,
    { method: 'POST', body: JSON.stringify({ begruendung }) },
  );
}

/**
 * Die Guthaben-Erstattung — Anlage und Freigabe, Auftrag
 * `V-guthabenauszahlung` (Auftraggeberwunsch 2026-08-28: Kunde löscht sein
 * Konto, Restguthaben wird ausgezahlt).
 *
 * **Zwei Fähigkeiten, ein Vier-Augen-Paar wie bei der Kontierung.**
 * `legeGuthabenErstattungAn()` bucht NICHT — erst
 * `gebGuthabenErstattungFrei()` bucht `1796` an das Geldkonto des Zahlwegs.
 */
export interface GuthabenErstattungEingabe {
  readonly customerId: string;
  readonly betrag: string;
  readonly zahlweg: string;
  readonly referenz: string;
  readonly begruendung: string;
}

/** `POST /api/v1/guthaben/erstattungen` — die Anlage. Bucht nicht. */
export async function legeGuthabenErstattungAn(
  eingabe: GuthabenErstattungEingabe,
): Promise<GuthabenErstattung> {
  return await request('/guthaben/erstattungen', guthabenErstattungSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/**
 * `POST /api/v1/guthaben/erstattungen/:id/freigabe` — die Freigabe. Bucht
 * `1796` an das Geldkonto des Zahlwegs, ohne Steuerzeile.
 */
export async function gebGuthabenErstattungFrei(id: string): Promise<GuthabenErstattung> {
  return await request(
    `/guthaben/erstattungen/${encodeURIComponent(id)}/freigabe`,
    guthabenErstattungSchema,
    { method: 'POST' },
  );
}

/**
 * `GET /api/v1/guthaben/erstattungen/wartend` — der Arbeitsvorrat der
 * Freigabe, dieselbe Bauform wie `listKontierungenWartend()`.
 */
export async function listGuthabenErstattungenWartend(): Promise<readonly GuthabenErstattung[]> {
  const result = await request('/guthaben/erstattungen/wartend', guthabenErstattungWartendListSchema);
  return result.erstattungen;
}

/**
 * Die Guthaben-Korrektur — LESEN und FREIGEBEN, Auftrag `GU-korrekturmaske`
 * (Auftraggeberfrage vom 2026-09-08: „Wo ist Guthaben → Korrektur?").
 *
 * **Vier der sechs Endpunkte, und die zwei fehlenden sind Absicht.** Die
 * ANLAGE (`POST /guthaben/korrekturen`) und die SPIEGELUNG
 * (`POST /guthaben/korrekturen/:id/spiegelung`) haben hier keine Entsprechung:
 * Die Anlage entsteht heute ausschliesslich über den Cutover-Läufer
 * (`scripts/cutover-neuaufbau.mjs`, Phase 7), der genau EINE
 * Anfangsbestandskorrektur erzeugt und dann auf die Freigabe durch einen
 * zweiten Menschen wartet — und das ist der Vorgang, für den die Oberfläche
 * fehlte. Ein Anlegeformular ohne diesen Anlass wäre ein zweiter Weg zu einer
 * freihändigen Buchung ohne äusseren Anker.
 *
 * **`id` ist IMMER die Kennung der ANLAGE**, nie `zeileId`: Alle schreibenden
 * Endpunkte lösen im Pfad über `ersetzt_korrektur_id IS NULL` auf, und eine
 * Zeilenkennung führte geradewegs in ein `GUTHABEN_KORREKTUR_NOT_FOUND`.
 */

/** `GET /api/v1/guthaben/korrekturen` — je Vorgang der aktuelle Stand. `guthaben:abstimmung:lesen`. */
export async function listGuthabenKorrekturen(filter?: {
  readonly status?: string;
}): Promise<readonly GuthabenKorrektur[]> {
  const result = await request(
    `/guthaben/korrekturen${query({ status: filter?.status })}`,
    guthabenKorrekturListSchema,
  );
  return result.korrekturen;
}

/**
 * `GET /api/v1/guthaben/korrekturen/:id` — die ganze Kette, Anlage zuerst.
 *
 * Die einzige Ansicht, in der die Buchung einer GESPIEGELTEN Korrektur
 * sichtbar bleibt: `journalEntryId` hängt an der Freigabezeile und wandert
 * ausdrücklich nicht auf die Spiegelzeile mit.
 */
export async function getGuthabenKorrekturKette(
  id: string,
): Promise<readonly GuthabenKorrektur[]> {
  const result = await request(
    `/guthaben/korrekturen/${encodeURIComponent(id)}`,
    guthabenKorrekturKetteSchema,
  );
  return result.kette;
}

/**
 * `POST /api/v1/guthaben/korrekturen/:id/freigabe` — die Freigabe. Bucht das
 * Guthabenkonto gegen das Gegenkonto des Grundes, ohne Steuerzeile.
 * `guthaben:erstatten:freigeben`; wer angelegt hat, wird mit
 * `GUTHABEN_KORREKTUR_APPROVAL_SELF` abgewiesen.
 */
export async function gebGuthabenKorrekturFrei(id: string): Promise<GuthabenKorrektur> {
  return await request(
    `/guthaben/korrekturen/${encodeURIComponent(id)}/freigabe`,
    guthabenKorrekturSchema,
    { method: 'POST' },
  );
}

/**
 * Der Geldkonto-Anfangsbestand — nur Lesen und Freigeben, Auftrag
 * `K-geldkontenmaske` (2026-09-08). Angelegt wird ausschliesslich über den
 * Handlauf `pnpm kontierung:geldkontenanfang -- --run`
 * (`GeldkontoAnfangsbestandController`, Kopfkommentar „KEIN `POST` ZUM
 * ANLEGEN"); diese Seite bietet dafür keinen Weg.
 */

/**
 * `GET /api/v1/kontierungen/geldkonto-anfangsbestaende` — die Arbeitsliste.
 * Erfordert `kontierung:approve`.
 */
export async function listGeldkontoAnfangsbestaende(): Promise<
  readonly GeldkontoAnfangsbestandZeile[]
> {
  const result = await request(
    '/kontierungen/geldkonto-anfangsbestaende',
    geldkontoAnfangsbestandListeSchema,
  );
  return result.anfangsbestaende;
}

/**
 * `POST …/geldkonto-anfangsbestaende/:id/freigabe` — die Freigabe durch eine
 * ZWEITE Person. Bucht `<Geldkonto>` gegen `9000`, kein Rumpf.
 */
export async function gebGeldkontoAnfangsbestandFrei(
  id: string,
): Promise<GeldkontoAnfangsbestandFreigabeAntwort> {
  return await request(
    `/kontierungen/geldkonto-anfangsbestaende/${encodeURIComponent(id)}/freigabe`,
    geldkontoAnfangsbestandFreigabeAntwortSchema,
    { method: 'POST' },
  );
}

/**
 * Der Sachkonto-Saldenvortrag — Auftrag `K-saldenvortrag` (2026-10-06),
 * Migration `0285`, `D-53`. Anders als beim Geldkonto-Anfangsbestand legt hier
 * ein Mensch an (`kontierung:write`), und eine zweite Person gibt frei
 * (`kontierung:approve`).
 */

/** `GET /api/v1/kontierungen/saldenvortraege` — Liste und Kontenauswahl. Erfordert `journal:read`. */
export async function listSaldenvortraege(): Promise<SachkontoSaldenvortragListe> {
  return await request('/kontierungen/saldenvortraege', sachkontoSaldenvortragListeSchema);
}

/** `POST /api/v1/kontierungen/saldenvortraege` — die Anlage. Bucht nicht. */
export async function legeSaldenvortragAn(
  eingabe: SachkontoSaldenvortragAnlageEingabe,
): Promise<SachkontoSaldenvortragAnlageAntwort> {
  return await request('/kontierungen/saldenvortraege', sachkontoSaldenvortragAnlageAntwortSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/**
 * `POST …/saldenvortraege/:id/ersetzen` — die Ersetzung einer angelegten Zeile,
 * oder einer freigegebenen, deren Buchung storniert ist (`D-54`). Bucht nicht;
 * die neue Zeile wartet wieder auf die Freigabe durch eine zweite Person.
 */
export async function ersetzeSaldenvortrag(
  id: string,
  eingabe: SachkontoSaldenvortragErsetzenEingabe,
): Promise<SachkontoSaldenvortragAnlageAntwort> {
  return await request(
    `/kontierungen/saldenvortraege/${encodeURIComponent(id)}/ersetzen`,
    sachkontoSaldenvortragAnlageAntwortSchema,
    { method: 'POST', body: JSON.stringify(eingabe) },
  );
}

/** `POST …/saldenvortraege/:id/freigabe` — die Freigabe durch eine ZWEITE Person; bucht. */
export async function gebSaldenvortragFrei(
  id: string,
): Promise<SachkontoSaldenvortragFreigabeAntwort> {
  return await request(
    `/kontierungen/saldenvortraege/${encodeURIComponent(id)}/freigabe`,
    sachkontoSaldenvortragFreigabeAntwortSchema,
    { method: 'POST' },
  );
}

/**
 * `GET /api/v1/guthaben/korrekturen/spiegel-offen` — der Arbeitsvorrat der
 * Spiegelung. Trägt `guthaben:erstatten` und NICHT die Lesefähigkeit der
 * Liste; wer nur freigeben darf, bekommt hier eine 403 und die Oberfläche
 * ruft die Liste deshalb nur mit der passenden Fähigkeit ab.
 */
export async function listGuthabenKorrekturenSpiegelOffen(): Promise<
  readonly GuthabenKorrekturSpiegelOffen[]
> {
  const result = await request(
    '/guthaben/korrekturen/spiegel-offen',
    guthabenKorrekturSpiegelOffenListSchema,
  );
  return result.korrekturen;
}

// Vorsystem — Seite Übernahme (LX-webseite) ----------------------------------
//
// Die Routen der Übernahme aus Lexware Office (`apps/api/src/vorsystem/
// uebernahme.controller.ts`, Spec Lexware-Migration 20.8) und die bestehende
// Liste und Freigabe der Eröffnungsposten (`vorsystem.controller.ts`). Alle
// Routen der Übernahme verlangen `vorsystem:import` und sind sitzungsgebunden;
// die Seite rechnet nichts nach, was der Server schon beantwortet
// (`uebernehmenErlaubt`, `trockenlaufAuftragId`, `grund`).

/** `GET /api/v1/vorsystem/uebernahme` — der Stand der Übernahme samt Tor je Schritt. */
export async function ladeUebernahmeStatus(): Promise<VorsystemUebernahmeStatus> {
  return await request('/vorsystem/uebernahme', vorsystemUebernahmeStatusSchema);
}

/**
 * `GET /api/v1/vorsystem/uebernahme/angelegt` — nur das Bit der Menüspalte (`E-2002`): ist die Übernahme angelegt?
 * Eine Abfrage im Server; den Stand holt allein die Seite.
 */
export async function ladeUebernahmeAngelegt(): Promise<boolean> {
  return (await request('/vorsystem/uebernahme/angelegt', vorsystemAngelegtSchema)).angelegt;
}

/** `GET /api/v1/vorsystem/auftraege` — die jüngsten Aufträge, jüngster zuerst. */
export async function ladeUebernahmeAuftraege(): Promise<readonly VorsystemAuftrag[]> {
  const liste = await request('/vorsystem/auftraege', vorsystemAuftragListeSchema);
  return liste.auftraege;
}

/** `GET /api/v1/vorsystem/auftraege/:id` — der Stand eines Auftrags (die Seite fragt alle fünf Sekunden). */
export async function ladeUebernahmeAuftrag(id: string): Promise<VorsystemAuftrag> {
  return await request(`/vorsystem/auftraege/${encodeURIComponent(id)}`, vorsystemAuftragSchema);
}

/**
 * `POST /api/v1/vorsystem/auftraege` — einen Schritt anfordern. `schreiben: false` ist der
 * Trockenlauf („Prüfen"), `true` der echte Lauf; der Server lässt einen echten Lauf nur nach
 * einem sauberen Trockenlauf desselben Stands zu (`E-1607`). Der Schritt `guthaben` geht
 * ausschliesslich über {@link legeUebernahmeGuthabenAn}.
 */
export async function legeUebernahmeAuftragAn(eingabe: {
  readonly schritt: Exclude<VorsystemAuftragSchritt, 'guthaben'>;
  readonly schreiben: boolean;
  readonly eingabe?: VorsystemAuftragEingabe;
}): Promise<VorsystemAuftrag> {
  return await request('/vorsystem/auftraege', vorsystemAuftragSchema, {
    method: 'POST',
    body: JSON.stringify({
      art: 'schritt',
      schritt: eingabe.schritt,
      schreiben: eingabe.schreiben,
      eingabe: eingabe.eingabe ?? {},
    }),
  });
}

/**
 * `POST /api/v1/vorsystem/auftraege/guthaben` — der Trockenlauf des Guthabens mit der
 * Stichtagsdatei. Der Rumpf IST die Datei (Bytes, kein JSON; Grenze `VORSYSTEM_DATEI_MAX_BYTES`
 * am Server, Vorgabe 10 MB, Antwort `413 VORSYSTEM_DATEI_TOO_LARGE`). Sie erscheint in keiner
 * Antwort wieder (`E-1918`); der echte Lauf entsteht über {@link uebernimmUebernahmeTrockenlauf}
 * mit denselben Bytes.
 */
export async function legeUebernahmeGuthabenAn(datei: File): Promise<VorsystemAuftrag> {
  return await request('/vorsystem/auftraege/guthaben', vorsystemAuftragSchema, {
    method: 'POST',
    body: datei,
    // Gewinnt gegen das `application/json`, das `request` sonst setzt.
    headers: { 'content-type': 'application/octet-stream' },
  });
}

/** `POST /api/v1/vorsystem/auftraege/:id/uebernehmen` — aus einem sauberen Trockenlauf den echten Lauf anfordern. */
export async function uebernimmUebernahmeTrockenlauf(id: string): Promise<VorsystemAuftrag> {
  return await request(`/vorsystem/auftraege/${encodeURIComponent(id)}/uebernehmen`, vorsystemAuftragSchema, {
    method: 'POST',
  });
}

/** `POST /api/v1/vorsystem/auftraege/:id/abbrechen` — einen Auftrag abbrechen (ohne Rumpf). */
export async function brichUebernahmeAuftragAb(id: string): Promise<VorsystemAuftrag> {
  return await request(`/vorsystem/auftraege/${encodeURIComponent(id)}/abbrechen`, vorsystemAuftragSchema, {
    method: 'POST',
  });
}

/** `GET /api/v1/vorsystem/befunde` — je Schritt die Befunde des jüngsten Laufs (nie ein Name, nie ein Betrag). */
export async function ladeUebernahmeBefunde(): Promise<VorsystemBefunde> {
  return await request('/vorsystem/befunde', vorsystemBefundeSchema);
}

/** `GET /api/v1/vorsystem/kontaktentscheide` — die Entscheide zu Kontakten mit vergebener Nummer (`D-97`). */
export async function ladeKontaktentscheide(): Promise<VorsystemKontaktentscheide> {
  return await request('/vorsystem/kontaktentscheide', vorsystemKontaktentscheideSchema);
}

/** `POST /api/v1/vorsystem/kontaktentscheide` — verknüpfen oder umnummerieren, mit Begründung (`D-97`). */
export async function triffKontaktentscheid(eingabe: VorsystemKontaktentscheidEingabe): Promise<VorsystemKontaktentscheid> {
  return await request('/vorsystem/kontaktentscheide', vorsystemKontaktentscheidSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/** `POST /api/v1/vorsystem/kontaktentscheide/:id/zuruecknehmen` — einen Entscheid zurücknehmen. 204 ohne Rumpf. */
export async function nimmKontaktentscheidZurueck(id: string): Promise<void> {
  await request(`/vorsystem/kontaktentscheide/${encodeURIComponent(id)}/zuruecknehmen`, ohneInhaltSchema, { method: 'POST' });
}

/**
 * `GET /api/v1/vorsystem/kontenabbildung` — die Sachkonten aus Lexware Office (`D-82`): Kategorie → Sachkonto,
 * Konto des DATEV-Exports → Konto dieses Systems und die Konten, die heute gelten.
 */
export async function ladeKontenabbildung(): Promise<VorsystemKontenabbildung> {
  return await request('/vorsystem/kontenabbildung', vorsystemKontenabbildungSchema);
}

/** `PUT /api/v1/vorsystem/kontenabbildung/kategorien/:kategorie` — bestätigt das Sachkonto einer Buchungskategorie. */
export async function bestaetigeKategorieKonto(kategorie: string, konto: string): Promise<VorsystemKontenabbildung> {
  return await request(`/vorsystem/kontenabbildung/kategorien/${encodeURIComponent(kategorie)}`, vorsystemKontenabbildungSchema, {
    method: 'PUT',
    body: JSON.stringify({ konto }),
  });
}

/** `DELETE /api/v1/vorsystem/kontenabbildung/kategorien/:kategorie` — nimmt die Bestätigung zurück. */
export async function nimmKategorieKontoZurueck(kategorie: string): Promise<VorsystemKontenabbildung> {
  return await request(`/vorsystem/kontenabbildung/kategorien/${encodeURIComponent(kategorie)}`, vorsystemKontenabbildungSchema, {
    method: 'DELETE',
  });
}

/** `PUT /api/v1/vorsystem/kontenabbildung/konten/:kontoVorsystem` — bestätigt das Konto für ein Konto des Exports. */
export async function bestaetigeExportKonto(kontoVorsystem: string, konto: string): Promise<VorsystemKontenabbildung> {
  return await request(`/vorsystem/kontenabbildung/konten/${encodeURIComponent(kontoVorsystem)}`, vorsystemKontenabbildungSchema, {
    method: 'PUT',
    body: JSON.stringify({ konto }),
  });
}

/** `DELETE /api/v1/vorsystem/kontenabbildung/konten/:kontoVorsystem` — nimmt die Bestätigung zurück. */
export async function nimmExportKontoZurueck(kontoVorsystem: string): Promise<VorsystemKontenabbildung> {
  return await request(`/vorsystem/kontenabbildung/konten/${encodeURIComponent(kontoVorsystem)}`, vorsystemKontenabbildungSchema, {
    method: 'DELETE',
  });
}

/**
 * `POST /api/v1/vorsystem/kontenabbildung/export` — der DATEV-Export aus Lexware Office für die Vorschläge
 * (`D-93`). Der Rumpf IST die Datei (Bytes; Grenze `VORSYSTEM_DATEI_MAX_BYTES`, Vorgabe 10 MB, Antwort
 * `413 VORSYSTEM_EXPORT_TOO_LARGE`); der Server legt sie nicht ab.
 */
export async function liesKontenExport(datei: File): Promise<VorsystemKontenExportErgebnis> {
  return await request('/vorsystem/kontenabbildung/export', vorsystemKontenExportErgebnisSchema, {
    method: 'POST',
    body: datei,
    headers: { 'content-type': 'application/octet-stream' },
  });
}

/** `GET /api/v1/vorsystem/kontenabbildung/kategorien/:kategorie` — häufigste Gegenparteien und drei Beispielbelege. */
export async function ladeKategorieDetails(kategorie: string): Promise<VorsystemKategorieDetails> {
  return await request(`/vorsystem/kontenabbildung/kategorien/${encodeURIComponent(kategorie)}`, vorsystemKategorieDetailsSchema);
}

/** `POST …/kontenabbildung/kategorien/bestaetigen` — übernimmt je Kategorie ihre Empfehlung, in einem Stapel. */
export async function bestaetigeKategorien(kategorien: readonly string[], regelMerken: boolean): Promise<VorsystemKontenStapelErgebnis> {
  return await request('/vorsystem/kontenabbildung/kategorien/bestaetigen', vorsystemKontenStapelErgebnisSchema, {
    method: 'POST',
    body: JSON.stringify({ kategorien, regelMerken }),
  });
}

/** `POST …/kontenabbildung/kategorien/zuordnen` — ein Konto für alle genannten Kategorien, bestätigt, in einem Stapel. */
export async function ordneKategorienZu(
  kategorien: readonly string[],
  konto: string,
  regelMerken: boolean,
): Promise<VorsystemKontenStapelErgebnis> {
  return await request('/vorsystem/kontenabbildung/kategorien/zuordnen', vorsystemKontenStapelErgebnisSchema, {
    method: 'POST',
    body: JSON.stringify({ kategorien, konto, regelMerken }),
  });
}

/** `POST …/kontenabbildung/kategorien/sichere-bestaetigen` — bestätigt jede sichere Empfehlung, in einem Stapel. */
export async function bestaetigeSichereKategorien(regelMerken: boolean): Promise<VorsystemKontenStapelErgebnis> {
  return await request('/vorsystem/kontenabbildung/kategorien/sichere-bestaetigen', vorsystemKontenStapelErgebnisSchema, {
    method: 'POST',
    body: JSON.stringify({ regelMerken }),
  });
}

/** `POST …/kontenabbildung/stapel/:stapelId/rueckgaengig` — macht einen Stapel rückgängig (ohne Rumpf). */
export async function macheKontenstapelRueckgaengig(stapelId: string): Promise<VorsystemKontenStapelErgebnis> {
  return await request(`/vorsystem/kontenabbildung/stapel/${encodeURIComponent(stapelId)}/rueckgaengig`, vorsystemKontenStapelErgebnisSchema, {
    method: 'POST',
  });
}

/** `GET /api/v1/vorsystem/eroeffnungsposten` — die Arbeitsliste samt Buchungsform (`null`, solange keine gewählt ist) und Einstellung. `journal:read`. */
export async function ladeEroeffnungsposten(): Promise<EroeffnungspostenListe> {
  return await request('/vorsystem/eroeffnungsposten', eroeffnungspostenListeSchema);
}

/** `PUT /api/v1/vorsystem/eroeffnungsform` — wählt die Buchungsform der Eröffnung. `vorsystem:import`, sitzungsgebunden. */
export async function setzeEroeffnungsform(eingabe: EroeffnungsformEingabe): Promise<Eroeffnungsform> {
  return await request('/vorsystem/eroeffnungsform', eroeffnungsformSchema, {
    method: 'PUT',
    body: JSON.stringify(eingabe),
  });
}

/**
 * `POST /api/v1/vorsystem/eroeffnungsposten/:id/freigabe` — die BESTEHENDE Freigaberoute
 * (`kontierung:approve`, vier Augen). `id` ist die Kennung der Anlage, nie `zeileId`.
 */
export async function gebEroeffnungspostenFrei(id: string): Promise<EroeffnungspostenFreigabeAntwort> {
  return await request(
    `/vorsystem/eroeffnungsposten/${encodeURIComponent(id)}/freigabe`,
    eroeffnungspostenFreigabeAntwortSchema,
    { method: 'POST' },
  );
}

/**
 * `POST /api/v1/vorsystem/eroeffnungsposten/freigabe` — die Sammelfreigabe der Eröffnungen
 * (`kontierung:approve`, sitzungsgebunden). Ohne `postenIds` alle angelegten Eröffnungen; je Posten
 * dieselbe Regel wie die Einzelfreigabe, ein Ergebnis je Posten. Ausbuchungen nie.
 */
export async function gebEroeffnungspostenGesammeltFrei(
  postenIds?: readonly string[],
): Promise<EroeffnungspostenSammelfreigabeAntwort> {
  return await request('/vorsystem/eroeffnungsposten/freigabe', eroeffnungspostenSammelfreigabeAntwortSchema, {
    method: 'POST',
    body: JSON.stringify(postenIds === undefined ? {} : { postenIds }),
  });
}

// --- Benutzerverwaltung — `apps/api/src/users/users.controller.ts` --------
//
// `user:read` fuer die Liste, `user:manage` fuer Anlegen, Rollen, Deaktivieren,
// Aktivieren, Sperraufhebung und TOTP-Reset — siehe den Kopfkommentar des
// Controllers. Seit `U-benutzerstatus` (2026-09-09) traegt die Oberflaeche
// auch die vier Statuswege; vorher gab es dafuer nur die API (`curl`).

/** Die Benutzerliste, nach E-Mail-Adresse sortiert. */
export async function listUsers(): Promise<readonly User[]> {
  const result = await request('/users', userListSchema);
  return result.users;
}

/** Die Eingabe fuer `POST /api/v1/users` — siehe `createSchema` im Controller. */
export interface UserAnlageEingabe {
  readonly email: string;
  readonly displayName: string;
  readonly roles: readonly string[];
}

/**
 * Legt einen Benutzer an. Das Einmalpasswort steht **nur** in der Antwort
 * dieses einen Aufrufs — es wird nicht gespeichert und ist ueber keinen
 * zweiten Weg zu bekommen.
 */
export async function createUser(eingabe: UserAnlageEingabe): Promise<CreatedUser> {
  return await request('/users', createdUserSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/**
 * Erzeugt für ein Konto ein neues Einmalpasswort — `POST /users/:id/password/reset`.
 *
 * Dieselbe Zusage wie bei `createUser()`: Das Passwort steht **nur** in der Antwort
 * dieses einen Aufrufs. Das bisherige Passwort gilt danach nicht mehr, und jede
 * Sitzung des Kontos endet; der zweite Faktor bleibt.
 */
export async function erzeugeNeuesEinmalpasswort(id: string): Promise<PasswordReset> {
  return await request(`/users/${encodeURIComponent(id)}/password/reset`, passwordResetSchema, {
    method: 'POST',
  });
}

/** Vergibt eine Rolle. 204 ohne Rumpf. */
export async function grantUserRole(id: string, role: string): Promise<void> {
  await request(`/users/${encodeURIComponent(id)}/roles`, ohneInhaltSchema, {
    method: 'POST',
    body: JSON.stringify({ role }),
  });
}

/** Entzieht eine Rolle. 204 ohne Rumpf. */
export async function revokeUserRole(id: string, role: string): Promise<void> {
  await request(
    `/users/${encodeURIComponent(id)}/roles/${encodeURIComponent(role)}`,
    ohneInhaltSchema,
    { method: 'DELETE' },
  );
}

/**
 * Deaktiviert ein Konto (`POST …/disable`). 204 ohne Rumpf.
 *
 * Der letzte Administrator ist geschützt (`LAST_ADMIN`), der Systembenutzer
 * unveränderlich (`SYSTEM_USER_IMMUTABLE`) — beide Ablehnungen kommen als
 * `ApiError` zurück, die Meldung des Dienstes wird unverändert angezeigt.
 */
export async function deaktiviereBenutzer(id: string): Promise<void> {
  await request(`/users/${encodeURIComponent(id)}/disable`, ohneInhaltSchema, {
    method: 'POST',
  });
}

/**
 * Aktiviert ein deaktiviertes Konto wieder (`POST …/enable`). 204 ohne Rumpf.
 * Passwort und zweiter Faktor bleiben unverändert.
 */
export async function aktiviereBenutzer(id: string): Promise<void> {
  await request(`/users/${encodeURIComponent(id)}/enable`, ohneInhaltSchema, {
    method: 'POST',
  });
}

/**
 * Hebt eine Fehlversuchssperre auf und setzt den Fehlversuchszähler zurück
 * (`POST …/unlock`). 204 ohne Rumpf. Nicht dringend — die Sperre läuft nach
 * 15 Minuten von selbst aus.
 */
export async function entsperreBenutzer(id: string): Promise<void> {
  await request(`/users/${encodeURIComponent(id)}/unlock`, ohneInhaltSchema, {
    method: 'POST',
  });
}

/**
 * Setzt den zweiten Faktor zurück (`POST …/totp/reset`). 204 ohne Rumpf. Der
 * Weg für ein verlorenes Gerät: Der Benutzer steht danach wieder auf
 * `awaiting_totp_enroll` und richtet bei der nächsten Anmeldung neu ein.
 */
export async function setzeTotpZurueck(id: string): Promise<void> {
  await request(`/users/${encodeURIComponent(id)}/totp/reset`, ohneInhaltSchema, {
    method: 'POST',
  });
}

// --- API-Tokens — `apps/api/src/apitokens/apitokens.controller.ts` --------
//
// Alle vier Endpunkte verlangen `apitoken:manage` und eine SITZUNG: Ein Token,
// das ein zweites Token ausstellen koennte, naehme den letzten Menschen aus der
// Verantwortungskette (`actorOf()` im Controller antwortet einer Token-Anfrage
// hier mit 403 und nicht mit 401). `PUT /:id/permissions` bekommt seine
// Oberflaeche mit `W-tokenrechte` (2026-09-26) — der Dialog
// `ApiTokenPermissionsDialog.tsx` an einem bestehenden, aktiven Token; vorher
// blieb dafuer nur ein neues Token, vier Wochen lang.

/** Die Tokenliste — aktive und widerrufene, neueste zuerst. */
export async function listApiTokens(): Promise<readonly ApiToken[]> {
  const result = await request('/apitokens', apiTokenListSchema);
  return result.tokens;
}

/** Die Eingabe fuer `POST /api/v1/apitokens` — siehe `createSchema` im Controller. */
export interface ApiTokenAnlageEingabe {
  readonly beschreibung: string;
  /** ISO-Zeitstempel oder `null` — ohne Angabe gilt das Token bis zum Widerruf. */
  readonly expiresAt: string | null;
  readonly permissions: readonly { readonly method: string; readonly path: string }[];
}

/**
 * Legt ein Token an und reicht die Antwort UNVERAENDERT durch — samt ihrem
 * `value`.
 *
 * **Der Wert wird hier nicht angefasst, nicht gemerkt und nicht protokolliert.**
 * Diese Funktion haelt keinen Zustand; der Wert lebt ausschliesslich in dem
 * einen `useState` des Anlegedialogs (`ApiTokenCreateDialog.tsx`) und
 * verschwindet mit ihm. Dieselbe Zusage wie bei `createUser()` oben.
 */
export async function createApiToken(eingabe: ApiTokenAnlageEingabe): Promise<CreatedApiToken> {
  return await request('/apitokens', createdApiTokenSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/**
 * Widerruft ein Token. 204 ohne Rumpf, idempotent.
 *
 * `DELETE` ist die Methode, die Zeile bleibt: Widerruf statt Hartloeschung,
 * damit `access_log.token_id` und `audit_log.token_id` ihre Referenz behalten
 * (Design-Scribble `2026-08-31-api-tokens-und-debugger.md` Abschnitt 8).
 */
export async function revokeApiToken(id: string): Promise<void> {
  await request(`/apitokens/${encodeURIComponent(id)}`, ohneInhaltSchema, { method: 'DELETE' });
}

/** Der Rumpf fuer `PUT /api/v1/apitokens/:id/permissions` — dieselbe Form wie beim Anlegen. */
export interface ApiTokenBerechtigungseingabe {
  readonly permissions: readonly { readonly method: string; readonly path: string }[];
}

/**
 * Setzt die **vollstaendige** Berechtigungsliste eines bestehenden Tokens —
 * `ApiTokenPermissionsDialog.tsx`. Kein Anhaengen: Was in `permissions` fehlt
 * und heute aktiv ist, wird entzogen; ein leerer Satz entzieht damit jede
 * Berechtigung (der Vertrag laesst das zu, `min(0)`).
 *
 * `API_TOKEN_ROUTE_UNKNOWN` und `API_TOKEN_NOT_FOUND` sind erwartbare
 * Fehlerfaelle dieses Aufrufs und keine Ausnahme — der Dialog faengt sie ab
 * und zeigt sie an der Auswahl beziehungsweise als Hinweis mit Neuladen.
 */
export async function setApiTokenPermissions(
  id: string,
  eingabe: ApiTokenBerechtigungseingabe,
): Promise<readonly ApiTokenPermission[]> {
  const result = await request(
    `/apitokens/${encodeURIComponent(id)}/permissions`,
    apiTokenPermissionsResponseSchema,
    { method: 'PUT', body: JSON.stringify(eingabe) },
  );
  return result.permissions;
}

/**
 * Die Schnittstellenbeschreibung der LAUFENDEN Auslieferung — Quelle der
 * Endpunktauswahl im Anlegedialog.
 *
 * **Eigener `fetch` statt `request()`, und das ist der ganze Unterschied.**
 * `request()` haengt jeden Pfad an `/api/v1`; dieser Endpunkt liegt unter
 * `/api/docs` (`apps/api/src/openapi/docs.controller.ts`) und braucht nur eine
 * Sitzung, keine Faehigkeit. Fehlerbehandlung und Schemapruefung sind dieselben
 * — deshalb steht die Antwort trotzdem durch `schnittstellenbeschreibungSchema`
 * und nicht als `as`-Behauptung in der Oberflaeche.
 *
 * **Warum die laufende Auslieferung und nicht die eingecheckte Datei.** Ein
 * Import von `docs/openapi/openapi.json` zur Bauzeit legte rund ein Megabyte in
 * jedes Buendel — fuer eine Liste, die genau ein Administrator beim Anlegen
 * eines Tokens sieht. Vor allem aber ist die laufende Auslieferung die Instanz,
 * gegen die `assertRegistered()` die Berechtigung dann wirklich prueft: Zwei
 * Staende, die auseinanderliefen, faenden sich sonst erst im 400 wieder.
 * `pnpm api:spec:check` haelt beide ohnehin Byte fuer Byte aneinander.
 */
export async function ladeSchnittstellenbeschreibung(): Promise<Schnittstellenbeschreibung> {
  const response = await fetch('/api/docs/openapi.json', {
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });

  const text = await response.text();
  let body: unknown = undefined;
  if (text !== '') {
    try {
      body = JSON.parse(text);
    } catch {
      body = undefined;
    }
  }

  if (!response.ok) throw toApiError(response.status, body);

  const parsed = schnittstellenbeschreibungSchema.safeParse(body ?? {});
  if (!parsed.success) {
    throw new ApiError(
      response.status,
      'RESPONSE_UNREADABLE',
      'Die Schnittstellenbeschreibung hat eine unerwartete Form.',
      undefined,
    );
  }
  return parsed.data;
}

// --- Aborechnungsvorlagen — `apps/api/src/aborechnung/aborechnungen.controller.ts` ---
//
// Sechs Endpunkte, zwei Faehigkeiten. Lesen haengt an `document:read`, jedes
// Schreiben an `aborechnung:manage` — und der EINE Weg nach `aktiv`
// zusaetzlich an `aborechnung:scharfschalten`, das allein der Administrator
// traegt. Das ist keine Eigenheit dieser Datei: Der Wächter am Rand kennt nur
// die Faehigkeit der ROUTE, und welcher der drei Statuswege gemeint ist,
// steht im Rumpf — deshalb prueft der Handler die engere Schwelle selbst.
//
// **Es gibt kein `deleteAborechnung`, und das ist kein Versehen.** `beendet`
// ist der Endzustand einer Vorlage; die Rechnungen, die sie erzeugt hat,
// zeigen ueber `aborechnung_lauf` auf sie. Einen `DELETE`-Endpunkt gibt es
// nicht, und `fameacc_app` hat auf der Kopftabelle kein Loeschrecht.

/** Der Filter der Liste. Beide Angaben freiwillig; fehlend heisst «alle». */
export interface AborechnungFilterEingabe {
  readonly status?: string | undefined;
  readonly customerId?: string | undefined;
}

/**
 * Die Vorlagenliste, nach dem naechsten Stichtag sortiert.
 *
 * **Kein Positionsdetail und keine Lauf-Historie.** Beides liefert die
 * Einzelauskunft; eine Liste, die beides mitfuehrte, wuechse mit dem Produkt
 * aus Vorlagen und bereits gelaufenen Perioden.
 *
 * **Wohl aber Kundenanzeige und Summen, je Zeile** (`E-762`,
 * `C-abolistesummen`, 2026-09-03) -- serverseitig gerechnet, mit derselben
 * Arithmetik wie ein Beleg. Die Seite laedt seither weder Kunde noch
 * Positionen je Zeile nach.
 */
export async function listAborechnungen(
  filter: AborechnungFilterEingabe = {},
): Promise<readonly AborechnungVorlagenListenzeile[]> {
  const result = await request(
    `/aborechnungen${query({ status: filter.status, customerId: filter.customerId })}`,
    aborechnungVorlageListeSchema,
  );
  // Die vier E-762-Felder sind am gemeinsamen Kopftyp OPTIONAL, weil die
  // Einzelauskunft denselben Vertrag benutzt und sie nicht fuehrt (siehe
  // Kopfkommentar von `aborechnungVorlageKopfSchema` in
  // packages/contracts/src/aborechnungen.ts) -- der Dienst setzt sie aber
  // fuer JEDE Listenzeile, ausnahmslos. Die Verschaerfung ist deshalb sicher.
  return result.vorlagen as readonly AborechnungVorlagenListenzeile[];
}

/** Kopf, Positionen und die Lauf-Historie einer Vorlage. */
export async function getAborechnung(id: string): Promise<AborechnungVorlageAnsicht> {
  return await request(
    `/aborechnungen/${encodeURIComponent(id)}`,
    aborechnungVorlageAnsichtSchema,
  );
}

/**
 * Legt eine Vorlage an — **immer als Entwurf**.
 *
 * Der Rumpf traegt kein Statusfeld, weder ein pflichtiges noch ein
 * freiwilliges; ein mitgeschicktes waere eine 400. Ein Entwurf erzeugt nichts,
 * und solange das Altsystem denselben Vertrag noch fakturiert, ist genau das
 * die Zusage, auf der die Uebergangszeit ruht.
 */
export async function createAborechnung(
  eingabe: AborechnungAnlageRaw,
): Promise<AborechnungVorlageAnsicht> {
  return await request('/aborechnungen', aborechnungVorlageAnsichtSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/**
 * Aendert den Kopf — nur in `entwurf` und `pausiert`.
 *
 * Eine scharfgeschaltete Vorlage wird nicht geaendert, sie wird pausiert:
 * pausieren → aendern → wieder scharfschalten, und das Reaktivieren verlangt
 * dieselbe Administratorrolle wie die erste Scharfschaltung.
 */
export async function patchAborechnung(
  id: string,
  aenderung: AborechnungPatchRaw,
): Promise<AborechnungVorlageAnsicht> {
  return await request(`/aborechnungen/${encodeURIComponent(id)}`, aborechnungVorlageAnsichtSchema, {
    method: 'PATCH',
    body: JSON.stringify(aenderung),
  });
}

/**
 * Ersetzt die GESAMTE Positionsmenge — nur in `entwurf` und `pausiert`.
 *
 * Ersetzen und nicht Anfuegen, dieselbe Wahl wie bei
 * `replaceDocumentLines`: Geschickt wird die Liste, die danach gelten soll,
 * und was nicht darin steht, ist danach fort. Die Positionsnummern vergibt
 * das System nach der Reihenfolge der Liste.
 */
export async function replaceAborechnungPositionen(
  id: string,
  positionen: readonly AborechnungPositionInputRaw[],
): Promise<AborechnungVorlageAnsicht> {
  return await request(
    `/aborechnungen/${encodeURIComponent(id)}/positionen`,
    aborechnungVorlageAnsichtSchema,
    { method: 'PUT', body: JSON.stringify({ positionen }) },
  );
}

/**
 * Der Statuswechsel: `aktiv`, `pausiert` oder `beendet`.
 *
 * **`entwurf` ist kein Ziel.** Eine bereits scharfgeschaltete Vorlage in den
 * Entwurf zurueckzustellen hiesse, die Scharfschaltung rueckgaengig zu machen,
 * ohne dass es jemand sieht — der Vertrag lehnt deshalb schon den Wert ab.
 */
export async function setAborechnungStatus(
  id: string,
  status: 'aktiv' | 'pausiert' | 'beendet',
): Promise<AborechnungVorlageAnsicht> {
  return await request(
    `/aborechnungen/${encodeURIComponent(id)}/status`,
    aborechnungVorlageAnsichtSchema,
    { method: 'POST', body: JSON.stringify({ status }) },
  );
}

/**
 * Die Anlage einer Aufhebung — `laufId` ist die Kennung der LAUFZEILE
 * (`aborechnung_lauf`), nicht die einer Vorlage (Migration `0166`,
 * `I-abolaufupdate`). Antwort ist die VOLLE Vorlagenansicht, dieselbe Form
 * wie jeder andere schreibende Endpunkt dieser Seite — die betroffene
 * Laufzeile trägt jetzt `aufhebung.status: "angelegt"`.
 */
export async function aborechnungLaufAufhebungAnlegen(
  laufId: string,
  eingabe: { readonly grund: AborechnungLaufAufhebungGrund; readonly begruendung: string },
): Promise<AborechnungVorlageAnsicht> {
  return await request(
    `/aborechnungen/laeufe/${encodeURIComponent(laufId)}/aufhebung`,
    aborechnungVorlageAnsichtSchema,
    { method: 'POST', body: JSON.stringify(eingabe) },
  );
}

/**
 * Die Freigabe einer Aufhebung — `aufhebungId` ist die Kennung der
 * ANLAGE-Zeile der Aufhebung (`lauf.aufhebung.id` in der vorigen Antwort),
 * nicht die des Laufs. Verlangt eine ZWEITE Person (Vier-Augen); ein
 * Selbstversuch scheitert mit `ABORECHNUNG_LAUF_AUFHEBUNG_APPROVAL_SELF`.
 */
export async function aborechnungLaufAufhebungFreigeben(
  aufhebungId: string,
): Promise<AborechnungVorlageAnsicht> {
  return await request(
    `/aborechnungen/laeufe/aufhebung/${encodeURIComponent(aufhebungId)}/freigabe`,
    aborechnungVorlageAnsichtSchema,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

// --- E-Mail-Vorlagen — `apps/api/src/mailvorlagen/mailvorlagen.controller.ts` ---
//
// Sieben der acht Endpunkte, drei Faehigkeiten. Lesen, Katalog und Vorschau
// haengen an `document:manage`, das Anfuegen und Verwerfen an
// `mailvorlage:manage`, das Scharfschalten an der eigenen Faehigkeit
// `mailvorlage:scharfschalten` (Owner-Entscheid `MV-6` vom 2026-09-02: bei
// jedem Sachbearbeiter, nicht beim Administrator allein).
//
// **`POST /api/v1/mailvorlagen` (Anlegen) steht hier absichtlich NICHT.** Der
// Erstbestand aus Migration `0131` bringt den Rahmen und die fuenf deutschen
// Inhaltsvorlagen mit; angelegt wuerde ueber diese Oberflaeche allein eine
// zweite SPRACHE, und die hat `F-mailmaske` nicht im Umfang. Ein
// Client-Aufruf ohne Bedienelement waere toter Code, der so lange
// unbenutzt mitgepflegt wird, bis ihn jemand fuer erprobt haelt.
//
// **Es gibt kein `deleteMailvorlage` und kein `patchMailvorlage`, und das ist
// kein Versehen** (`D-10`): Aendern heisst eine Fassung anfuegen. Die Kennung
// einer Fassung haengt spaeter an jeder damit versandten Mail; eine geloeschte
// waere eine unaufloesbare Referenz an einem Geschaeftsbrief. `fameacc_app`
// hat auf `mailvorlage` weder `UPDATE` noch `DELETE`.
//
// **Die fuenf Schemas kommen aus dem Vertragspaket und nicht aus
// `api/schemas.ts`** — dieselbe Wahl und dieselbe Begruendung wie bei den
// Aborechnungsvorlagen oben und beim Anfrage-Debugger: Sie haben dort ein
// Gegenstueck, und eine zweite Abschrift waere genau die Fehlerklasse, die
// `W-schemadoppel` geschlossen hat. `vorschau`, `vorschauFehler` und
// `rahmenQuelle` sind damit ohne Zutun mitgezogen.

/** Alle Vorlagen: der Rahmen zuerst, danach die Inhaltsvorlagen — je mit der scharfen Fassung. */
export async function getMailvorlagen(): Promise<MailvorlageListe> {
  return await request('/mailvorlagen', mailvorlageListeSchema);
}

/**
 * Der Platzhalterkatalog je Art, die fuenf Anlaesse und die Einsetzstelle des
 * Rahmens.
 *
 * **Diese Liste ist die einzige.** Eine Oberflaeche, die sie abschriebe, boete
 * irgendwann einen Platzhalter an, den der Speicherpfad abweist.
 */
export async function getMailPlatzhalter(): Promise<MailPlatzhalterAntwort> {
  return await request('/mailvorlagen/platzhalter', mailPlatzhalterAntwortSchema);
}

/** Kopf und **vollstaendige** Fassungshistorie, die juengste zuerst. */
export async function getMailvorlage(id: string): Promise<MailvorlageAnsicht> {
  return await request(`/mailvorlagen/${encodeURIComponent(id)}`, mailvorlageAnsichtSchema);
}

/**
 * Die Live-Vorschau eines **ungespeicherten** Vorlagentextes — NUR LESEND,
 * obwohl `POST`.
 *
 * Der Rumpf traegt den ganzen Vorlagentext samt Rahmen; in eine Abfragezeile
 * gepresst waeren das hundert Zeilen HTML in einer URL, und URLs stehen im
 * Zugriffsprotokoll. Der Endpunkt beruehrt die Datenbank nicht (`E-725`).
 */
export async function mailvorlageVorschau(
  rumpf: unknown,
): Promise<MailvorlageVorschauAntwort> {
  return await request('/mailvorlagen/vorschau', mailvorlageVorschauAntwortSchema, {
    method: 'POST',
    body: JSON.stringify(rumpf),
  });
}

/** Fuegt eine Fassung an — **immer als Entwurf**; die scharfe bleibt scharf. */
export async function mailvorlageFassungAnfuegen(
  id: string,
  rumpf: unknown,
): Promise<MailvorlageFassungAnsicht> {
  return await request(
    `/mailvorlagen/${encodeURIComponent(id)}/fassungen`,
    mailvorlageFassungAnsichtSchema,
    { method: 'POST', body: JSON.stringify(rumpf) },
  );
}

/**
 * Schaltet eine Fassung scharf — der Uebergang `entwurf → scharf`, der die
 * bisher scharfe Fassung im selben Zug abloest.
 */
export async function mailvorlageFassungScharfschalten(
  id: string,
  fassungId: string,
): Promise<MailvorlageFassungAnsicht> {
  return await request(
    `/mailvorlagen/${encodeURIComponent(id)}/fassungen/${encodeURIComponent(fassungId)}/scharfschalten`,
    mailvorlageFassungAnsichtSchema,
    { method: 'POST' },
  );
}

/** Verwirft einen Entwurf — ein Endzustand, und ausdruecklich kein Loeschen. */
export async function mailvorlageFassungVerwerfen(
  id: string,
  fassungId: string,
): Promise<MailvorlageFassungAnsicht> {
  return await request(
    `/mailvorlagen/${encodeURIComponent(id)}/fassungen/${encodeURIComponent(fassungId)}/verwerfen`,
    mailvorlageFassungAnsichtSchema,
    { method: 'POST' },
  );
}

// --- Belegvorlagen — `apps/api/src/belegvorlagen/` -------------------------
//
// Acht Endpunkte der Stufe 0 (Spec 10.1): Liste, Katalog, Einzelauskunft,
// Anlegen, Fassung anfuegen, Scharfschalten, Verwerfen und die Vorschau. Lesen,
// Katalog und Vorschau haengen an `document:manage`, Anlegen, Anfuegen und
// Verwerfen an `belegvorlage:manage`, das Scharfschalten an der eigenen
// Faehigkeit `belegvorlage:scharfschalten` (`D-7`).
//
// **Es gibt kein `deleteBelegvorlage` und kein `patchBelegvorlage`, und das
// ist kein Versehen** (`D-5`): Aendern heisst eine Fassung anfuegen, und der
// Weg zurueck zur Werksvorlage ist eine Fassung mit leeren Einstellungen. Die
// Kennung einer Fassung steht spaeter im Nachweis jedes archivierten Belegs.
//
// **Die Vertraege kommen aus dem Vertragspaket und nicht aus
// `api/schemas.ts`** — dieselbe Wahl und dieselbe Begruendung wie bei den
// E-Mail-Vorlagen oben: Eine zweite Abschrift waere genau die Fehlerklasse, die
// `W-schemadoppel` geschlossen hat.

/** Die Rumpfformen, wie die Oberflaeche sie SENDET (vor dem Parsen des Servers). */
export type BelegvorlageAnlageRaw = z.input<typeof belegvorlageAnlageSchema>;
export type BelegvorlageFassungEingabeRaw = z.input<typeof belegvorlageFassungEingabeSchema>;
export type BelegvorlageVorschauEingabeRaw = z.input<typeof belegvorlageVorschauEingabeSchema>;

/** Hausstil und alle Stellen (Layout × Sprache), je mit scharfer Fassung oder «Werk». */
export async function getBelegvorlagen(): Promise<BelegvorlageListe> {
  return await request('/belegvorlagen', belegvorlageListeSchema);
}

/**
 * Der Katalog: Felder, Pflichtbloecke, Layouts und Sprachen.
 *
 * **Diese Liste ist die einzige.** Eine Oberflaeche, die sie abschriebe, boete
 * irgendwann ein Feld an, das der Speicherpfad abweist.
 */
export async function getBelegvorlagenKatalog(): Promise<BelegvorlageKatalog> {
  return await request('/belegvorlagen/katalog', belegvorlageKatalogSchema);
}

/** Kopf und **vollstaendige** Fassungshistorie, die juengste zuerst. */
export async function getBelegvorlage(id: string): Promise<BelegvorlageAnsicht> {
  return await request(`/belegvorlagen/${encodeURIComponent(id)}`, belegvorlageAnsichtSchema);
}

/** Legt eine Vorlage an — Kopf und erste Fassung, **immer als Entwurf**. */
export async function belegvorlageAnlegen(
  rumpf: BelegvorlageAnlageRaw,
): Promise<BelegvorlageAnsicht> {
  return await request('/belegvorlagen', belegvorlageAnsichtSchema, {
    method: 'POST',
    body: JSON.stringify(rumpf),
  });
}

/** Fuegt eine Fassung an — **immer als Entwurf**; die scharfe bleibt scharf. */
export async function belegvorlageFassungAnfuegen(
  id: string,
  rumpf: BelegvorlageFassungEingabeRaw,
): Promise<BelegvorlageFassungAnsicht> {
  return await request(
    `/belegvorlagen/${encodeURIComponent(id)}/fassungen`,
    belegvorlageFassungAnsichtSchema,
    { method: 'POST', body: JSON.stringify(rumpf) },
  );
}

/**
 * Schaltet eine Fassung scharf — der Uebergang `entwurf → scharf`, der die
 * bisher scharfe Fassung im selben Zug abloest.
 */
export async function belegvorlageFassungScharfschalten(
  id: string,
  fassungId: string,
): Promise<BelegvorlageFassungAnsicht> {
  return await request(
    `/belegvorlagen/${encodeURIComponent(id)}/fassungen/${encodeURIComponent(fassungId)}/scharfschalten`,
    belegvorlageFassungAnsichtSchema,
    { method: 'POST' },
  );
}

/** Verwirft einen Entwurf — ein Endzustand, und ausdruecklich kein Loeschen. */
export async function belegvorlageFassungVerwerfen(
  id: string,
  fassungId: string,
): Promise<BelegvorlageFassungAnsicht> {
  return await request(
    `/belegvorlagen/${encodeURIComponent(id)}/fassungen/${encodeURIComponent(fassungId)}/verwerfen`,
    belegvorlageFassungAnsichtSchema,
    { method: 'POST' },
  );
}

/**
 * Die Vorschau: das PDF des festen Musterbelegs mit **ungespeicherten**
 * Einstellungen — NUR LESEND, obwohl `POST` (`E-1929`).
 *
 * Der Rumpf traegt beide Ebenen; in eine Abfragezeile gepresst stuenden die
 * Texte in der URL, und URLs stehen im Zugriffsprotokoll. Die Antwort sind
 * PDF-Bytes (`application/pdf`) und kein JSON, deshalb der Weg von
 * `getDocumentPdf`. Das `AbortSignal` bricht einen laufenden Abruf ab, wenn
 * ein neuer beginnt — der Server hat hoechstens zwei Vorschauen zugleich.
 */
export async function belegvorlageVorschau(
  rumpf: BelegvorlageVorschauEingabeRaw,
  signal?: AbortSignal,
): Promise<Blob> {
  const response = await fetch(`${BASE}/belegvorlagen/vorschau`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { accept: 'application/pdf', 'content-type': 'application/json' },
    body: JSON.stringify(rumpf),
    ...(signal === undefined ? {} : { signal }),
  });
  if (!response.ok) {
    let body: unknown = undefined;
    try {
      body = JSON.parse(await response.text());
    } catch {
      body = undefined;
    }
    throw toApiError(response.status, body);
  }
  return await response.blob();
}

// --- Zahlungszuordnung Stufe 1 — `apps/api/src/zahlungen/` ----------------
//
// **Die vier Verträge kommen aus `@fameacc/contracts` und NICHT aus
// `api/schemas.ts`.** Sie haben dort kein Gegenstück, und eines anzulegen
// wäre genau die Fehlerklasse, die `W-schemadoppel` geschlossen hat: eine von
// Hand abgeschriebene Werteliste, die still veraltet, sobald der Vertrag einen
// Wert dazubekommt (dort war es `structured_xml`, und die Oberfläche lehnte
// jeden so erfassten Beleg ab, ohne dass ein Test es meldete). Dieselbe Wahl
// wie bei den E-Mail-Vorlagen, dem Anfrage-Debugger und den
// Aborechnungsvorlagen oben.
//
// **Seit `ZZ-freigabeliste` (2026-09-06) sind es NEUN.** Bis dahin standen
// hier vier, und der Kommentar sagte auch, warum: „Die Freigabe
// (`zahlung:freigeben`) und der Guthaben-Handweg haben in dieser Aufgabe
// keine Oberfläche […] die Freigabeliste ist ein eigener Auftrag." Das ist
// dieser Auftrag. Dazu kommen die zwei lesenden Abfragen, ohne die die
// Oberfläche nicht auskommt: die Liste der wartenden Zuordnungen und die
// Kette einer Bewegung (`ersetztZuordnungId` nach einer Aufhebung — ohne sie
// läuft jede Wiederzuordnung in `409 ZAHLUNG_ZUORDNUNG_ALREADY_EXISTS`).
// Eine Client-Funktion ohne Aufrufer wäre weiterhin toter Code mit einem
// Vertrag daran; jede der fünf neuen hat ihren.

/** Die Filter der Klärungsliste. Alle drei sind freiwillig. */
export interface ZahlungKlaerungFilter {
  readonly grund?: ZahlungKlaerungsgrund;
  readonly konto?: string;
  readonly limit?: number;
}

/**
 * `GET /api/v1/zahlungen/klaerung` — welcher Geldeingang wartet, und warum.
 *
 * Liest die Sicht `zahlung_klaerung` (Migration `0161`). **`gesamt` zählt an
 * `limit` vorbei** — die Oberfläche zeigt eine Seite und nennt die wahre
 * Grösse des Arbeitsvorrats daneben.
 */
export async function listZahlungKlaerung(
  filter: ZahlungKlaerungFilter = {},
): Promise<ZahlungKlaerungsliste> {
  const params = new URLSearchParams();
  if (filter.grund !== undefined) params.set('grund', filter.grund);
  if (filter.konto !== undefined) params.set('konto', filter.konto);
  if (filter.limit !== undefined) params.set('limit', String(filter.limit));
  const query = params.toString();
  return await request(
    `/zahlungen/klaerung${query === '' ? '' : `?${query}`}`,
    zahlungKlaerungslisteSchema,
  );
}

/**
 * `GET /api/v1/zahlungen/vorschlag/:bankTransactionId` — was zugeordnet würde.
 *
 * **Schreibt nichts.** Der Dialog belegt seine Felder damit vor; angenommen
 * wird der Vorschlag erst durch `legeZahlungZuordnungAn()`.
 */
export async function getZahlungsvorschlag(
  bankTransactionId: string,
): Promise<Zahlungsvorschlag> {
  return await request(
    `/zahlungen/vorschlag/${encodeURIComponent(bankTransactionId)}`,
    zahlungsvorschlagSchema,
  );
}

/**
 * `POST /api/v1/zahlungen/zuordnungen` — die Anlage. **Bucht nicht**: Sie
 * wartet auf die Freigabe durch eine zweite Person.
 *
 * Der Rumpf geht als `unknown` hinein und wird vom Server gegen
 * `zahlungZuordnungCreateRequestSchema` geprüft — dieselbe Wahl wie bei den
 * E-Mail-Vorlagen oben: Ein zweites `parse()` im Browser prüfte gegen dieselbe
 * Fassung des Vertrags und fügte der Sicherheit nichts hinzu, verdoppelte aber
 * die Fehlermeldungen an zwei Orten.
 */
export async function legeZahlungZuordnungAn(rumpf: unknown): Promise<ZahlungZuordnung> {
  return await request('/zahlungen/zuordnungen', zahlungZuordnungSchema, {
    method: 'POST',
    body: JSON.stringify(rumpf),
  });
}

/**
 * `GET /api/v1/belege/:id/zahlungsstand` — die Ableitung aus `D-5`/`D-15`.
 *
 * **Kein vierter `document.status`.** Bruttobetrag minus der Summe der
 * freigegebenen, ungeersetzten Zuordnungen auf die Hauptforderung; ein Rest
 * bis 0,05 € gilt als erledigt, ein negativer Rest ist eine Überzahlung.
 */
export async function getZahlungsstand(documentId: string): Promise<Zahlungsstand> {
  return await request(
    `/belege/${encodeURIComponent(documentId)}/zahlungsstand`,
    zahlungsstandSchema,
  );
}

/** Die Filter der Freigabeliste. Beide sind freiwillig. */
export interface ZahlungFreigabeFilter {
  readonly quelle?: ZahlungZuordnungQuelle;
  readonly limit?: number;
}

/**
 * `GET /api/v1/zahlungen/zuordnungen/wartend` — was auf sein zweites
 * Augenpaar wartet (Auftrag `ZZ-freigabeliste`).
 *
 * **`gesamt` zählt an `limit` vorbei**, dieselbe Bauform wie
 * `listZahlungKlaerung()` darüber.
 */
export async function listZahlungZuordnungenWartend(
  filter: ZahlungFreigabeFilter = {},
): Promise<ZahlungZuordnungFreigabeliste> {
  const params = new URLSearchParams();
  if (filter.quelle !== undefined) params.set('quelle', filter.quelle);
  if (filter.limit !== undefined) params.set('limit', String(filter.limit));
  const query = params.toString();
  return await request(
    `/zahlungen/zuordnungen/wartend${query === '' ? '' : `?${query}`}`,
    zahlungZuordnungFreigabelisteSchema,
  );
}

/**
 * `GET /api/v1/zahlungen/zuordnungen/bewegung/:bankTransactionId` — die
 * Kette einer Bewegung samt `fortsetzung`.
 *
 * **`fortsetzung` kommt vom Server und wird hier nicht gerechnet.** Sie ist
 * die Zeile, an die eine neue Zuordnung anschliessen muss; die Kettenregel
 * im Browser nachzurechnen wäre eine zweite Fassung von
 * `zahlung_zuordnung_bewegung_wurzel`.
 */
export async function getZahlungZuordnungKette(
  bankTransactionId: string,
): Promise<ZahlungZuordnungKette> {
  return await request(
    `/zahlungen/zuordnungen/bewegung/${encodeURIComponent(bankTransactionId)}`,
    zahlungZuordnungKetteSchema,
  );
}

/**
 * `POST /api/v1/zahlungen/zuordnungen/:id/freigeben` — die sachliche
 * Freigabe durch die zweite Person. **Bucht.**
 *
 * Kein Rumpf: Der Vorgang steht vollständig im Pfad, und wer freigibt, sagt
 * die Sitzung.
 */
export async function gebZahlungZuordnungFrei(id: string): Promise<ZahlungZuordnung> {
  return await request(
    `/zahlungen/zuordnungen/${encodeURIComponent(id)}/freigeben`,
    zahlungZuordnungSchema,
    { method: 'POST' },
  );
}

/**
 * `POST /api/v1/zahlungen/zuordnungen/:id/aufheben` — die Aufhebung.
 *
 * **Nichts wird gelöscht**: Es entsteht eine neue Zeile mit
 * `herkunft: "aufgehoben"`; war die Zuordnung gebucht, wird die Buchung über
 * die Generalumkehr storniert. Die Begründung ist Pflicht.
 */
export async function hebeZahlungZuordnungAuf(
  id: string,
  begruendung: string,
): Promise<ZahlungZuordnung> {
  return await request(
    `/zahlungen/zuordnungen/${encodeURIComponent(id)}/aufheben`,
    zahlungZuordnungSchema,
    { method: 'POST', body: JSON.stringify({ begruendung }) },
  );
}

/**
 * `POST /api/v1/zahlungen/guthabenverrechnung` — der Handweg aus `D-28`.
 *
 * **Bucht nicht.** Er legt eine Zuordnung mit `quelle: "guthaben"` an, die
 * auf ihre Freigabe wartet; erst die Freigabe ruft den vorhandenen
 * Einlösungsweg (`1796` an `1400`). Der Kunde kommt vom Beleg und steht
 * nicht im Rumpf.
 *
 * Der Rumpf geht als `unknown` hinein und wird vom Server gegen
 * `guthabenVerrechnungRequestSchema` geprüft — dieselbe Wahl wie bei
 * `legeZahlungZuordnungAn()` darüber.
 */
export async function verrechneGuthabenGegenBeleg(rumpf: unknown): Promise<ZahlungZuordnung> {
  return await request('/zahlungen/guthabenverrechnung', zahlungZuordnungSchema, {
    method: 'POST',
    body: JSON.stringify(rumpf),
  });
}

// ---------------------------------------------------------------------------
// Das Steuerszenario am Beleg — Chip `ST-web`, Plan Task 9
// ---------------------------------------------------------------------------
//
// **Die drei Verträge kommen aus `@fameacc/contracts` und nicht aus
// `api/schemas.ts`** — dieselbe Wahl wie bei der Zahlungszuordnung und den
// E-Mail-Vorlagen oben. Eine zweite Abschrift von
// `steuerszenarioAuskunftSchema` wäre genau die Fehlerklasse, die
// `W-schemadoppel` geschlossen hat: Sie ist eine unterschiedene Vereinigung
// über `abgeleitet`, und eine flach abgeschriebene Fassung wäre der Ort, an
// dem `abgeleitet: false` eines Tages ein `szenario: null` bekäme — und damit
// das `szenario ?? 'domestic'`, das `D-14` verbietet.

/**
 * `GET /api/v1/belege/:id/steuerszenario` — was gespeichert ist und was sich
 * zum Leistungsstichtag ableiten lässt.
 *
 * **Schreibt nichts.** `ableitung: null` heisst „kein Leistungsstichtag
 * ableitbar" und ist der planmässige Zustand eines frischen Entwurfs;
 * `ableitung.abgeleitet === false` heisst „die Lage ist bekannt, und aus ihr
 * folgt kein Szenario".
 */
export async function getSteuerszenario(documentId: string): Promise<SteuerszenarioAuskunft> {
  return await request(
    `/belege/${encodeURIComponent(documentId)}/steuerszenario`,
    steuerszenarioAuskunftSchema,
  );
}

/**
 * `POST /api/v1/belege/:id/steuerszenario/uebernehmen` — die Ableitung wird
 * der Stand des Belegs (`document:manage`, Herkunft `derived`).
 *
 * **Kein Rumpf.** Was übernommen wird, sagt die Ableitung; wer übernimmt,
 * sagt die Sitzung.
 */
export async function uebernimmSteuerszenario(
  documentId: string,
): Promise<SteuerszenarioAuskunft> {
  return await request(
    `/belege/${encodeURIComponent(documentId)}/steuerszenario/uebernehmen`,
    steuerszenarioAuskunftSchema,
    { method: 'POST' },
  );
}

/**
 * `POST /api/v1/belege/:id/steuerszenario/ueberschreiben` — ein Mensch
 * entscheidet anders (`steuer:szenario`, Herkunft `declared`).
 *
 * Der Rumpf geht als `unknown` hinein und wird vom Server gegen
 * `szenarioUeberschreibenRequestSchema` geprüft — dieselbe Wahl wie bei
 * `legeZahlungZuordnungAn()`: Ein zweites `parse()` im Browser prüfte gegen
 * dieselbe Fassung des Vertrags und verdoppelte nur die Fehlermeldungen.
 * Dass die Begründung Pflicht ist, sperrt die Maske trotzdem selbst — ein
 * Knopf, der zuverlässig eine 400 liefert, ist kein Angebot.
 */
export async function ueberschreibeSteuerszenario(
  documentId: string,
  rumpf: unknown,
): Promise<SteuerszenarioAuskunft> {
  return await request(
    `/belege/${encodeURIComponent(documentId)}/steuerszenario/ueberschreiben`,
    steuerszenarioAuskunftSchema,
    { method: 'POST', body: JSON.stringify(rumpf) },
  );
}

// ---------------------------------------------------------------------------
// DATEV-EXPORT (Chip `DX-maske`, Task 13; Spec 2026-09-08-datev-export-design
// Abschnitte 8.1, 8.2 und 7.3)
// ---------------------------------------------------------------------------
//
// **Die Verträge kommen aus `@fameacc/contracts` und nicht aus
// `api/schemas.ts`** — dieselbe Wahl wie bei der Zahlungszuordnung und den
// E-Mail-Vorlagen und aus demselben Grund: Eine zweite Abschrift wäre genau
// die Fehlerklasse, die `W-schemadoppel` geschlossen hat.

/**
 * `GET /api/v1/datev/perioden` — Schritt 1 der Maske.
 *
 * Nur geschlossene Perioden ohne versandten Buchungsstapel. **Jede Zeile ist
 * Arbeit und kein Verstoss**; die Liste wird leer, wenn jeder Monat draussen
 * ist.
 */
export async function listDatevPerioden(): Promise<DatevExportPeriodenListe> {
  return await request('/datev/perioden', datevExportPeriodenListeSchema);
}

/**
 * `GET /api/v1/datev/exporte` — das Register, neueste zuerst, wahlweise auf
 * eine Periode eingegrenzt. Auch die verworfenen Zeilen (`D-23`).
 */
export async function listDatevExporte(periodeId?: string): Promise<DatevExportListe> {
  const suffix = periodeId === undefined ? '' : `?periodeId=${encodeURIComponent(periodeId)}`;
  return await request(`/datev/exporte${suffix}`, datevExportListeSchema);
}

/** `GET /api/v1/datev/exporte/:id` — eine Registerzeile mit ihren Rückmeldungen. */
export async function getDatevExport(id: string): Promise<DatevExportDetail> {
  return await request(`/datev/exporte/${encodeURIComponent(id)}`, datevExportDetailSchema);
}

/**
 * `POST /api/v1/datev/vorschau` — Schritt 2, der Trockenlauf.
 *
 * **Ein Befund ist kein Fehler der Anfrage**: Er kommt mit `200` und
 * `ok: false` heraus, samt der vollständigen Liste aller sechzehn Prüfungen.
 * Die Maske schaltet den Erzeugen-Knopf allein nach diesem Urteil frei
 * (`D-30`).
 */
export async function vorschauDatevExport(
  jahr: number,
  monat: number,
): Promise<DatevStapelAnsicht> {
  return await request('/datev/vorschau', datevStapelAnsichtSchema, {
    method: 'POST',
    body: JSON.stringify({ jahr, monat }),
  });
}

/**
 * `POST /api/v1/datev/exporte` — Schritt 3. Schreibt je Lieferung eine
 * Registerzeile; der Stapel wartet danach auf die zweite Person.
 */
export async function erzeugeDatevExport(
  jahr: number,
  monat: number,
): Promise<DatevStapelAnsicht> {
  return await request('/datev/exporte', datevStapelAnsichtSchema, {
    method: 'POST',
    body: JSON.stringify({ jahr, monat }),
  });
}

/**
 * `POST /api/v1/datev/exporte/:id/freigabe` — Schritt 4, die zweite Person
 * (`D-21`). **Ohne Rumpf**: Welcher Stapel freigegeben wird, sagt die Route,
 * und wer freigibt, die Sitzung.
 */
export async function gebDatevExportFrei(id: string): Promise<DatevExportZeileAnsicht> {
  return await request(
    `/datev/exporte/${encodeURIComponent(id)}/freigabe`,
    datevExportZeileAnsichtSchema,
    { method: 'POST' },
  );
}

/**
 * `POST /api/v1/datev/exporte/:id/verwerfen` — verwerfen statt löschen
 * (`D-23`). Der Grund ist Pflicht.
 */
export async function verwirfDatevExport(
  id: string,
  grund: string,
): Promise<DatevExportZeileAnsicht> {
  return await request(
    `/datev/exporte/${encodeURIComponent(id)}/verwerfen`,
    datevExportZeileAnsichtSchema,
    { method: 'POST', body: JSON.stringify({ grund }) },
  );
}

/**
 * `POST /api/v1/datev/exporte/:id/rueckmeldungen` — der Rückkanal (`D-25`).
 *
 * Der Rumpf geht als `unknown` hinein und wird vom Server gegen
 * `datevExportRueckmeldungEingabeSchema` geprüft — dieselbe Wahl wie bei
 * `ueberschreibeSteuerszenario()`: Ein zweites `parse()` im Browser prüfte
 * gegen dieselbe Fassung des Vertrags und verdoppelte nur die
 * Fehlermeldungen.
 */
export async function erfasseDatevRueckmeldung(
  id: string,
  rumpf: unknown,
): Promise<DatevExportRueckmeldungZeile> {
  return await request(
    `/datev/exporte/${encodeURIComponent(id)}/rueckmeldungen`,
    datevExportRueckmeldungZeileSchema,
    { method: 'POST', body: JSON.stringify(rumpf) },
  );
}

// --- Webhooks — `apps/api/src/webhooks/webhooks.controller.ts` -----------
//
// Aufgabe 4 `WH-oberflaeche`. Alle fuenf Endpunkte verlangen die Faehigkeit
// `webhooks:manage`; eine Lesefaehigkeit daneben gibt es nicht (`D-8`).
//
// DIE VERTRAEGE KOMMEN AUS `@fameacc/contracts` und werden nicht in
// `api/schemas.ts` abgeschrieben -- dieselbe Wahl wie bei der
// DATEV-Exportmaske und der Zahlungszuordnung oben.
//
// DAS SECRET WIRD HIER NICHT ANGEFASST, NICHT GEMERKT UND NICHT
// PROTOKOLLIERT. Zwei Funktionen geben es zurueck -- `legeWebhookEndpunktAn`
// und `rotiereWebhookSecret` --, beide reichen die Antwort UNVERAENDERT
// durch, und keine haelt Zustand. Der Klartext lebt anschliessend
// ausschliesslich in dem einen `useState` der Maske
// (`webhooks/SecretAnzeige.tsx`) und verschwindet mit ihr. Dieselbe Zusage
// wie bei `createApiToken()` und `createUser()` oben.

/** Die Endpunktliste samt ihren Zaehlern — `GET /api/v1/webhooks/endpoints`. */
export async function ladeWebhookEndpunkte(): Promise<WebhookEndpunktListe> {
  return await request('/webhooks/endpoints', webhookEndpunktListeSchema);
}

/**
 * Zählt die erschöpften Zustellungen über alle Webhook-Endpunkte.
 *
 * Verwendet von der Abzeichenzählung im Navigationsmenu.
 */
export async function zaehleWebhookErschoepft(): Promise<number> {
  const liste = await ladeWebhookEndpunkte();
  return liste.endpunkte.reduce((summe, { erschoepft }) => summe + erschoepft, 0);
}

/**
 * Legt einen Endpunkt an — `POST /api/v1/webhooks/endpoints`.
 *
 * Die Antwort traegt das Secret **genau einmal** (`D-6`). Es gibt keinen
 * zweiten Abrufweg, weil der Server keinen anbietet.
 */
export async function legeWebhookEndpunktAn(
  eingabe: WebhookEndpunktAnlage,
): Promise<WebhookEndpunktMitSecret> {
  return await request('/webhooks/endpoints', webhookEndpunktMitSecretSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/**
 * Aendert Name, Adresse und/oder Zustand — `PATCH /api/v1/webhooks/endpoints/:id`.
 *
 * **`{ aktiv: false }` IST das Deaktivieren**; es gibt kein `DELETE` (`D-14`).
 */
export async function aendereWebhookEndpunkt(
  id: string,
  patch: WebhookEndpunktPatch,
): Promise<WebhookEndpunktAntwort> {
  return await request(
    `/webhooks/endpoints/${encodeURIComponent(id)}`,
    webhookEndpunktAntwortSchema,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
}

/**
 * Erneuert das Secret — `POST /api/v1/webhooks/endpoints/:id/secret`.
 *
 * **Ohne Rumpf**: Welcher Endpunkt rotiert wird, sagt die Route. Das alte
 * Secret gilt ab der Antwort nicht mehr — es gibt keine Ueberlappung (`D-6`).
 */
export async function rotiereWebhookSecret(id: string): Promise<WebhookEndpunktMitSecret> {
  return await request(
    `/webhooks/endpoints/${encodeURIComponent(id)}/secret`,
    webhookEndpunktMitSecretSchema,
    { method: 'POST' },
  );
}

/**
 * Die Workflows für die Ereignisauswahl eines neuen Endpunkts — `GET /api/v1/workflows`
 * (Spec Workflows Nachtrag 0.4 Nr. 2): Ein Endpunkt mit Ereignis `workflow.<schluessel>` empfängt,
 * was der Knoten „Webhook senden" dieses Workflows schickt. Verlangt `workflow:read`; wer sie
 * nicht hat, bekommt den Fehler, und der Dialog bietet dann nur das Katalogereignis an.
 */
export async function ladeWorkflowsFuerEndpunkte(): Promise<WorkflowListe> {
  return await request('/workflows', workflowListeSchema);
}

/**
 * Das Zustellungsprotokoll eines Endpunkts —
 * `GET /api/v1/webhooks/endpoints/:id/zustellungen`.
 *
 * `status` weggelassen heisst «alle»: Das ist die Abwesenheit des Filters und
 * kein vierter Statuswert.
 */
export async function ladeWebhookZustellungen(
  id: string,
  filter: { status?: WebhookZustellungStatus; limit?: number; offset?: number } = {},
): Promise<WebhookZustellungenSeite> {
  const parameter = new URLSearchParams();
  if (filter.status !== undefined) parameter.set('status', filter.status);
  if (filter.limit !== undefined) parameter.set('limit', String(filter.limit));
  if (filter.offset !== undefined) parameter.set('offset', String(filter.offset));
  const anhang = parameter.toString();
  return await request(
    `/webhooks/endpoints/${encodeURIComponent(id)}/zustellungen${anhang === '' ? '' : `?${anhang}`}`,
    webhookZustellungenSeiteSchema,
  );
}

/**
 * Sendet eine Zustellung erneut — `POST /api/v1/webhooks/zustellungen/:id/erneut`.
 * Aufgabe 5 `WH-nachsenden`, `D-5`.
 *
 * **Ohne Rumpf.** `status` wird `offen`, `naechster_versuch_am` wird `now()`;
 * `versuch` bleibt unangetastet — er ist die Geschichte der Zeile. Auf einer
 * bereits `zugestellt`en Zeile antwortet der Server mit
 * `WEBHOOK_ZUSTELLUNG_ZUGESTELLT` (`400`): `zugestellt` ist endgültig.
 */
export async function sendeWebhookZustellungErneut(id: string): Promise<WebhookZustellung> {
  return await request(
    `/webhooks/zustellungen/${encodeURIComponent(id)}/erneut`,
    webhookZustellungSchema,
    { method: 'POST' },
  );
}

/**
 * Testet einen Webhook-Endpunkt — `POST /api/v1/webhooks/endpoints/:id/test`.
 * Aufgabe 5 `WH-nachsenden`, `D-15`.
 *
 * **Synchron**: Der Server sendet sofort und einmalig ein erfundenes
 * Ereignis (`"test": true` im Rumpf, `X-Fame-Test: 1` im Kopf) und gibt das
 * Ergebnis in der Antwort zurück — kein zweiter Aufruf, keine Wartezeit.
 * Ohne Rumpf; welcher Endpunkt getestet wird, sagt die Route.
 */
export async function testeWebhookEndpunkt(id: string): Promise<WebhookZustellung> {
  return await request(
    `/webhooks/endpoints/${encodeURIComponent(id)}/test`,
    webhookZustellungSchema,
    { method: 'POST' },
  );
}

// --- E-Mail-Eingang — `apps/api/src/maileingang/maileingang.controller.ts` ---
//
// Spec `2026-11-04-email-eingang-design.md`, Abschnitt 7 und Nachtrag 0.4:
// sechzehn Routen unter `/api/v1/maileingang`. Alle verlangen
// `maileingang:manage`, ausser dem Protokoll (`incoming:read`, `D-14`).
//
// DIE VERTRAEGE KOMMEN AUS `@fameacc/contracts` und werden nicht in
// `api/schemas.ts` abgeschrieben -- dieselbe Wahl wie bei den Webhooks oben;
// keines der Schemas traegt eine `Money`-Marke, ein `ohneMarken()` haette
// nichts zu tun.
//
// DAS PASSWORT GEHT NUR HINAUS, NIE HEREIN. Es steht in drei Anfragen
// (Anlegen, Aendern, Pruefen vor dem Speichern) und in keiner Antwort; diese
// Funktionen reichen es durch und halten keinen Zustand.

/** `GET /api/v1/maileingang/konten` — ohne Passwort, mit `passwortGesetztAm`. */
export async function ladeMailKonten(): Promise<MailKontoListe> {
  return await request('/maileingang/konten', mailKontoListeSchema);
}

/**
 * Zählt die Konten mit einem Fehler — für das Abzeichen der Menüspalte. Über
 * die Liste und nicht über einen eigenen Zählendpunkt, derselbe Weg wie bei
 * `zaehleWebhookErschoepft()`.
 */
export async function zaehleMailKontenMitFehler(): Promise<number> {
  const liste = await ladeMailKonten();
  return liste.konten.filter((konto) => konto.aktiv && konto.letzterFehler !== null).length;
}

/** `GET /api/v1/maileingang/konten/:id`. */
export async function ladeMailKonto(id: string): Promise<MailKonto> {
  return await request(`/maileingang/konten/${encodeURIComponent(id)}`, mailKontoSchema);
}

/** `POST /api/v1/maileingang/konten` — das Passwort ist Pflicht. */
export async function legeMailKontoAn(eingabe: z.input<typeof mailKontoAnlageSchema>): Promise<MailKonto> {
  return await request('/maileingang/konten', mailKontoSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/**
 * `PATCH /api/v1/maileingang/konten/:id` — ein fehlendes `passwort` heisst
 * „unverändert“; ändern sich Server, Port oder Benutzer, ist es Pflicht.
 */
export async function aendereMailKonto(id: string, patch: z.input<typeof mailKontoPatchSchema>): Promise<MailKonto> {
  return await request(`/maileingang/konten/${encodeURIComponent(id)}`, mailKontoSchema, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/** `POST /api/v1/maileingang/konten/:id/deaktivieren` — es gibt kein Löschen. */
export async function deaktiviereMailKonto(id: string): Promise<MailKonto> {
  return await request(
    `/maileingang/konten/${encodeURIComponent(id)}/deaktivieren`,
    mailKontoSchema,
    { method: 'POST' },
  );
}

/** `POST /api/v1/maileingang/konten/:id/pruefen` — mit den gespeicherten Daten (`D-17`). */
export async function pruefeMailKonto(id: string): Promise<MailKontoPruefung> {
  return await request(
    `/maileingang/konten/${encodeURIComponent(id)}/pruefen`,
    mailKontoPruefungSchema,
    { method: 'POST' },
  );
}

/** `POST /api/v1/maileingang/konten/pruefen` — mit den eingegebenen Daten, vor dem Speichern. */
export async function pruefeMailVerbindung(
  eingabe: z.input<typeof mailKontoPruefungRumpfSchema>,
): Promise<MailKontoPruefung> {
  return await request('/maileingang/konten/pruefen', mailKontoPruefungSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/**
 * `POST /api/v1/maileingang/konten/:id/abrufen` — setzt den nächsten Lauf auf
 * jetzt; geholt wird beim nächsten Takt, nicht in dieser Anfrage (`D-9`).
 */
export async function rufeMailKontoAb(id: string): Promise<MailKonto> {
  return await request(`/maileingang/konten/${encodeURIComponent(id)}/abrufen`, mailKontoSchema, {
    method: 'POST',
  });
}

/** `GET /api/v1/maileingang/konten/:id/absender`. */
export async function ladeMailAbsender(kontoId: string): Promise<MailKontoAbsenderListe> {
  return await request(
    `/maileingang/konten/${encodeURIComponent(kontoId)}/absender`,
    mailKontoAbsenderListeSchema,
  );
}

/** `POST /api/v1/maileingang/konten/:id/absender`. */
export async function legeMailAbsenderAn(
  kontoId: string,
  eingabe: z.input<typeof mailKontoAbsenderAnlageSchema>,
): Promise<MailKontoAbsender> {
  return await request(
    `/maileingang/konten/${encodeURIComponent(kontoId)}/absender`,
    mailKontoAbsenderSchema,
    { method: 'POST', body: JSON.stringify(eingabe) },
  );
}

/** `DELETE /api/v1/maileingang/konten/:id/absender/:absenderId` — antwortet mit dem entfernten Eintrag. */
export async function entferneMailAbsender(kontoId: string, absenderId: string): Promise<MailKontoAbsender> {
  return await request(
    `/maileingang/konten/${encodeURIComponent(kontoId)}/absender/${encodeURIComponent(absenderId)}`,
    mailKontoAbsenderSchema,
    { method: 'DELETE' },
  );
}

/** `GET /api/v1/maileingang/regeln` — in ihrer Reihenfolge. */
export async function ladeMailRegeln(): Promise<MailRegelListe> {
  return await request('/maileingang/regeln', mailRegelListeSchema);
}

/** `POST /api/v1/maileingang/regeln`. */
export async function legeMailRegelAn(eingabe: z.input<typeof mailRegelAnlageSchema>): Promise<MailRegel> {
  return await request('/maileingang/regeln', mailRegelSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/** `PATCH /api/v1/maileingang/regeln/:id` — auch Zuordnung und Reihenfolge. */
export async function aendereMailRegel(id: string, patch: z.input<typeof mailRegelPatchSchema>): Promise<MailRegel> {
  return await request(`/maileingang/regeln/${encodeURIComponent(id)}`, mailRegelSchema, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/** `POST /api/v1/maileingang/regeln/:id/deaktivieren`. */
export async function deaktiviereMailRegel(id: string): Promise<MailRegel> {
  return await request(
    `/maileingang/regeln/${encodeURIComponent(id)}/deaktivieren`,
    mailRegelSchema,
    { method: 'POST' },
  );
}

/** Die Filter des Protokolls; ein fehlender Filter heisst „alle“. */
export interface MailProtokollFilter {
  readonly konto?: string | undefined;
  readonly ergebnis?: MailAbrufErgebnis | undefined;
  /** Ein Zeitpunkt in ISO-8601. */
  readonly seit?: string | undefined;
}

/** `GET /api/v1/maileingang/protokoll?konto=&ergebnis=&seit=` (`incoming:read`). */
export async function ladeMailProtokoll(
  filter: MailProtokollFilter = {},
): Promise<MailAbrufProtokoll> {
  return await request(
    `/maileingang/protokoll${query({ konto: filter.konto, ergebnis: filter.ergebnis, seit: filter.seit })}`,
    mailAbrufProtokollSchema,
  );
}

// --- Der Starttag der Buchungsautomatiken --------------------------------
//
// `apps/api/src/system/automatik-start.controller.ts`, Auftrag
// `WH-bankaufladung` (`E-1223`). Zwei Endpunkte mit ZWEI VERSCHIEDENEN
// Faehigkeiten, und das ist Absicht: Gelesen wird unter `system:read` wie
// jede andere Betriebsauskunft, gesetzt unter `webhooks:manage`. Die Maske
// steht trotzdem auf der Webhook-Seite -- es ist der Tag, ab dem ueberhaupt
// eine Nachricht entstehen kann.

/** Die Starttage aller Automatiken des Katalogs — `GET /api/v1/system/automatik-start`. */
export async function ladeAutomatikStart(): Promise<AutomatikStartListe> {
  return await request('/system/automatik-start', automatikStartListeSchema);
}

/**
 * Setzt den Starttag einer Automatik —
 * `PUT /api/v1/system/automatik-start/:schluessel`.
 *
 * **`PUT` und nicht `POST`**: Zweimal derselbe Aufruf hat dieselbe Wirkung wie
 * einmal. Einen Loeschweg gibt es nicht — wer zurueck zur Vorgabe will,
 * **setzt** sie und hinterlaesst dabei eine Protokollzeile.
 */
export async function setzeAutomatikStart(
  schluessel: AutomatikStartSchluessel,
  abDatum: string,
): Promise<AutomatikStart> {
  return await request(
    `/system/automatik-start/${encodeURIComponent(schluessel)}`,
    automatikStartSchema,
    { method: 'PUT', body: JSON.stringify({ abDatum }) },
  );
}

/**
 * `GET /api/v1/auswertungen` — die Bereichsseite der Auswertungen.
 *
 * **Ein Aufruf und nicht sieben.** Die Bereichsseite rechnet keine Kennzahl;
 * sie holt das Verzeichnis der Unterseiten, den aufgelösten Zeitraum und den
 * Stichtag für die Fussleiste. Was eine Unterseite zeigt, holt sie selbst.
 *
 * `zeitraum` geht als Suchparameter mit, sobald einer gewählt ist — ohne
 * Angabe entscheidet der Server (`monat`), und zwar an genau einer Stelle
 * statt an zweien.
 */
export async function getAuswertungenUeberblick(
  zeitraum?: string,
): Promise<AuswertungUeberblickAnsicht> {
  const suffix = zeitraum === undefined ? '' : `?zeitraum=${encodeURIComponent(zeitraum)}`;
  return await request(`/auswertungen${suffix}`, auswertungUeberblickSchema);
}

/**
 * `GET /api/v1/auswertungen/umsatz` — die Umsatzreihe der Belegschicht.
 *
 * **Zwei Schalter und nicht einer.** `zeitraum` bestimmt den Ausschnitt für
 * die Kennzahlen, `reihe` die Länge der Zeitreihe unter dem Chart. Beide
 * gehen nur mit, wenn eine Wahl getroffen wurde — ohne Angabe entscheidet der
 * Server (`monat`, `24`), und zwar an genau einer Stelle statt an zweien.
 *
 * **Das Vertragsschema und nicht das gespiegelte:** Die Antwort trägt
 * Beträge als `Money` und Verhältnisse als `Factor`, und genau so brauchen
 * sie die Chartbausteine und `KpiKarte`. Dieselbe Wahl wie bei
 * `getDashboardKennzahlen()`.
 */
export async function getAuswertungUmsatz(
  zeitraum?: string,
  reihe?: string,
): Promise<AuswertungUmsatz> {
  return await request(
    `/auswertungen/umsatz${query({ zeitraum, reihe })}`,
    auswertungUmsatzSchema,
  );
}

/**
 * `GET /api/v1/auswertungen/prognose` — Umsatzprognose und
 * Liquiditätsvorschau.
 *
 * **Kein Parameter, und das ist eine Aussage über das Verfahren.** Eine
 * Fortschreibung liest ihre Grundlage vollständig; wer sie beschnitte,
 * änderte nicht die Ansicht, sondern das Ergebnis.
 */
export async function getAuswertungPrognose(): Promise<AuswertungPrognose> {
  return await request('/auswertungen/prognose', auswertungPrognoseSchema);
}

/**
 * `GET /api/v1/auswertungen/kunden` — die Kundenauswertung.
 *
 * **Der VERTRAG prüft diese Antwort und nicht das lokal gespiegelte
 * `betragSchema`** — derselbe Weg, den `getDashboardKennzahlen` oben schon
 * geht, und aus demselben Grund: Die Chartbausteine unter
 * `apps/web/src/ui/charts/` nehmen `Money` entgegen (`Reihe.werte`,
 * `Segment.wert`), und der lokale Spiegel liefert unmarkierte Zeichenketten.
 * Ein `as Money` an der Aufrufstelle wäre genau die Zusicherung, die
 * `money/no-float-money` nicht mehr prüfen könnte. Der Anteil der
 * Konzentration kommt aus demselben Grund als `Factor` — er wird
 * ausschliesslich über `format.ts::satz()` angezeigt und nie `Number()`.
 *
 * `zeitraum` und `reihe` gehen als Suchparameter mit, sobald sie gewählt
 * sind; ohne Angabe entscheidet der Server (`monat`, `24`), und zwar an
 * genau einer Stelle statt an zweien.
 */
export async function getAuswertungKunden(
  zeitraum?: AuswertungZeitraumTyp,
  reihe?: AuswertungReihenlaenge,
): Promise<AuswertungKunden> {
  return await request(`/auswertungen/kunden${query({ zeitraum, reihe })}`, auswertungKundenSchema);
}

/**
 * `GET /api/v1/auswertungen/abo` — der wiederkehrende Umsatz.
 *
 * Wie `getAuswertungKunden()` über den VERTRAG geprüft und nicht über den
 * lokalen Spiegel: Die Beträge gehen in einen Chartbaustein, und der nimmt
 * `Money`. Begründung dort.
 */
export async function getAuswertungAbo(zeitraum?: AuswertungZeitraumTyp): Promise<AuswertungAbo> {
  return await request(`/auswertungen/abo${query({ zeitraum })}`, auswertungAboSchema);
}

/**
 * `GET /api/v1/auswertungen/zahlungswege` — Anteil je Zahlungsweg,
 * Guthabenfluss und die ungebuchten Auszahlungen.
 *
 * **Zwei Quellen kommen getrennt an und werden getrennt gezeigt** (`D-20`):
 * `gebucht` ab dem Journalbeginn, `altbestand` für 2022 bis 2025. Der
 * Aufrufer addiert sie nicht — sie zählen Verschiedenes.
 *
 * `reihe` steuert die Länge der Monatsreihe unter dem Chart und ist vom
 * `zeitraum` unabhängig: Der Zeitraum bestimmt, worüber die Anteile
 * gerechnet werden, die Reihenlänge, wie weit die Kurve zurückreicht.
 */
export async function getAuswertungZahlungswege(
  zeitraum?: string,
  reihe?: string,
): Promise<AuswertungZahlungswegeAnsicht> {
  const teile: string[] = [];
  if (zeitraum !== undefined) teile.push(`zeitraum=${encodeURIComponent(zeitraum)}`);
  if (reihe !== undefined) teile.push(`reihe=${encodeURIComponent(reihe)}`);
  const suffix = teile.length === 0 ? '' : `?${teile.join('&')}`;
  return await request(`/auswertungen/zahlungswege${suffix}`, auswertungZahlungswegeSchema);
}

/**
 * `GET /api/v1/auswertungen/forderungen` — Altersstruktur, Laufzeit,
 * Klärstand und der Mahnstand des Altbestands.
 *
 * **Kein `reihe`-Parameter**: Der offene Bestand ist eine Momentaufnahme zum
 * Stichtag und keine Zeitreihe. Der Zeitraum wirkt trotzdem — er bestimmt
 * den Umsatz, gegen den die Forderungslaufzeit gerechnet wird.
 */
export async function getAuswertungForderungen(
  zeitraum?: string,
): Promise<AuswertungForderungenAnsicht> {
  const suffix = zeitraum === undefined ? '' : `?zeitraum=${encodeURIComponent(zeitraum)}`;
  return await request(`/auswertungen/forderungen${suffix}`, auswertungForderungenSchema);
}

/**
 * `GET /api/v1/auswertungen/kosten` — Aufwand je Sachkonto, Kostenquote und
 * Ergebnisvorschau (Plan Task 8 `[AW-kosten]`).
 *
 * **Das Schema kommt aus `@fameacc/contracts`**, nicht aus `./schemas`: Die
 * Beträge dieser Antwort gehen unmittelbar in die Chartbausteine, und die
 * verlangen `Money`. Derselbe Weg, den `getDashboardKennzahlen()` schon geht.
 *
 * `zeitraum` und `reihe` gehen nur mit, wenn sie gewählt sind — ohne Angabe
 * entscheidet der Server (`monat`, `24`), und zwar an einer Stelle statt an
 * zweien.
 */
export async function getAuswertungKosten(
  zeitraum?: string,
  reihe?: string,
): Promise<AuswertungKosten> {
  return await request(`/auswertungen/kosten${query({ zeitraum, reihe })}`, auswertungKostenSchema);
}

/**
 * `GET /api/v1/auswertungen/steuer` — Umsatzsteuer je Monat, Zahllastvorschau
 * und Szenario-Anteile (Plan Task 9 `[AW-steuer]`).
 *
 * Eine Vorschau und ausdrücklich keine Umsatzsteuer-Voranmeldung; die
 * Unvollständigkeiten stehen in `grenzen` der Antwort und nicht in der
 * Oberfläche.
 */
export async function getAuswertungSteuer(
  zeitraum?: string,
  reihe?: string,
): Promise<AuswertungSteuer> {
  return await request(`/auswertungen/steuer${query({ zeitraum, reihe })}`, auswertungSteuerSchema);
}

/**
 * `GET /api/v1/auswertungen/bwa` — die betriebswirtschaftliche Auswertung aus
 * dem eigenen Journal (Plan Task 2/3 `[BWA-api]`/`[BWA-web]`, Spec
 * `docs/superpowers/specs/2026-09-14-bwa-design.md` Abschnitt 5).
 *
 * **Kein `zeitraum` und keine `reihe`.** Die BWA wählt Jahr UND Monat
 * zugleich (`D-14`: „eine BWA ist eine Monatsrechnung") und nicht einen der
 * fünf Zeiträume dieser Ära — dieselbe Begründung, aus der der Vertrag ihre
 * Antwort unter einem eigenen Schlüssel `bwa` nestet statt die Hülle zu
 * spreizen. Ohne Angabe entscheidet der Server (der letzte abgeschlossene
 * Kalendermonat mit Journalzeile), und zwar an genau einer Stelle.
 */
export async function getAuswertungBwa(jahr?: number, monat?: number): Promise<AuswertungBwa> {
  return await request(`/auswertungen/bwa${query({ jahr, monat })}`, auswertungBwaSchema);
}

/**
 * `GET /api/v1/auswertungen/ustva` — das Kennzahlblatt der
 * Umsatzsteuer-Voranmeldung (Plan Task 3 `[UV-web]`, Spec
 * `docs/superpowers/specs/2026-10-09-ustva-kennzahlblatt-design.md`
 * Abschnitt 5.1).
 *
 * **Zeitraum: `jahr` und genau eines von `quartal` und `monat`, oder gar
 * keiner.** Ohne Angabe entscheidet der Server — der jüngste abgeschlossene
 * Zeitraum in der eingestellten Zeitraumart (Spec 5.2), und zwar an genau
 * einer Stelle.
 */
export async function getAuswertungUstva(anfrage: UstvaAnfrage = {}): Promise<UstvaBlatt> {
  return await request(
    `/auswertungen/ustva${query({ jahr: anfrage.jahr, quartal: anfrage.quartal, monat: anfrage.monat })}`,
    ustvaBlattSchema,
  );
}

/**
 * `GET /api/v1/auswertungen/ustva/nachweis` — die Einzelposten einer Kennzahl
 * oder eines Abschnitts ohne Kennzahl, seitenweise (Spec 5.3, `D-29`).
 *
 * Wird erst gerufen, wenn der Anwender den Nachweis aufklappt: Ein Quartal hat
 * Hunderte Tilgungen, und die Seite lädt nur, was sie zeigt.
 */
export async function getAuswertungUstvaNachweis(
  anfrage: UstvaNachweisAnfrage,
): Promise<UstvaNachweis> {
  return await request(
    `/auswertungen/ustva/nachweis${query({
      jahr: anfrage.jahr,
      quartal: anfrage.quartal,
      monat: anfrage.monat,
      kennzahl: anfrage.kennzahl,
      abschnitt: anfrage.abschnitt,
      seite: anfrage.seite,
      groesse: anfrage.groesse,
    })}`,
    ustvaNachweisSchema,
  );
}

// ---------------------------------------------------------------------------
// Lastschrift — Auftrag `LS-einzug` (Task 3), Spec
// `docs/superpowers/specs/2026-09-15-lastschrift-design.md` Abschnitt 6.
// ---------------------------------------------------------------------------

/**
 * `GET /api/v1/lastschrift/faellig` — die Liste des Reiters „Lastschrift",
 * samt `fruehestesEinzugsdatum`.
 *
 * **Liefert seit `LS-faelligkeitnachzug` die ganze Hülle statt nur der
 * Zeilen.** Der Reiter braucht das früheste zulässige Einzugsdatum (`D-8`),
 * um eine überholte Vormerkung zu erkennen und „Neu datieren" mit dem
 * richtigen Vorschlag anzubieten — selbst rechnen kann er es nicht, es hängt
 * am TARGET2-Kalender UND am `HIDMES`-Vorlauf des Instituts. Dieselbe
 * Entscheidung wie bei {@link listLastschriftDateien} und `fints`.
 */
export async function listLastschriftFaellig(): Promise<LastschriftFaelligList> {
  return await request('/lastschrift/faellig', lastschriftFaelligListSchema);
}

/**
 * `POST /api/v1/documents/:id/lastschrift/vormerken` — Betrag und Fälligkeit
 * bestimmt der Server; `faelligkeit` ist eine bewusste Übersteuerung und
 * bleibt deshalb optional. Fehlt sie, setzt der Dienst `document.due_date`,
 * oder — falls das nicht mehr zulässig ist — den frühesten erlaubten Tag
 * (`D-8`).
 *
 * **Eine Funktion für diese Route** (`LS-clientdublette`): Bis dahin gab es
 * hier zwei — `vormerkeLastschrift(documentId, input: LastschriftVormerken)`
 * aus `LS-rechnung`, als Platzhalter gebaut, bevor die Route existierte, und
 * diese aus `LS-einzug`. Genutzt von `DocumentPage.tsx` und
 * `FaelligTabelle.tsx`, beide gegen denselben Vertrag
 * (`lastschriftVormerkenSchema`).
 */
export async function lastschriftVormerken(
  documentId: string,
  faelligkeit?: string,
): Promise<LastschriftEinzug> {
  return await request(
    `/documents/${encodeURIComponent(documentId)}/lastschrift/vormerken`,
    lastschriftEinzugSchema,
    { method: 'POST', body: JSON.stringify(faelligkeit === undefined ? {} : { faelligkeit }) },
  );
}

/** `POST /api/v1/lastschrift/einzuege/:id/stornieren` — nur aus `vorgemerkt`. */
export async function lastschriftStornieren(einzugId: string, grund: string): Promise<LastschriftEinzug> {
  return await request(
    `/lastschrift/einzuege/${encodeURIComponent(einzugId)}/stornieren`,
    lastschriftEinzugSchema,
    { method: 'POST', body: JSON.stringify({ grund }) },
  );
}

/**
 * `POST /api/v1/lastschrift/einzuege/:id/neu-datieren` — nur aus
 * `vorgemerkt` (`D-24`).
 *
 * Ohne `faelligkeit` setzt der Server das früheste zulässige Einzugsdatum.
 * Die Oberfläche schickt es trotzdem mit, weil sie es dem Anwender vorher
 * zeigt und er es ändern darf — ein Feld, das man sieht, soll auch das sein,
 * was ankommt.
 */
export async function lastschriftNeuDatieren(
  einzugId: string,
  faelligkeit?: string,
): Promise<LastschriftEinzug> {
  return await request(
    `/lastschrift/einzuege/${encodeURIComponent(einzugId)}/neu-datieren`,
    lastschriftEinzugSchema,
    { method: 'POST', body: JSON.stringify(faelligkeit === undefined ? {} : { faelligkeit }) },
  );
}

/** `POST /api/v1/lastschrift/einzuege/:id/zurueckgegeben` — nur aus `eingereicht`. */
export async function lastschriftZurueckgegeben(
  einzugId: string,
  grund: string,
): Promise<LastschriftEinzug> {
  return await request(
    `/lastschrift/einzuege/${encodeURIComponent(einzugId)}/zurueckgegeben`,
    lastschriftEinzugSchema,
    { method: 'POST', body: JSON.stringify({ grund }) },
  );
}

/**
 * `GET /api/v1/lastschrift/dateien` — jede Datei mit ihrem
 * Einreichungsprotokoll, plus `fints`: das FinTS-Wissen der Liste, EINMAL je
 * Antwort (Aufgabe `LS-belegknoepfe`). Ersetzt die frühere Rückgabe von nur
 * `dateien` — `LastschriftPage.tsx` liest `fints` seither statt eine eigene
 * Heuristik über `listBankAccounts()` zu bauen.
 */
export async function listLastschriftDateien(): Promise<{
  readonly dateien: readonly LastschriftDateiZeile[];
  readonly fints: LastschriftDateiListFints;
  readonly fruehestesEinzugsdatum: string;
}> {
  return await request('/lastschrift/dateien', lastschriftDateiListSchema);
}

/**
 * „SEPA-Datei erzeugen" — nimmt ALLE vorgemerkten Einzüge, keine Auswahl.
 * Liefert jede neu erzeugte Datei (mehrere, wenn eine Gruppe geteilt wurde).
 */
export async function erzeugeLastschriftDateien(): Promise<readonly LastschriftDatei[]> {
  const antwort = await request(
    '/lastschrift/dateien',
    lastschriftDateiErzeugenAntwortSchema,
    { method: 'POST' },
  );
  return antwort.dateien;
}

/** Der Ersatzweg: „als hochgeladen bestätigen". */
export async function lastschriftDateiHochgeladen(
  dateiId: string,
  grund?: string,
): Promise<LastschriftDatei> {
  return await request(
    `/lastschrift/dateien/${encodeURIComponent(dateiId)}/hochgeladen`,
    lastschriftDateiSchema,
    { method: 'POST', body: JSON.stringify(grund === undefined ? {} : { grund }) },
  );
}

/** Verwirft eine Datei aus `erstellt`, mit Grund. */
export async function lastschriftDateiVerwerfen(dateiId: string, grund: string): Promise<LastschriftDatei> {
  return await request(
    `/lastschrift/dateien/${encodeURIComponent(dateiId)}/verwerfen`,
    lastschriftDateiSchema,
    { method: 'POST', body: JSON.stringify({ grund }) },
  );
}

/** Die URL, unter der `LastschriftPage.tsx` den Download als `<a href>` anbietet. */
export function lastschriftDateiDownloadUrl(dateiId: string): string {
  return `${BASE}/lastschrift/dateien/${encodeURIComponent(dateiId)}/xml`;
}

// --- Kundenportal: die Arbeitsliste der Portal-Vorgänge [KP-oberflaeche] -----
//
// Zwei Routen, zwei Fähigkeiten (Spec Kundenportal Abschnitt 6, Betrieb):
// `document:read` liest die Liste, `document:manage` erledigt eine Zeile. Das
// PORTAL selbst wird von hier aus NIE aufgerufen — seine Routen tragen
// `portal:zugriff`, und keine Rolle trägt diese Fähigkeit (`D-7`): Eine
// Sitzung bekommt dort `403`, auch die des Administrators. Diese zwei Routen
// sind die einzige Berührung der Oberfläche mit dem Kundenportal.

/**
 * Die Arbeitsliste — `GET /api/v1/portal-vorgaenge?status=offen|erledigt`.
 *
 * Der Zustand ist ein Pflichtargument und keine Vorgabe: Eine Liste, die
 * offene und erledigte Vorgänge vermischte, wäre für beide Fragen die falsche
 * Antwort — «was ist zu tun» und «was wurde getan» sind zwei Bildschirme.
 *
 * **Seitenweise seit `KP-mengengrenzen`:** Die Antwort trägt eine Seite
 * (`limit`, `offset`) und `gesamt`. `kundeId` grenzt auf einen Kunden ein —
 * die Kundenakte fragt damit nur nach ihren eigenen Vorgängen, statt die
 * ganze Liste zu laden und selbst zu filtern.
 */
export async function getPortalVorgaenge(
  status: 'offen' | 'erledigt',
  seite: {
    readonly limit: number;
    readonly offset: number;
    readonly kundeId?: string | undefined;
  },
): Promise<PortalVorgangListe> {
  const params = new URLSearchParams({
    status,
    limit: String(seite.limit),
    offset: String(seite.offset),
  });
  if (seite.kundeId !== undefined) params.set('kundeId', seite.kundeId);
  return await request(`/portal-vorgaenge?${params.toString()}`, portalVorgangListeSchema);
}

/**
 * Erledigt einen Vorgang — `POST /api/v1/portal-vorgaenge/:id/erledigen`.
 *
 * Die Notiz ist freiwillig; eine leere Notiz wird **weggelassen** und nicht als
 * Leerzeichenkette gesendet — `erledigt_notiz IS NULL` heisst «ohne Notiz
 * erledigt», eine leere Zeichenkette hiesse «mit einer Notiz erledigt, die
 * nichts sagt».
 *
 * Ein zweites Erledigen antwortet `409 PORTAL_VORGANG_ERLEDIGT` (`E-1395`); die
 * Seite zeigt die Meldung und lädt neu, statt den Fehler zu verschlucken.
 */
export async function erledigePortalVorgang(
  id: string,
  notiz?: string,
): Promise<PortalVorgang> {
  return await request(
    `/portal-vorgaenge/${encodeURIComponent(id)}/erledigen`,
    portalVorgangSchema,
    { method: 'POST', body: JSON.stringify(notiz === undefined ? {} : { notiz }) },
  );
}

/**
 * Zählt die offenen Portal-Vorgänge — für das Abzeichen der Menüspalte.
 *
 * `gesamt` aus einer Seite der Länge eins und kein eigener Zählendpunkt. Bis
 * `KP-mengengrenzen` war es `.length` über die ganze Liste — seit die Liste
 * Seiten hat, stünde dort höchstens die Seitenlänge.
 */
export async function zaehlePortalVorgaengeOffen(): Promise<number> {
  return (await getPortalVorgaenge('offen', { limit: 1, offset: 0 })).gesamt;
}

// ---------------------------------------------------------------------------
// Das Satzangebot — Auftrag `PF-kuszenario` (Spec Systemeinstellungen 3.4,
// `D-35`). Der Vertrag kommt aus `@fameacc/contracts`, dieselbe Wahl wie beim
// Steuerszenario oben.
// ---------------------------------------------------------------------------

/**
 * `GET /api/v1/steuer/satzangebot` — die Steuersätze, die eine Position am
 * Stichtag tragen kann, samt Schlüssel und vorgeschlagenem Erlöskonto. Unter
 * Regelbesteuerung drei Einträge, unter Kleinunternehmer genau einer. Ohne
 * `stichtag` gilt der heutige Tag (`Europe/Berlin`).
 */
export async function getSatzangebot(stichtag?: string): Promise<Satzangebot> {
  const abfrage = stichtag === undefined ? '' : `?stichtag=${encodeURIComponent(stichtag)}`;
  return await request(`/steuer/satzangebot${abfrage}`, satzangebotSchema);
}

// --- Workflows — Spec `docs/superpowers/specs/2026-11-04-workflows-design.md` Abschnitt 7 ---
//
// Achtzehn Routen unter `/api/v1/workflows` plus vier für Geheimnisse (Nachtrag 0.4
// Nr. 5, 18); die öffentliche Eingangsroute (`POST /workflows/eingang/:schluessel`,
// `D-18`) ruft die Oberfläche nie. Lesen unter `workflow:read`, alles Schreibende
// unter `workflow:manage` — darüber entscheidet der Wächter bei jeder Anfrage.
//
// **Die Verträge kommen aus `@fameacc/contracts` und nicht aus `api/schemas.ts`**,
// dieselbe Wahl wie bei den Webhooks: Kein Feld trägt eine `Money`-Marke, also gibt
// es nichts, was `ohneMarken()` abzuziehen hätte, und eine Abschrift wäre die zweite
// Wahrheit, gegen die `pnpm web:schemas` steht.
//
// **Das Secret des Webhook-Eingangs** kommt genau einmal zurück — in der Antwort von
// `rotiereWorkflowWebhookEingang`, unverändert an die Anzeige gereicht
// (`webhooks/SecretAnzeige.tsx`) und mit ihr verschwunden. Ein **Geheimniswert**
// kommt nie zurück: Er wird geschrieben und nie gelesen.

/** Die Liste — `GET /api/v1/workflows`. */
export async function ladeWorkflows(): Promise<WorkflowListe> {
  return await request('/workflows', workflowListeSchema);
}

/** Legt einen Workflow an, wahlweise aus einer Vorlage — `POST /api/v1/workflows`. */
export async function legeWorkflowAn(eingabe: WorkflowAnlage): Promise<WorkflowDetail> {
  return await request('/workflows', workflowDetailSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/** Die Vorlagen (`D-20`) — `GET /api/v1/workflows/katalog`. */
export async function ladeWorkflowKatalog(): Promise<WorkflowKatalog> {
  return await request('/workflows/katalog', workflowKatalogSchema);
}

/** Kopf, scharfe Fassung und Entwurf — `GET /api/v1/workflows/:id`. */
export async function ladeWorkflow(id: string): Promise<WorkflowDetail> {
  return await request(`/workflows/${encodeURIComponent(id)}`, workflowDetailSchema);
}

/** Name, Beschreibung, aktiv — `PATCH /api/v1/workflows/:id`. */
export async function aendereWorkflow(id: string, patch: WorkflowAenderung): Promise<WorkflowDetail> {
  return await request(`/workflows/${encodeURIComponent(id)}`, workflowDetailSchema, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/**
 * Speichert den Entwurf — `PUT /api/v1/workflows/:id/entwurf`.
 *
 * **`erwartetGeaendertAm` ist die optimistische Prüfung** (Nachtrag 0.4 Nr. 13): Hat
 * ein anderes Fenster den Entwurf seither gespeichert, antwortet der Server mit
 * `409 WORKFLOW_ENTWURF_VERALTET`, und der Builder sagt das, statt zu überschreiben.
 */
export async function speichereWorkflowEntwurf(
  id: string,
  eingabe: WorkflowEntwurfRumpf,
): Promise<WorkflowEntwurfAntwort> {
  return await request(`/workflows/${encodeURIComponent(id)}/entwurf`, workflowEntwurfAntwortSchema, {
    method: 'PUT',
    body: JSON.stringify(eingabe),
  });
}

/** Entwurf → scharf; mit Befunden `422` — `POST /api/v1/workflows/:id/scharfschalten`. */
export async function schalteWorkflowScharf(id: string): Promise<WorkflowDetail> {
  return await request(`/workflows/${encodeURIComponent(id)}/scharfschalten`, workflowDetailSchema, {
    method: 'POST',
  });
}

/** Legt die scharfe Fassung still — `POST /api/v1/workflows/:id/stilllegen`. */
export async function legeWorkflowStill(id: string): Promise<WorkflowDetail> {
  return await request(`/workflows/${encodeURIComponent(id)}/stilllegen`, workflowDetailSchema, {
    method: 'POST',
  });
}

/** Handstart oder Testlauf (`D-19`) — `POST /api/v1/workflows/:id/starten`. */
export async function starteWorkflow(id: string, eingabe: WorkflowStartRumpf): Promise<WorkflowLaufDetail> {
  return await request(`/workflows/${encodeURIComponent(id)}/starten`, workflowLaufDetailSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/** Die Läufe eines Workflows — `GET /api/v1/workflows/:id/laeufe?status=&seit=`. */
export async function ladeWorkflowLaeufe(
  id: string,
  filter: { readonly status?: WorkflowLaufStatus; readonly seit?: string } = {},
): Promise<WorkflowLaufListe> {
  return await request(
    `/workflows/${encodeURIComponent(id)}/laeufe${query({ status: filter.status, seit: filter.seit })}`,
    workflowLaufListeSchema,
  );
}

/** Ein Lauf mit seinen Schritten — `GET /api/v1/workflows/laeufe/:laufId`. */
export async function ladeWorkflowLauf(laufId: string): Promise<WorkflowLaufDetail> {
  return await request(`/workflows/laeufe/${encodeURIComponent(laufId)}`, workflowLaufDetailSchema);
}

/** Bricht einen Lauf ab (nur `laeuft`/`wartet`) — `POST /api/v1/workflows/laeufe/:laufId/abbrechen`. */
export async function brecheWorkflowLaufAb(laufId: string): Promise<WorkflowLaufDetail> {
  return await request(`/workflows/laeufe/${encodeURIComponent(laufId)}/abbrechen`, workflowLaufDetailSchema, {
    method: 'POST',
  });
}

/**
 * Die Adresse des Webhook-Eingangs, ohne Secret — `GET /api/v1/workflows/:id/webhook-eingang`;
 * `eingang` ist `null`, solange keiner eingerichtet ist.
 */
export async function ladeWorkflowWebhookEingang(id: string): Promise<WorkflowWebhookEingangAntwort> {
  return await request(
    `/workflows/${encodeURIComponent(id)}/webhook-eingang`,
    workflowWebhookEingangAntwortSchema,
  );
}

/**
 * Richtet den Eingang ein oder rotiert sein Secret (der Schlüssel bleibt); das Secret steht
 * genau einmal in der Antwort — `POST /api/v1/workflows/:id/webhook-eingang/rotieren`.
 */
export async function rotiereWorkflowWebhookEingang(id: string): Promise<WorkflowWebhookEingangMitSecret> {
  return await request(
    `/workflows/${encodeURIComponent(id)}/webhook-eingang/rotieren`,
    workflowWebhookEingangMitSecretSchema,
    { method: 'POST' },
  );
}

/** Die letzten Ereignisse, zur Fehlersuche — `GET /api/v1/workflows/ereignisse?art=&seit=`. */
export async function ladeWorkflowEreignisse(
  filter: { readonly art?: string; readonly seit?: string } = {},
): Promise<WorkflowEreignisListe> {
  return await request(
    `/workflows/ereignisse${query({ art: filter.art, seit: filter.seit })}`,
    workflowEreignisListeSchema,
  );
}

/** Alle Fassungen — `GET /api/v1/workflows/:id/fassungen`. */
export async function ladeWorkflowFassungen(id: string): Promise<WorkflowFassungListe> {
  return await request(`/workflows/${encodeURIComponent(id)}/fassungen`, workflowFassungListeSchema);
}

/** Eine Fassung — `GET /api/v1/workflows/:id/fassungen/:nummer`. */
export async function ladeWorkflowFassung(id: string, nummer: number): Promise<WorkflowFassung> {
  return await request(
    `/workflows/${encodeURIComponent(id)}/fassungen/${String(nummer)}`,
    workflowFassungSchema,
  );
}

/** Die Geheimnisse, ohne Wert — `GET /api/v1/workflows/geheimnisse`. */
export async function ladeWorkflowGeheimnisse(): Promise<WorkflowGeheimnisListe> {
  return await request('/workflows/geheimnisse', workflowGeheimnisListeSchema);
}

/** Legt ein Geheimnis an — `POST /api/v1/workflows/geheimnisse`. Die Antwort trägt den Wert nicht. */
export async function legeWorkflowGeheimnisAn(eingabe: WorkflowGeheimnisAnlage): Promise<WorkflowGeheimnis> {
  return await request('/workflows/geheimnisse', workflowGeheimnisSchema, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  });
}

/** Neuer Wert oder neue Beschreibung — `PATCH /api/v1/workflows/geheimnisse/:id`. */
export async function aendereWorkflowGeheimnis(
  id: string,
  patch: WorkflowGeheimnisAenderung,
): Promise<WorkflowGeheimnis> {
  return await request(`/workflows/geheimnisse/${encodeURIComponent(id)}`, workflowGeheimnisSchema, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/** Deaktiviert ein Geheimnis — `POST /api/v1/workflows/geheimnisse/:id/deaktivieren`. */
export async function deaktiviereWorkflowGeheimnis(id: string): Promise<WorkflowGeheimnis> {
  return await request(
    `/workflows/geheimnisse/${encodeURIComponent(id)}/deaktivieren`,
    workflowGeheimnisSchema,
    { method: 'POST' },
  );
}
